import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import LocalShippingRoundedIcon from '@mui/icons-material/LocalShippingRounded';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import MarkEmailReadRoundedIcon from '@mui/icons-material/MarkEmailReadRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import RouteRoundedIcon from '@mui/icons-material/RouteRounded';
import SelectAllRoundedIcon from '@mui/icons-material/SelectAllRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import FilterAltOffRoundedIcon from '@mui/icons-material/FilterAltOffRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import { NavLink } from 'react-router-dom';
import {
  AnimatedActionBar,
  AnimatedMetricValue,
  AnimatedPage,
  AnimatedSection,
  AnimatedTableRow,
  metricMotion,
} from '../components/animations/MotionContainers';
import {
  auditLogisticsExport,
  exportLogisticsExcel,
  cancelLogisticsLoadOrder,
  createLogisticsCliente,
  createLogisticsCmrProfile,
  createLogisticsLogisticCenter,
  createLogisticsLoadOrder,
  createLogisticsLoadOrderAttachment,
  createLogisticsLoadOrderStatus,
  createLogisticsLoadOrderEmailSetting,
  createLogisticsProduct,
  createLogisticsProveedor,
  createLogisticsTransportista,
  createNotificationTemplate,
  createLogisticsViewProfile,
  deleteLogisticsDocumentLine,
  deleteLogisticsDocumentLines,
  downloadFileBlob,
  getLogisticsClientes,
  getLogisticsCmrFields,
  getLogisticsCmrProfiles,
  getLogisticsLogisticCenters,
  getLogisticsLoadOrders,
  getLogisticsLoadOrderById,
  getLogisticsLoadOrderAttachments,
  getLogisticsLoadOrderEmailDeliveries,
  getLogisticsEmailDeliveries,
  getLogisticsLoadOrderEmailSettings,
  getLogisticsLoadOrderStatuses,
  getLogisticsPendingImports,
  getLogisticsProducts,
  getLogisticsProveedores,
  getLogisticsTransportTariffs,
  getLogisticsTransportistas,
  getLogisticsTraceability,
  getLogisticsViewCustomers,
  getLogisticsDocumentLine,
  getLogisticsViewDocuments,
  getLogisticsViewFields,
  getLogisticsViewProfiles,
  getNotificationEmailAccounts,
  getNotificationTemplates,
  generateLogisticsLoadOrderCmrPdf,
  importLogisticsDocuments,
  previewLogisticsLoadOrderEmails,
  retryLogisticsEmailDelivery,
  sendLogisticsLoadOrderEmails,
  sendLogisticsLoadOrderEmailsByScope,
  setLogisticsViewProfileCustomers,
  syncLogisticsBusinessCentralMasterData,
  updateLogisticsCliente,
  updateLogisticsCmrProfile,
  updateLogisticsDocumentLine,
  updateLogisticsLogisticCenter,
  updateLogisticsLoadOrder,
  updateLogisticsLoadOrderStatus,
  updateLogisticsLoadOrderEmailSetting,
  updateLogisticsProduct,
  updateLogisticsProveedor,
  updateLogisticsTransportista,
  updateLogisticsViewProfile,
} from '../services';
import {
  DEFAULT_LOAD_ORDER_STATUSES,
  loadOrderStatusLabel,
} from '../config/loadOrderStatuses';

const PAGE_SIZE = 500;
const DEFAULT_TABLE_PAGE_SIZE = 500;
const TABLE_ROWS_PER_PAGE_OPTIONS = [25, 50, 100, 500];
const GENERAL_VIEW_CUSTOMER_CODE = 'GENERAL';
const DATE_FILTER_DEBOUNCE_MS = 1200;
const PENDING_IMPORT_COLUMNS_STORAGE_KEY = 'clasol.logistics.pendingImportColumns.v1';
const LOAD_ORDER_ASSIGNED_LINE_FIELDS_STORAGE_KEY =
  'clasol.logistics.loadOrderAssignedLineFields.v1';
const MASTER_TABLE_COLUMNS_STORAGE_KEY = 'clasol.logistics.masterTableColumns.v1';
const LOAD_ORDER_ASSIGNED_LINE_FIELD_OPTIONS = [
  { field: 'document', label: 'Documento' },
  { field: 'pedido', label: 'Pedido' },
  { field: 'line', label: 'Linea' },
  { field: 'product', label: 'Producto' },
  { field: 'variety', label: 'Variedad' },
  { field: 'matricula', label: 'Matricula' },
  { field: 'load', label: 'Carga' },
  { field: 'unload', label: 'Descarga' },
  { field: 'pallets', label: 'Palets' },
];
const DEFAULT_LOAD_ORDER_ASSIGNED_LINE_FIELDS = [
  'document',
  'line',
  'variety',
  'matricula',
  'pallets',
];
const CANCELLED_LOAD_ORDER_STATUS_CODES = ['CANCELADA', 'CANCELADO', 'CANCELLED', 'ANULADA', 'ANULADO'];
const LOAD_ORDER_HEADER_FIELD_OPTIONS = [
  { field: 'header.number', label: 'Numero orden', section: 'Cabecera orden' },
  { field: 'header.loadDate', label: 'Fecha carga', section: 'Cabecera orden' },
  { field: 'header.status', label: 'Estado', section: 'Cabecera orden' },
  { field: 'header.transportistaId', label: 'Transportista', section: 'Cabecera orden' },
  { field: 'header.matricula', label: 'Matricula orden', section: 'Cabecera orden' },
  { field: 'header.driverCount', label: 'Choferes', section: 'Cabecera orden' },
  { field: 'header.manualPrice', label: 'Precio manual', section: 'Cabecera orden' },
  { field: 'header.notes', label: 'Notas orden', section: 'Cabecera orden' },
];
const DEFAULT_PENDING_IMPORT_COLUMNS = [
  { field: 'document_type', label: 'Tipo' },
  { field: 'source_document_no', label: 'Documento' },
  { field: 'source_line_no', label: 'Linea' },
  { field: 'des_cliente', label: 'Cliente' },
  { field: 'des_dir_envio', label: 'Destino' },
  { field: 'fecha_carga', label: 'Fecha carga' },
  { field: 'des_variedad', label: 'Variedad' },
  { field: 'cantidad_pales', label: 'Palets' },
];
const UNLOAD_TRIP_COLUMNS = [
  { field: 'source_line_origin', label: 'Origen de la linea' },
  { field: 'source_position_label', label: 'Origen disponible' },
  { field: 'origin_location', label: 'Ubicacion actual' },
  { field: 'matricula', label: 'Matricula actual' },
  { field: 'source_document_no', label: 'Pedido' },
  { field: 'source_line_no', label: 'Linea' },
  { field: 'des_cliente', label: 'Cliente' },
  { field: 'des_dir_envio', label: 'Destino' },
  { field: 'des_variedad', label: 'Variedad' },
  { field: 'cantidad_pales_cargados', label: 'Palets cargados' },
  { field: 'cantidad_pales_otros_movimientos', label: 'Otros movimientos' },
  { field: 'cantidad_pales_descarga_asignados', label: 'Asignados descarga' },
  { field: 'cantidad_pales_descargados', label: 'Descargados' },
  { field: 'cantidad_pales_descarga_pendientes', label: 'Pendientes descarga' },
];
const INTERMEDIATE_STAGE_OPTIONS = [
  { value: 'TRANSIT', label: 'En transito', operationType: 'TRANSIT_STAGE' },
  { value: 'CUSTOMS', label: 'Aduanas', operationType: 'CUSTOMS_STAGE' },
  { value: 'LOGISTICS_CENTER', label: 'Centro logistico', operationType: 'LOGISTICS_CENTER_STAGE' },
  { value: 'VEHICLE_CHANGE', label: 'Cambio de matricula', operationType: 'VEHICLE_CHANGE_STAGE', tripType: 'TRANSIT' },
];
const INTERMEDIATE_TRIP_TYPES = ['TRANSIT', 'CUSTOMS', 'LOGISTICS_CENTER'];
const MASTER_TABLE_ENTITIES = [
  { entity: 'transportistas', title: 'Transportistas' },
  { entity: 'tarifas', title: 'Tarifas' },
  { entity: 'clientes', title: 'Clientes y destinos' },
  { entity: 'proveedores', title: 'Proveedores y almacenes' },
  { entity: 'centros-logisticos', title: 'Centros logisticos' },
  { entity: 'productos', title: 'Productos logisticos' },
];

const platformPages = [
  {
    label: 'Integraciones',
    path: '/admin/integraciones',
    eyebrow: 'Plataforma',
    description: 'Sincronizacion con Business Central y servicios de soporte.',
  },
  {
    label: 'Auditoria',
    path: '/admin/auditoria',
    eyebrow: 'Plataforma',
    description: 'Consulta de actividad registrada por la API.',
  },
];

export function TransportDashboardPage() {
  const documentsTable = useRemoteTableState('', 'asc');
  const documentsApiParams = documentsTable.apiParams;
  const setDocumentsMeta = documentsTable.setMeta;
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [viewDocuments, setViewDocuments] = useState(emptyViewDocuments());
  const [loadOrders, setLoadOrders] = useState([]);
  const [selectedLineIds, setSelectedLineIds] = useState([]);
  const [viewDateRangeInput, setViewDateRangeInput] = useState(defaultDateRange());
  const viewDateRange = useDebouncedValue(viewDateRangeInput, DATE_FILTER_DEBOUNCE_MS);
  const [masters, setMasters] = useState(emptyMasters());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLoadOrder, setEditingLoadOrder] = useState(null);
  const [editingPendingLine, setEditingPendingLine] = useState(null);
  const [loading, setLoading] = useState({
    customers: true,
    documents: false,
    orders: true,
    masters: false,
  });
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const loadCustomers = useCallback(async () => {
    setLoading((current) => ({ ...current, customers: true }));
    try {
      const response = await getLogisticsViewCustomers({
        page: 1,
        limit: PAGE_SIZE,
        sortDirection: 'asc',
      });
      const data = responseList(response);
      setCustomers(data);
      setSelectedCustomer((current) => current || data[0]?.cliente?.cod_cliente || '');
    } catch (error) {
      setMessage(apiMessage(error, 'No se han podido cargar los clientes.'));
    } finally {
      setLoading((current) => ({ ...current, customers: false }));
    }
  }, []);

  const loadDocuments = useCallback(async (codCliente) => {
    if (!codCliente) {
      setViewDocuments(emptyViewDocuments());
      setSelectedLineIds([]);
      return;
    }

    setLoading((current) => ({ ...current, documents: true }));
    try {
      const response = await getLogisticsViewDocuments({
        codCliente,
        operationType: 'INTERNAL_LOAD_ORDER',
        fechaDesde: viewDateRange.fechaDesde,
        fechaHasta: viewDateRange.fechaHasta,
        ...documentsApiParams,
      });
      setDocumentsMeta(response?.meta);
      setViewDocuments(normalizeViewDocuments(response, codCliente));
      setSelectedLineIds([]);
    } catch (error) {
      setViewDocuments(emptyViewDocuments());
      setSelectedLineIds([]);
      setMessage(apiMessage(error, 'No se han podido cargar las lineas.'));
    } finally {
      setLoading((current) => ({ ...current, documents: false }));
    }
  }, [documentsApiParams, setDocumentsMeta, viewDateRange.fechaDesde, viewDateRange.fechaHasta]);

  const loadLoadOrders = useCallback(async () => {
    setLoading((current) => ({ ...current, orders: true }));
    try {
      const response = await getLogisticsLoadOrders({
        page: 1,
        limit: PAGE_SIZE,
        sortBy: 'load_date',
        sortDirection: 'desc',
        tripType: 'LOAD',
        operationType: 'INTERNAL_LOAD_ORDER',
        loadDateFrom: viewDateRange.fechaDesde,
        loadDateTo: viewDateRange.fechaHasta,
      });
      setLoadOrders(responseList(response));
    } catch (error) {
      setLoadOrders([]);
      setMessage(apiMessage(error, 'No se han podido cargar ordenes de carga.'));
    } finally {
      setLoading((current) => ({ ...current, orders: false }));
    }
  }, [viewDateRange.fechaDesde, viewDateRange.fechaHasta]);

  const loadMasters = useCallback(async () => {
    setLoading((current) => ({ ...current, masters: true }));
    try {
      const options = await loadMasterOptions();
      setMasters(options);
      return options;
    } catch (error) {
      setMessage(apiMessage(error, 'No se han podido cargar maestros.'));
      return emptyMasters();
    } finally {
      setLoading((current) => ({ ...current, masters: false }));
    }
  }, []);

  useEffect(() => {
    loadCustomers();
    loadLoadOrders();
  }, [loadCustomers, loadLoadOrders]);

  useEffect(() => {
    loadDocuments(selectedCustomer);
  }, [loadDocuments, selectedCustomer]);

  const selectedCustomerInfo = useMemo(
    () =>
      customers.find((item) => item?.cliente?.cod_cliente === selectedCustomer) ||
      null,
    [customers, selectedCustomer]
  );

  const selectedLineKeySet = useMemo(
    () => new Set(selectedLineIds),
    [selectedLineIds]
  );

  const customerLoadOrders = useMemo(
    () =>
      selectedCustomer === GENERAL_VIEW_CUSTOMER_CODE
        ? loadOrders
        : loadOrders.filter((order) =>
            loadOrderMatchesCustomer(order, selectedCustomer, viewDocuments.rows)
          ),
    [loadOrders, selectedCustomer, viewDocuments.rows]
  );

  const pendingDocumentRows = useMemo(
    () => viewDocuments.rows,
    [viewDocuments.rows]
  );

  const selectedRows = useMemo(
    () =>
      pendingDocumentRows.filter((row) =>
        selectedLineKeySet.has(getLineKey(row))
      ),
    [pendingDocumentRows, selectedLineKeySet]
  );

  const handleToggleLine = (row) => {
    const key = getLineKey(row);
    setSelectedLineIds((current) =>
      current.includes(key)
        ? current.filter((value) => value !== key)
        : [...current, key]
    );
  };

  const handleOpenLoadOrderDialog = async () => {
    setEditingLoadOrder(null);
    await loadMasters();
    setDialogOpen(true);
  };

  const handleSaveLoadOrder = async (payload) => {
    setActionLoading(true);
    setMessage(null);
    try {
      if (editingLoadOrder?.id) {
        await updateLogisticsLoadOrder(editingLoadOrder.id, payload);
        setMessage({ severity: 'success', text: 'Orden de carga actualizada.' });
      } else {
        await createLogisticsLoadOrder(payload);
        setMessage({ severity: 'success', text: 'Orden de carga creada.' });
      }
      setDialogOpen(false);
      setEditingLoadOrder(null);
      setSelectedLineIds([]);
      await Promise.all([
        loadLoadOrders(),
        selectedCustomer ? loadDocuments(selectedCustomer) : Promise.resolve(),
      ]);
    } catch (error) {
      setMessage(apiMessage(error, 'No se ha podido guardar la orden de carga.'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditPendingLine = async (row) => {
    const lineId = getLineId(row);
    if (!lineId) {
      setMessage({ severity: 'error', text: 'La linea no tiene identificador para editar.' });
      return;
    }
    setActionLoading(true);
    setMessage(null);
    try {
      const line = await getLogisticsDocumentLine(lineId);
      setEditingPendingLine(line);
    } catch (error) {
      setMessage(apiMessage(error, 'No se ha podido cargar el detalle de la linea.'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleSavePendingLine = async (payload) => {
    const lineId = getLineId(editingPendingLine);
    if (!lineId) {
      setMessage({ severity: 'error', text: 'La linea no tiene identificador para editar.' });
      return;
    }
    setActionLoading(true);
    setMessage(null);
    try {
      await updateLogisticsDocumentLine(lineId, payload);
      setEditingPendingLine(null);
      setMessage({ severity: 'success', text: 'Linea actualizada.' });
      await loadDocuments(selectedCustomer);
    } catch (error) {
      setMessage(apiMessage(error, 'No se ha podido actualizar la linea.'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeletePendingLines = async (rows) => {
    if (!rows.length || actionLoading) return;
    const ids = rows.map(getLineId);
    if (ids.some((id) => !id)) {
      setMessage({ severity: 'error', text: 'Alguna linea no tiene identificador para eliminar.' });
      return;
    }
    const confirmed = window.confirm(
      `Eliminar ${rows.length === 1 ? `la linea ${lineLabel(rows[0])}` : `las ${rows.length} lineas seleccionadas`} solo de Suite Transporte? Esta accion no modifica Business Central. Si alguna tiene referencias de trabajo, no se eliminara ninguna.`
    );
    if (!confirmed) {
      return;
    }
    setActionLoading(true);
    setMessage(null);
    try {
      if (ids.length === 1) await deleteLogisticsDocumentLine(ids[0]);
      else await deleteLogisticsDocumentLines(ids);
      setSelectedLineIds([]);
      setMessage({ severity: 'success', text: ids.length === 1 ? 'Linea eliminada de Suite Transporte.' : `${ids.length} lineas eliminadas de Suite Transporte.` });
      await loadDocuments(selectedCustomer);
    } catch (error) {
      setMessage(apiMessage(error, 'No se han podido eliminar las lineas.'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeletePendingLine = (row) => handleDeletePendingLines([row]);

  const documentRows = pendingDocumentRows;
  const columns = viewDocuments.columns;
  const loadingAny = Object.values(loading).some(Boolean);

  return (
    <AnimatedPage>
      <Box className="suite-page transport-page">
        <PageHeader
          eyebrow="Gestion de cargas"
          title="Cargas"
          description="Lineas pendientes por cliente y ordenes de transporte ya planificadas."
        />

        {message ? (
          <Alert severity={message.severity} onClose={() => setMessage(null)}>
            {message.text}
          </Alert>
        ) : null}

        {loadingAny || actionLoading ? <LinearProgress /> : null}

        <Paper className="dashboard-section-card transport-date-filter-card" elevation={0}>
          <Box className="transport-filter-bar">
            <Button
              size="small"
              variant={isFechaDateRangePreset(viewDateRangeInput, 'today') ? 'contained' : 'outlined'}
              onClick={() => setViewDateRangeInput((current) => applyFechaDateRangePreset(current, 'today'))}
            >
              Hoy
            </Button>
            <Button
              size="small"
              variant={isFechaDateRangePreset(viewDateRangeInput, 'week') ? 'contained' : 'outlined'}
              onClick={() => setViewDateRangeInput((current) => applyFechaDateRangePreset(current, 'week'))}
            >
              Semana
            </Button>
            <TextField
              label="Fecha carga desde"
              size="small"
              type="date"
              value={viewDateRangeInput.fechaDesde}
              onChange={(event) =>
                setViewDateRangeInput((current) => ({
                  ...current,
                  fechaDesde: event.target.value,
                }))
              }
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              label="Fecha carga hasta"
              size="small"
              type="date"
              value={viewDateRangeInput.fechaHasta}
              onChange={(event) =>
                setViewDateRangeInput((current) => ({
                  ...current,
                  fechaHasta: event.target.value,
                }))
              }
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Box>
        </Paper>

        <AnimatedSection className="transport-view-section">
          <Paper className="dashboard-section-card suite-view-card" elevation={0}>
            <Box className="transport-section-header transport-section-header-spread">
              <Box>
                <Typography className="section-title">Vista por cliente</Typography>
                <Typography className="transport-section-subtitle">
                  {selectedCustomerInfo?.cliente?.des_cliente ||
                    'Selecciona un cliente con perfil activo.'}
                </Typography>
              </Box>
              <Stack direction="row" gap={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <Chip className="suite-status-chip" label={`${selectedRows.length} seleccionadas`} />
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  startIcon={<DeleteOutlineRoundedIcon />}
                  disabled={!selectedRows.length || actionLoading}
                  onClick={() => handleDeletePendingLines(selectedRows)}
                >
                  Eliminar seleccionadas
                </Button>
              </Stack>
            </Box>

            <CustomerTabs
              customers={customers}
              value={selectedCustomer}
              onChange={setSelectedCustomer}
            />

            <CustomerLoadOrdersPreview
              orders={customerLoadOrders}
              onEdit={async (order) => {
                setEditingLoadOrder(order);
                await loadMasters();
                setDialogOpen(true);
              }}
            />

            <Box className="transport-section-header transport-section-header-spread transport-pending-lines-header">
              <Box>
                <Typography className="section-title">Lineas pendientes de asignar</Typography>
                <Typography className="transport-section-subtitle">
                  Selecciona las lineas que quieres incluir en una nueva orden.
                </Typography>
              </Box>
              <Chip
                className="transport-count-chip transport-count-chip-warning"
                label={`${documentRows.length} lineas`}
              />
            </Box>

            <DynamicRowsTable
              columns={columns}
              rows={documentRows}
              meta={viewDocuments.meta}
              table={documentsTable}
              emptyText="No hay lineas para este cliente o no existe perfil activo."
              selectable
              selectedKeys={selectedLineKeySet}
              onToggleRow={handleToggleLine}
              onEditRow={handleEditPendingLine}
              onDeleteRow={handleDeletePendingLine}
            />
          </Paper>
        </AnimatedSection>

        <LoadOrderDialog
          open={dialogOpen}
          loadOrder={editingLoadOrder}
          onClose={() => {
            setDialogOpen(false);
            setEditingLoadOrder(null);
          }}
          onSubmit={handleSaveLoadOrder}
          selectedRows={selectedRows}
          masterOptions={masters}
          statuses={masters.loadOrderStatuses}
          submitting={actionLoading}
        />
        <PendingLineEditDialog
          open={Boolean(editingPendingLine)}
          line={editingPendingLine}
          loading={actionLoading}
          onClose={() => setEditingPendingLine(null)}
          onSubmit={handleSavePendingLine}
        />

        {selectedRows.length > 0 ? (
          <Fab
            variant="extended"
            className="transport-floating-action"
            disabled={actionLoading}
            onClick={handleOpenLoadOrderDialog}
          >
            <AddRoundedIcon sx={{ mr: 1 }} />
            Nueva orden ({selectedRows.length})
          </Fab>
        ) : null}
      </Box>
    </AnimatedPage>
  );
}

export function ApplicationRoutePage({ page }) {
  if (page.path === '/cargas-gestionadas') {
    return <LoadOrdersPage page={page} />;
  }

  if (page.path === '/viaje-carga') {
    return <LoadTripPage page={page} />;
  }

  if (page.path === '/viaje-descarga') {
    return <UnloadTripPage page={page} />;
  }

  if (page.path === '/trazabilidad') {
    return <TraceabilityPage page={page} />;
  }

  if (page.path === '/estados-ordenes-carga') {
    return <LoadOrderStatusesPage page={page} />;
  }

  if (page.path === '/camiones') {
    return <ImportPage page={page} />;
  }

  if (page.path === '/transportes') {
    return <TransportResourcesPage page={page} />;
  }

  if (page.path === '/clientes') {
    return <MasterDataPage page={page} entity="clientes" />;
  }

  if (page.path === '/proveedores') {
    return <MasterDataPage page={page} entity="proveedores" />;
  }

  if (page.path === '/centros-logisticos') {
    return <MasterDataPage page={page} entity="centros-logisticos" />;
  }

  if (page.path === '/productos') {
    return <MasterDataPage page={page} entity="productos" />;
  }

  if (page.path === '/informes') {
    return <ReportsPage page={page} />;
  }

  if (page.path === '/correos-carga') {
    return <LoadOrderEmailSettingsPage page={page} />;
  }

  if (page.path === '/personalizacion-lineas-asignadas') {
    return <AssignedLinesCustomizationPage page={page} />;
  }

  if (page.path === '/personalizacion-maestros') {
    return <MasterTablesCustomizationPage page={page} />;
  }

  if (page.path === '/buzon-envios') {
    return <EmailDeliveriesPage page={page} />;
  }

  if (page.path === '/configuracion') {
    return <LogisticsConfigurationPage page={page} />;
  }

  return <OperationalPage page={page} />;
}

function EmailDeliveriesPage({ page }) {
  const table = useRemoteTableState('created_at', 'desc');
  const setTableMeta = table.setMeta;
  const [filters, setFilters] = useState(defaultEmailDeliveryFilters());
  const [filterDraft, setFilterDraft] = useState(defaultEmailDeliveryFilters());
  const tableApiParams = useMemo(
    () => ({
      ...table.apiParams,
      ...cleanPayload(filters),
    }),
    [filters, table.apiParams]
  );
  const [deliveries, setDeliveries] = useState([]);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [meta, setMeta] = useState(defaultMeta());
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  const loadDeliveries = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    try {
      const response = await getLogisticsEmailDeliveries(tableApiParams);
      setDeliveries(responseList(response));
      const nextMeta = response.meta || defaultMeta();
      setMeta(nextMeta);
      setTableMeta(nextMeta);
    } catch (error) {
      setMessage(apiMessage(error, 'No se ha podido cargar el buzon de envios.'));
    } finally {
      setLoading(false);
    }
  }, [setTableMeta, tableApiParams]);

  useEffect(() => {
    loadDeliveries();
  }, [loadDeliveries]);

  const handleFilterChange = (field, value) => {
    setFilterDraft((current) => ({ ...current, [field]: value }));
  };

  const handleApplyFilters = () => {
    table.handlePageChange(null, 0);
    setFilters(filterDraft);
  };

  const handleClearFilters = () => {
    const emptyFilters = defaultEmailDeliveryFilters();
    table.handlePageChange(null, 0);
    setFilterDraft(emptyFilters);
    setFilters(emptyFilters);
  };

  return (
    <AnimatedPage>
      <PageHeader page={page} />

      {message ? (
        <Alert
          severity={message.severity}
          onClose={() => setMessage(null)}
          className="transport-inline-alert"
        >
          {message.text}
        </Alert>
      ) : null}

      <AnimatedSection>
        <Paper className="dashboard-section-card transport-managed-card" elevation={0}>
          <Box className="transport-section-header transport-section-header-spread">
            <Box>
              <Typography className="section-title">Correos enviados</Typography>
              <Typography className="section-subtitle">
                Historico de envios creados desde las ordenes de carga.
              </Typography>
            </Box>
            <Stack direction="row" gap={1} sx={{ flexWrap: 'wrap' }}>
              <Chip
                className="transport-count-chip"
                label={`${meta.total ?? deliveries.length} envios`}
              />
              <IconButton size="small" title="Actualizar" onClick={loadDeliveries}>
                <RefreshRoundedIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Box>
          <Box className="transport-filter-bar">
            <TextField
              label="Buscar"
              value={filterDraft.search}
              onChange={(event) => handleFilterChange('search', event.target.value)}
              placeholder="Asunto, destinatario, orden o error"
            />
            <TextField
              label="Orden"
              value={filterDraft.orderId}
              onChange={(event) => handleFilterChange('orderId', event.target.value)}
            />
            <FormControl>
              <InputLabel>Estado</InputLabel>
              <Select
                label="Estado"
                value={filterDraft.status}
                onChange={(event) => handleFilterChange('status', event.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                {EMAIL_DELIVERY_STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Desde"
              type="date"
              value={filterDraft.createdFrom}
              onChange={(event) => handleFilterChange('createdFrom', event.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              label="Hasta"
              type="date"
              value={filterDraft.createdTo}
              onChange={(event) => handleFilterChange('createdTo', event.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <Button variant="contained" onClick={handleApplyFilters}>
              Filtrar
            </Button>
            <Button variant="outlined" onClick={handleClearFilters}>
              Limpiar
            </Button>
          </Box>
          {loading ? <LinearProgress className="transport-linear-progress" /> : null}
          <StaticRowsTable
            columns={emailDeliveryColumns()}
            rows={deliveries}
            meta={meta}
            table={table}
            onEdit={setSelectedDelivery}
            actionLabel="Ver"
          />
        </Paper>
      </AnimatedSection>
      <EmailDeliveryDetailDialog
        open={Boolean(selectedDelivery)}
        delivery={selectedDelivery}
        onClose={() => setSelectedDelivery(null)}
        onDownloadAttachment={downloadAttachment}
      />
    </AnimatedPage>
  );
}

function LoadOrdersPage({ page }) {
  const documentsTable = useRemoteTableState('', 'asc');
  const documentsApiParams = documentsTable.apiParams;
  const setDocumentsMeta = documentsTable.setMeta;
  const ordersTable = useLoadOrderListState();
  const ordersApiParams = ordersTable.apiParams;
  const setOrdersMeta = ordersTable.setMeta;
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [viewDocuments, setViewDocuments] = useState(emptyViewDocuments());
  const [orders, setOrders] = useState([]);
  const [selectedLineIds, setSelectedLineIds] = useState([]);
  const [masters, setMasters] = useState(emptyMasters());
  const [viewDateRangeInput, setViewDateRangeInput] = useState(defaultDateRange());
  const viewDateRange = useDebouncedValue(viewDateRangeInput, DATE_FILTER_DEBOUNCE_MS);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPendingLine, setEditingPendingLine] = useState(null);
  const [loading, setLoading] = useState({
    customers: true,
    documents: false,
    masters: false,
  });
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const loadCustomers = useCallback(async () => {
    setLoading((current) => ({ ...current, customers: true }));
    try {
      const data = await loadOrderCustomerOptions();
      setCustomers(data);
      setSelectedCustomer((current) => data.some((item) => item.cliente.cod_cliente === current) ? current : '');
    } catch (error) {
      setMessage(apiMessage(error, 'No se han podido cargar los clientes.'));
    } finally {
      setLoading((current) => ({ ...current, customers: false }));
    }
  }, []);

  const loadDocuments = useCallback(async (codCliente) => {
    setLoading((current) => ({ ...current, documents: true }));
    try {
      const response = await getLogisticsViewDocuments({
        ...(codCliente ? { codCliente } : {}),
        documentType: 'PEDIDO',
        operationType: 'INTERNAL_LOAD_ORDER',
        fechaDesde: viewDateRange.fechaDesde,
        fechaHasta: viewDateRange.fechaHasta,
        ...documentsApiParams,
      });
      setDocumentsMeta(response?.meta);
      setViewDocuments(normalizeViewDocuments(response, codCliente));
      setSelectedLineIds([]);
    } catch (error) {
      setViewDocuments(emptyViewDocuments());
      setSelectedLineIds([]);
      setMessage(apiMessage(error, 'No se han podido cargar las lineas de pedido.'));
    } finally {
      setLoading((current) => ({ ...current, documents: false }));
    }
  }, [documentsApiParams, setDocumentsMeta, viewDateRange.fechaDesde, viewDateRange.fechaHasta]);

  const loadMasters = useCallback(async () => {
    setLoading((current) => ({ ...current, masters: true }));
    try {
      const options = await loadMasterOptions();
      setMasters(options);
      return options;
    } catch (error) {
      setMessage(apiMessage(error, 'No se han podido cargar maestros.'));
      return emptyMasters();
    } finally {
      setLoading((current) => ({ ...current, masters: false }));
    }
  }, []);

  const loadOrders = useCallback(async () => {
    setLoading((current) => ({ ...current, orders: true }));
    try {
      const response = await getLogisticsLoadOrders({
        ...ordersApiParams,
        tripType: 'LOAD',
        operationType: 'INTERNAL_LOAD_ORDER',
        loadDateFrom: viewDateRange.fechaDesde,
        loadDateTo: viewDateRange.fechaHasta,
      });
      setOrdersMeta(response?.meta);
      setOrders(responseList(response));
    } catch (error) {
      setOrders([]);
      setMessage(apiMessage(error, 'No se han podido cargar ordenes de carga.'));
    } finally {
      setLoading((current) => ({ ...current, orders: false }));
    }
  }, [ordersApiParams, setOrdersMeta, viewDateRange.fechaDesde, viewDateRange.fechaHasta]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  useEffect(() => {
    loadDocuments(selectedCustomer);
  }, [loadDocuments, selectedCustomer]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const selectedCustomerInfo = useMemo(
    () =>
      customers.find((item) => item?.cliente?.cod_cliente === selectedCustomer) ||
      null,
    [customers, selectedCustomer]
  );

  const selectedLineKeySet = useMemo(
    () => new Set(selectedLineIds),
    [selectedLineIds]
  );

  const documentRows = viewDocuments.rows;
  const { page: documentsPage, rowsPerPage: documentsPageSize, handlePageChange: changeDocumentsPage } = documentsTable;
  const documentsTotal = Number(viewDocuments.meta?.total || 0);
  useEffect(() => {
    if (!loading.documents && documentsPage > 0 && documentsPage * documentsPageSize >= documentsTotal) {
      changeDocumentsPage(null, Math.max(Math.ceil(documentsTotal / documentsPageSize) - 1, 0));
    }
  }, [loading.documents, documentsPage, documentsPageSize, documentsTotal, changeDocumentsPage]);
  const selectedRows = useMemo(
    () =>
      documentRows.filter((row) =>
        selectedLineKeySet.has(getLineKey(row))
      ),
    [documentRows, selectedLineKeySet]
  );

  const handleToggleLine = (row) => {
    const key = getLineKey(row);
    setSelectedLineIds((current) =>
      current.includes(key)
        ? current.filter((value) => value !== key)
        : [...current, key]
    );
  };

  const handleSelectAllShownLines = () => {
    setSelectedLineIds(documentRows.map(getLineKey).filter(Boolean));
  };

  const handleClearSelectedLines = () => {
    setSelectedLineIds([]);
  };

  const handleExportSelectedLines = async () => {
    if (!selectedRows.length) {
      return;
    }
    const fileName = `lineas-pedido-${selectedCustomer || 'todos'}-${toInputDate(new Date())}.xlsx`;

    try {
      await auditLogisticsExport({
        exportType: 'VIEW_DOCUMENT_LINES',
        documentType: 'PEDIDO',
        codCliente: selectedCustomer || undefined,
        fileName,
        rowCount: selectedRows.length,
        selectedLineIds: selectedRows.map(getLineKey).filter(Boolean),
        columns: viewDocuments.columns
          .filter((column) => column.visible !== false)
          .map((column) => column.field),
      });
    } catch (error) {
      console.warn('No se ha podido registrar la auditoria de la exportacion.', error);
    }

    try {
      await exportRowsToExcel({
        rows: selectedRows,
        columns: viewDocuments.columns,
        fileName,
        sheetName: 'Lineas pedido',
      });
    } catch (error) {
      setMessage(apiMessage(error, 'No se ha podido generar el Excel.'));
    }
  };

  const handleOpenLoadOrderDialog = async () => {
    if (hasMultipleCustomers(selectedRows)) {
      setMessage({ severity: 'warning', text: 'Selecciona lineas de un solo cliente para crear la orden.' });
      return;
    }
    await loadMasters();
    setDialogOpen(true);
  };

  const handleSaveLoadOrder = async (payload) => {
    setActionLoading(true);
    setMessage(null);
    try {
      await createLogisticsLoadOrder(payload);
      setMessage({ severity: 'success', text: 'Orden de carga creada.' });
      setDialogOpen(false);
      setSelectedLineIds([]);
      await Promise.all([
        loadDocuments(selectedCustomer),
        loadOrders(),
      ]);
    } catch (error) {
      setMessage(apiMessage(error, 'No se ha podido guardar la orden de carga.'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditPendingLine = async (row) => {
    const lineId = getLineId(row);
    if (!lineId) {
      setMessage({ severity: 'error', text: 'La linea no tiene identificador para editar.' });
      return;
    }
    setActionLoading(true);
    setMessage(null);
    try {
      const line = await getLogisticsDocumentLine(lineId);
      setEditingPendingLine(line);
    } catch (error) {
      setMessage(apiMessage(error, 'No se ha podido cargar el detalle de la linea.'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleSavePendingLine = async (payload) => {
    const lineId = getLineId(editingPendingLine);
    if (!lineId) {
      setMessage({ severity: 'error', text: 'La linea no tiene identificador para editar.' });
      return;
    }
    setActionLoading(true);
    setMessage(null);
    try {
      await updateLogisticsDocumentLine(lineId, payload);
      setEditingPendingLine(null);
      setMessage({ severity: 'success', text: 'Linea actualizada.' });
      await loadDocuments(selectedCustomer);
    } catch (error) {
      setMessage(apiMessage(error, 'No se ha podido actualizar la linea.'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeletePendingLines = async (rows) => {
    if (!rows.length || actionLoading) return;
    const ids = rows.map(getLineId);
    if (ids.some((id) => !id)) {
      setMessage({ severity: 'error', text: 'Alguna linea no tiene identificador para eliminar.' });
      return;
    }
    const confirmed = window.confirm(
      `Eliminar ${rows.length === 1 ? `la linea ${lineLabel(rows[0])}` : `las ${rows.length} lineas seleccionadas`} solo de Suite Transporte? Esta accion no modifica Business Central. Si alguna tiene referencias de trabajo, no se eliminara ninguna.`
    );
    if (!confirmed) {
      return;
    }
    setActionLoading(true);
    setMessage(null);
    try {
      if (ids.length === 1) await deleteLogisticsDocumentLine(ids[0]);
      else await deleteLogisticsDocumentLines(ids);
      setSelectedLineIds([]);
      setMessage({ severity: 'success', text: ids.length === 1 ? 'Linea eliminada de Suite Transporte.' : `${ids.length} lineas eliminadas de Suite Transporte.` });
      await loadDocuments(selectedCustomer);
    } catch (error) {
      setMessage(apiMessage(error, 'No se han podido eliminar las lineas.'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeletePendingLine = (row) => handleDeletePendingLines([row]);

  const loadingAny = Object.values(loading).some(Boolean);

  return (
    <AnimatedPage>
      <Box className="suite-page transport-page">
        <PageHeader
          page={page}
          description="Lineas de pedido importadas desde Business Central, agrupadas por cliente para preparar ordenes de carga."
        />

        {message ? (
          <Alert severity={message.severity} onClose={() => setMessage(null)}>
            {message.text}
          </Alert>
        ) : null}
        {loadingAny || actionLoading ? <LinearProgress /> : null}

        <Paper className="dashboard-section-card transport-date-filter-card" elevation={0}>
          <Box className="transport-filter-bar">
            <Button
              size="small"
              variant={isFechaDateRangePreset(viewDateRangeInput, 'today') ? 'contained' : 'outlined'}
              onClick={() => setViewDateRangeInput((current) => applyFechaDateRangePreset(current, 'today'))}
            >
              Hoy
            </Button>
            <Button
              size="small"
              variant={isFechaDateRangePreset(viewDateRangeInput, 'week') ? 'contained' : 'outlined'}
              onClick={() => setViewDateRangeInput((current) => applyFechaDateRangePreset(current, 'week'))}
            >
              Semana
            </Button>
            <TextField
              label="Fecha carga desde"
              size="small"
              type="date"
              value={viewDateRangeInput.fechaDesde}
              onChange={(event) =>
                setViewDateRangeInput((current) => ({
                  ...current,
                  fechaDesde: event.target.value,
                }))
              }
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              label="Fecha carga hasta"
              size="small"
              type="date"
              value={viewDateRangeInput.fechaHasta}
              onChange={(event) =>
                setViewDateRangeInput((current) => ({
                  ...current,
                  fechaHasta: event.target.value,
                }))
              }
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <Button
              variant="outlined"
              startIcon={<RefreshRoundedIcon />}
              disabled={actionLoading}
              onClick={() => loadDocuments(selectedCustomer)}
            >
              Actualizar
            </Button>
          </Box>
        </Paper>

        <AnimatedSection className="transport-view-section">
          <Paper className="dashboard-section-card suite-view-card" elevation={0}>
            <Box className="transport-section-header transport-section-header-spread">
              <Box>
                <Typography className="section-title">Pedidos por cliente</Typography>
                <Typography className="transport-section-subtitle">
                  {selectedCustomerInfo?.cliente?.des_cliente ||
                    'Todos los clientes'}
                </Typography>
              </Box>
              <Stack direction="row" gap={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <Chip
                  className="suite-status-chip"
                  label={`${selectedRows.length} seleccionadas`}
                />
                <Chip
                  className="transport-count-chip transport-count-chip-warning"
                  label={`${documentRows.length} lineas`}
                />
                <Button
                  size="small"
                  variant="outlined"
                  disabled={!documentRows.length || actionLoading}
                  onClick={handleSelectAllShownLines}
                >
                  Seleccionar todo
                </Button>
                <Button
                  size="small"
                  variant="text"
                  disabled={!selectedRows.length || actionLoading}
                  onClick={handleClearSelectedLines}
                >
                  Limpiar seleccion
                </Button>
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  startIcon={<DeleteOutlineRoundedIcon />}
                  disabled={!selectedRows.length || actionLoading}
                  onClick={() => handleDeletePendingLines(selectedRows)}
                >
                  Eliminar seleccionadas
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<FileDownloadRoundedIcon />}
                  disabled={!selectedRows.length || actionLoading}
                  onClick={handleExportSelectedLines}
                >
                  Exportar Excel
                </Button>
                <Button
                  size="small"
                  className="suite-primary-button"
                  variant="contained"
                  startIcon={<AddRoundedIcon />}
                  disabled={!selectedRows.length || actionLoading}
                  onClick={handleOpenLoadOrderDialog}
                >
                  Crear orden
                </Button>
              </Stack>
            </Box>

            <CustomerTabs
              customers={customers}
              value={selectedCustomer}
              onChange={(code) => {
                setSelectedLineIds([]);
                setSelectedCustomer(code);
              }}
              searchable
            />

            <DynamicRowsTable
              columns={viewDocuments.columns}
              rows={documentRows}
              meta={viewDocuments.meta}
              table={documentsTable}
              emptyText="No hay lineas de pedido importadas para este cliente o no existe perfil activo."
              selectable
              selectedKeys={selectedLineKeySet}
              onToggleRow={handleToggleLine}
              onEditRow={handleEditPendingLine}
              onDeleteRow={handleDeletePendingLine}
            />
          </Paper>
        </AnimatedSection>

        <LoadOrdersPanel
          rows={orders}
          meta={ordersTable.meta}
          table={ordersTable}
          onRefresh={() => Promise.all([loadOrders(), loadDocuments(selectedCustomer)])}
        />

        <LoadOrderDialog
          open={dialogOpen}
          loadOrder={null}
          onClose={() => {
            setDialogOpen(false);
          }}
          onSubmit={handleSaveLoadOrder}
          selectedRows={selectedRows}
          masterOptions={masters}
          statuses={masters.loadOrderStatuses}
          submitting={actionLoading}
        />
        <PendingLineEditDialog
          open={Boolean(editingPendingLine)}
          line={editingPendingLine}
          loading={actionLoading}
          onClose={() => setEditingPendingLine(null)}
          onSubmit={handleSavePendingLine}
        />

        {selectedRows.length > 0 ? (
          <Fab
            variant="extended"
            className="transport-floating-action"
            disabled={actionLoading}
            onClick={handleOpenLoadOrderDialog}
          >
            <AddRoundedIcon sx={{ mr: 1 }} />
            Nueva orden ({selectedRows.length})
          </Fab>
        ) : null}
      </Box>
    </AnimatedPage>
  );
}

function LoadTripPage({ page }) {
  const documentsTable = useRemoteTableState('', 'asc');
  const tripsTable = useLoadOrderListState();
  const tripsApiParams = tripsTable.apiParams;
  const setTripsMeta = tripsTable.setMeta;
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [viewDocuments, setViewDocuments] = useState(emptyViewDocuments());
  const [trips, setTrips] = useState([]);
  const [intermediateTrips, setIntermediateTrips] = useState([]);
  const [selectedLineIds, setSelectedLineIds] = useState([]);
  const [masters, setMasters] = useState(emptyMasters());
  const [dateRangeInput, setDateRangeInput] = useState(defaultDateRange());
  const dateRange = useDebouncedValue(dateRangeInput, DATE_FILTER_DEBOUNCE_MS);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [stageDialogOrder, setStageDialogOrder] = useState(null);
  const [loading, setLoading] = useState({
    customers: true,
    documents: false,
    trips: false,
    stages: false,
    masters: false,
  });
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const loadCustomers = useCallback(async () => {
    setLoading((current) => ({ ...current, customers: true }));
    try {
      const data = await loadOrderCustomerOptions();
      setCustomers(data);
      setSelectedCustomer((current) => data.some((item) => item.cliente.cod_cliente === current) ? current : '');
    } catch (error) {
      setMessage(apiMessage(error, 'No se han podido cargar los clientes.'));
    } finally {
      setLoading((current) => ({ ...current, customers: false }));
    }
  }, []);

  const loadDocuments = useCallback(async () => {
    setLoading((current) => ({ ...current, documents: true }));
    try {
      const [orders, assignedTrips] = await Promise.all([
        loadAllLogisticsRows(getLogisticsLoadOrders, {
          operationType: 'INTERNAL_LOAD_ORDER', tripType: 'LOAD',
          loadDateFrom: dateRange.fechaDesde, loadDateTo: dateRange.fechaHasta,
          sortBy: 'load_date', sortDirection: 'desc',
        }),
        loadAllLogisticsRows(getLogisticsLoadOrders, { operationType: 'LOAD_TRIP', tripType: 'LOAD' }),
      ]);
      setViewDocuments({ rows: flattenLoadOrderOrigins(activeLoadOrders(orders), activeLoadOrders(assignedTrips)), columns: [], meta: {} });
      setSelectedLineIds([]);
    } catch (error) {
      setViewDocuments(emptyViewDocuments());
      setSelectedLineIds([]);
      setMessage(apiMessage(error, 'No se han podido cargar las ordenes de carga.'));
    } finally {
      setLoading((current) => ({ ...current, documents: false }));
    }
  }, [dateRange.fechaDesde, dateRange.fechaHasta]);

  const loadTrips = useCallback(async () => {
    setLoading((current) => ({ ...current, trips: true }));
    try {
      const response = await getLogisticsLoadOrders({
        ...tripsApiParams,
        tripType: 'LOAD',
        operationType: 'LOAD_TRIP',
      });
      setTripsMeta(response?.meta);
      setTrips(activeLoadOrders(responseList(response)));
    } catch (error) {
      setTrips([]);
      setMessage(apiMessage(error, 'No se han podido cargar los viajes de carga.'));
    } finally {
      setLoading((current) => ({ ...current, trips: false }));
    }
  }, [setTripsMeta, tripsApiParams]);

  const loadIntermediateTrips = useCallback(async () => {
    setLoading((current) => ({ ...current, stages: true }));
    try {
      const responses = await Promise.all(
        INTERMEDIATE_TRIP_TYPES.map((tripType) =>
          getLogisticsLoadOrders({
            page: 1,
            limit: 500,
            sortBy: 'load_date',
            sortDirection: 'desc',
            tripType,
          })
        )
      );
      setIntermediateTrips(activeLoadOrders(responses.flatMap(responseList)));
    } catch (error) {
      setIntermediateTrips([]);
      setMessage(apiMessage(error, 'No se han podido cargar etapas intermedias.'));
    } finally {
      setLoading((current) => ({ ...current, stages: false }));
    }
  }, []);

  const loadMasters = useCallback(async () => {
    setLoading((current) => ({ ...current, masters: true }));
    try {
      const options = await loadMasterOptions();
      setMasters(options);
      return options;
    } catch (error) {
      setMessage(apiMessage(error, 'No se han podido cargar maestros.'));
      return emptyMasters();
    } finally {
      setLoading((current) => ({ ...current, masters: false }));
    }
  }, []);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  useEffect(() => {
    loadTrips();
  }, [loadTrips]);

  useEffect(() => {
    loadIntermediateTrips();
  }, [loadIntermediateTrips]);

  const selectedCustomerInfo = useMemo(
    () => customers.find((item) => item?.cliente?.cod_cliente === selectedCustomer) || null,
    [customers, selectedCustomer]
  );
  const selectedLineKeySet = useMemo(() => new Set(selectedLineIds), [selectedLineIds]);
  const availableRows = useMemo(
    () => viewDocuments.rows.filter((row) => linePendingPallets(row) > 0
      && (!selectedCustomer || row.cod_cliente === selectedCustomer)
      && customers.some((customer) => customer.cliente.cod_cliente === row.cod_cliente)),
    [viewDocuments.rows, selectedCustomer, customers]
  );
  const selectedRows = useMemo(
    () => availableRows.filter((row) => selectedLineKeySet.has(getLineKey(row))),
    [availableRows, selectedLineKeySet]
  );
  const columns = useMemo(
    () => ensureLoadTripColumns(viewDocuments.columns),
    [viewDocuments.columns]
  );
  const stagesByParent = useMemo(
    () => groupLoadOrdersByParent(intermediateTrips),
    [intermediateTrips]
  );
  const loadingAny = Object.values(loading).some(Boolean);

  const availableOrders = useMemo(() => {
    const groups = new Map();
    availableRows.forEach((row) => {
      if (!groups.has(row.load_order_id)) groups.set(row.load_order_id, { id: row.load_order_id, rows: [] });
      groups.get(row.load_order_id).rows.push(row);
    });
    return [...groups.values()];
  }, [availableRows]);
  const { page: originPage, rowsPerPage: originsPerPage, handlePageChange: changeOriginPage } = documentsTable;
  const visibleOrders = availableOrders.slice(originPage * originsPerPage, (originPage + 1) * originsPerPage);
  useEffect(() => {
    if (originPage > 0 && originPage * originsPerPage >= availableOrders.length) changeOriginPage(null, 0);
  }, [availableOrders.length, originPage, originsPerPage, changeOriginPage]);

  const handleToggleLine = (row) => {
    const key = getLineKey(row);
    setSelectedLineIds((current) =>
      current.includes(key)
        ? current.filter((value) => value !== key)
        : [...current, key]
    );
  };

  const handleOpenTripDialog = async (rowsToLoad = selectedRows) => {
    if (hasMultipleCustomers(rowsToLoad)) {
      setMessage({ severity: 'warning', text: 'Selecciona lineas de un solo cliente para crear el viaje.' });
      return;
    }
    if (!rowsToLoad.length || rowsToLoad.some((row) => !row.load_order_id)) {
      setMessage({ severity: 'warning', text: 'Selecciona lineas de las ordenes de carga para crear el viaje.' });
      return;
    }
    setSelectedLineIds(rowsToLoad.map(getLineKey));
    await loadMasters();
    setDialogOpen(true);
  };

  const handleSaveTrip = async (payload) => {
    setActionLoading(true);
    setMessage(null);
    try {
      await createLogisticsLoadOrder(payload);
      setDialogOpen(false);
      setSelectedLineIds([]);
      setMessage({ severity: 'success', text: 'Viaje de carga creado.' });
      await Promise.all([loadDocuments(selectedCustomer), loadTrips(), loadIntermediateTrips()]);
    } catch (error) {
      setMessage(apiMessage(error, 'No se ha podido crear el viaje de carga.'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveIntermediateStage = async (payload) => {
    setActionLoading(true);
    setMessage(null);
    try {
      await createLogisticsLoadOrder(payload);
      setStageDialogOrder(null);
      setMessage({ severity: 'success', text: 'Etapa intermedia creada.' });
      await Promise.all([loadTrips(), loadIntermediateTrips()]);
    } catch (error) {
      setMessage(apiMessage(error, 'No se ha podido crear la etapa intermedia.'));
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <AnimatedPage>
      <Box className="suite-page transport-page">
        <PageHeader page={page} />

        {message ? (
          <Alert severity={message.severity} onClose={() => setMessage(null)}>
            {message.text}
          </Alert>
        ) : null}
        {loadingAny || actionLoading ? <LinearProgress /> : null}

        <Paper className="dashboard-section-card transport-date-filter-card" elevation={0}>
          <Box className="transport-filter-bar">
            <Button
              size="small"
              variant={isFechaDateRangePreset(dateRangeInput, 'today') ? 'contained' : 'outlined'}
              onClick={() => setDateRangeInput((current) => applyFechaDateRangePreset(current, 'today'))}
            >
              Hoy
            </Button>
            <Button
              size="small"
              variant={isFechaDateRangePreset(dateRangeInput, 'week') ? 'contained' : 'outlined'}
              onClick={() => setDateRangeInput((current) => applyFechaDateRangePreset(current, 'week'))}
            >
              Semana
            </Button>
            <TextField
              label="Fecha carga desde"
              size="small"
              type="date"
              value={dateRangeInput.fechaDesde}
              onChange={(event) =>
                setDateRangeInput((current) => ({ ...current, fechaDesde: event.target.value }))
              }
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              label="Fecha carga hasta"
              size="small"
              type="date"
              value={dateRangeInput.fechaHasta}
              onChange={(event) =>
                setDateRangeInput((current) => ({ ...current, fechaHasta: event.target.value }))
              }
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <Button
              variant="outlined"
              startIcon={<RefreshRoundedIcon />}
              disabled={actionLoading}
              onClick={() => Promise.all([loadDocuments(selectedCustomer), loadTrips(), loadIntermediateTrips()])}
            >
              Actualizar
            </Button>
          </Box>
        </Paper>

        <AnimatedSection className="transport-view-section">
          <Paper className="dashboard-section-card suite-view-card" elevation={0}>
            <Box className="transport-section-header transport-section-header-spread">
              <Box>
                <Typography className="section-title">Ordenes de carga pendientes de asignar a viaje</Typography>
                <Typography className="transport-section-subtitle">
                  {selectedCustomerInfo?.cliente?.des_cliente ||
                    'Todos los clientes'}
                </Typography>
              </Box>
              <Stack direction="row" gap={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <Chip className="suite-status-chip" label={`${selectedRows.length} seleccionadas`} />
                <Chip
                  className="transport-count-chip transport-count-chip-warning"
                  label={`${availableOrders.length} ordenes`}
                />
                <Button
                  size="small"
                  variant="outlined"
                  disabled={!availableRows.length || actionLoading}
                  onClick={() => setSelectedLineIds(availableRows.map(getLineKey).filter(Boolean))}
                >
                  Seleccionar todo
                </Button>
                <Button
                  size="small"
                  variant="text"
                  disabled={!selectedRows.length || actionLoading}
                  onClick={() => setSelectedLineIds([])}
                >
                  Limpiar seleccion
                </Button>
                <Button
                  size="small"
                  className="suite-primary-button"
                  variant="contained"
                  startIcon={<LocalShippingRoundedIcon />}
                  disabled={!selectedRows.length || actionLoading}
                  onClick={() => handleOpenTripDialog()}
                >
                  Crear viaje
                </Button>
              </Stack>
            </Box>

            <CustomerTabs customers={customers} value={selectedCustomer} onChange={(code) => {
              setSelectedLineIds([]);
              setSelectedCustomer(code);
            }} searchable />

            {!availableOrders.length ? <Box className="transport-empty-state">No hay ordenes de carga con palets pendientes de asignar a viaje.</Box> : null}
            {visibleOrders.map((origin) => {
              const first = origin.rows[0];
              return <Accordion key={origin.id} defaultExpanded disableGutters className="transport-order-accordion">
                <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
                  <Stack direction="row" gap={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                    <LoadDocumentHeading order={{ ...first.document_heading, lines: origin.rows }} />
                    <Chip size="small" label={`${formatQuantity(origin.rows.reduce((sum, row) => sum + linePendingPallets(row), 0))} palets pendientes`} />
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack direction="row" gap={1} sx={{ justifyContent: 'flex-end', mb: 1 }}>
                    <Button size="small" variant="outlined" disabled={actionLoading}
                      onClick={() => setSelectedLineIds((current) => [...new Set([...current, ...origin.rows.map(getLineKey)])])}>
                      Seleccionar lineas de esta orden
                    </Button>
                    <Button size="small" variant="contained" disabled={actionLoading}
                      aria-label={`Crear viaje desde ${first.load_order_number}`}
                      onClick={() => handleOpenTripDialog(origin.rows)}>Crear viaje de esta orden</Button>
                  </Stack>
                  <DynamicRowsTable columns={columns} rows={origin.rows} paginated={false}
                    selectable selectedKeys={selectedLineKeySet} onToggleRow={handleToggleLine} />
                </AccordionDetails>
              </Accordion>;
            })}
            {availableOrders.length ? <TablePager table={documentsTable} count={availableOrders.length} /> : null}
          </Paper>
        </AnimatedSection>

        <LoadOrdersPanel
          rows={trips}
          meta={tripsTable.meta}
          table={tripsTable}
          onRefresh={() => Promise.all([loadDocuments(selectedCustomer), loadTrips(), loadIntermediateTrips()])}
          title="Viajes de carga"
          subtitle="Viajes de recogida creados, matriculas y palets asignados por linea."
          emptyText="No hay viajes de carga."
          countLabel="viajes"
          childStagesByParent={stagesByParent}
          onAddStage={async (order) => {
            const options = await loadMasters();
            setMasters(options);
            setStageDialogOrder(order);
          }}
        />

        <LoadTripDialog
          open={dialogOpen}
          selectedRows={selectedRows}
          masterOptions={masters}
          submitting={actionLoading}
          onClose={() => setDialogOpen(false)}
          onSubmit={handleSaveTrip}
        />
        <IntermediateStageDialog
          open={Boolean(stageDialogOrder)}
          order={stageDialogOrder}
          masterOptions={masters}
          submitting={actionLoading}
          onClose={() => setStageDialogOrder(null)}
          onSubmit={handleSaveIntermediateStage}
        />
      </Box>
    </AnimatedPage>
  );
}

function UnloadTripPage({ page }) {
  const loadTripsTable = useRemoteTableState('load_date', 'desc');
  const unloadTripsTable = useLoadOrderListState();
  const unloadTripsApiParams = unloadTripsTable.apiParams;
  const setUnloadTripsMeta = unloadTripsTable.setMeta;
  const changeUnloadPage = unloadTripsTable.handlePageChange;
  const [loadTrips, setLoadTrips] = useState([]);
  const [intermediateTrips, setIntermediateTrips] = useState([]);
  const [unloadTrips, setUnloadTrips] = useState([]);
  const unloadRequest = useRef(0);
  const [customerMasters, setCustomerMasters] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [selectedUnloadCustomer, setSelectedUnloadCustomer] = useState('');
  const [selectedLineIds, setSelectedLineIds] = useState([]);
  const [masters, setMasters] = useState(emptyMasters());
  const [dateRangeInput, setDateRangeInput] = useState({ fechaDesde: '', fechaHasta: '' });
  const [unloadDateRange, setUnloadDateRange] = useState({ fechaDesde: '', fechaHasta: '' });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState({ loads: false, stages: false, unloads: false, masters: false, customers: true });
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const loadLoadTrips = useCallback(async () => {
    setLoading((current) => ({ ...current, loads: true }));
    try {
      const trips = await loadAllLogisticsRows(getLogisticsLoadOrders, {
        sortBy: 'load_date', sortDirection: 'desc',
        tripType: 'LOAD',
        operationType: 'LOAD_TRIP',
      });
      setLoadTrips(activeLoadOrders(trips));
      setSelectedLineIds([]);
    } catch (error) {
      setLoadTrips([]);
      setMessage(apiMessage(error, 'No se han podido cargar los viajes de carga.'));
    } finally {
      setLoading((current) => ({ ...current, loads: false }));
    }
  }, []);

  const loadIntermediateTrips = useCallback(async () => {
    setLoading((current) => ({ ...current, stages: true }));
    try {
      const responses = await Promise.all(
        INTERMEDIATE_TRIP_TYPES.map((tripType) =>
          loadAllLogisticsRows(getLogisticsLoadOrders, {
            sortBy: 'load_date',
            sortDirection: 'desc',
            tripType,
          })
        )
      );
      setIntermediateTrips(activeLoadOrders(responses.flat()));
    } catch (error) {
      setIntermediateTrips([]);
      setMessage(apiMessage(error, 'No se han podido cargar etapas intermedias.'));
    } finally {
      setLoading((current) => ({ ...current, stages: false }));
    }
  }, []);

  const loadUnloadTrips = useCallback(async () => {
    const request = ++unloadRequest.current;
    setLoading((current) => ({ ...current, unloads: true }));
    try {
      const response = await getLogisticsLoadOrders({
        ...unloadTripsApiParams,
        tripType: 'UNLOAD',
        operationType: 'UNLOAD_TRIP',
        ...(selectedUnloadCustomer ? { codCliente: selectedUnloadCustomer } : {}),
        ...(unloadDateRange.fechaDesde ? { loadDateFrom: unloadDateRange.fechaDesde } : {}),
        ...(unloadDateRange.fechaHasta ? { loadDateTo: unloadDateRange.fechaHasta } : {}),
      });
      if (request !== unloadRequest.current) return;
      setUnloadTripsMeta(response?.meta);
      setUnloadTrips(activeLoadOrders(responseList(response)));
    } catch (error) {
      if (request !== unloadRequest.current) return;
      setUnloadTrips([]);
      setMessage(apiMessage(error, 'No se han podido cargar los viajes de descarga.'));
    } finally {
      if (request === unloadRequest.current) setLoading((current) => ({ ...current, unloads: false }));
    }
  }, [setUnloadTripsMeta, unloadTripsApiParams, selectedUnloadCustomer, unloadDateRange.fechaDesde, unloadDateRange.fechaHasta]);

  const loadMasters = useCallback(async () => {
    setLoading((current) => ({ ...current, masters: true }));
    try {
      const options = await loadMasterOptions();
      setMasters(options);
      return options;
    } catch (error) {
      setMessage(apiMessage(error, 'No se han podido cargar maestros.'));
      return emptyMasters();
    } finally {
      setLoading((current) => ({ ...current, masters: false }));
    }
  }, []);

  useEffect(() => {
    loadLoadTrips();
  }, [loadLoadTrips]);

  useEffect(() => {
    loadIntermediateTrips();
  }, [loadIntermediateTrips]);

  useEffect(() => {
    loadUnloadTrips();
    return () => { unloadRequest.current += 1; };
  }, [loadUnloadTrips]);

  const sourceRows = useMemo(
    () => flattenUnloadTripRows([...loadTrips, ...intermediateTrips]),
    [intermediateTrips, loadTrips]
  );
  const loadCustomers = useCallback(async () => {
    setLoading((current) => ({ ...current, customers: true }));
    try {
      setCustomerMasters(await loadAllLogisticsRows(getLogisticsClientes, { sortBy: 'name', sortDirection: 'asc' }));
    } catch (error) {
      setCustomerMasters(null);
      setMessage(apiMessage(error, 'No se han podido cargar los clientes.'));
    } finally {
      setLoading((current) => ({ ...current, customers: false }));
    }
  }, []);
  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);
  const handleCustomerChange = (code) => {
    setSelectedCustomer(code);
    setSelectedLineIds([]);
    loadTripsTable.handlePageChange(null, 0);
  };
  const inactiveCustomerCodes = useMemo(() => new Set((customerMasters || [])
    .filter((item) => item.active === false)
    .flatMap((item) => [item.code, item.cod_navision, item.codNavision].filter(Boolean))), [customerMasters]);
  const customers = useMemo(() => {
    const options = new Map();
    sourceRows.forEach((row) => {
      if (row.cod_cliente && !inactiveCustomerCodes.has(row.cod_cliente)) {
        options.set(row.cod_cliente, { cliente: { cod_cliente: row.cod_cliente, des_cliente: row.des_cliente || row.cod_cliente } });
      }
    });
    return [...options.values()].sort((left, right) => left.cliente.des_cliente.localeCompare(right.cliente.des_cliente, 'es'));
  }, [sourceRows, inactiveCustomerCodes]);
  const availableRows = useMemo(
    () => loading.customers || customerMasters === null ? [] : sourceRows.filter((row) => !inactiveCustomerCodes.has(row.cod_cliente)
      && (!selectedCustomer || row.cod_cliente === selectedCustomer) && lineUnloadPendingPallets(row) > 0),
    [sourceRows, selectedCustomer, inactiveCustomerCodes, loading.customers, customerMasters]
  );
  const availableOrigins = useMemo(() => {
    const groups = new Map();
    availableRows.forEach((row) => {
      if (!groups.has(row.load_order_id)) groups.set(row.load_order_id, { id: row.load_order_id, rows: [] });
      groups.get(row.load_order_id).rows.push(row);
    });
    return [...groups.values()];
  }, [availableRows]);
  const { page: originPage, rowsPerPage: originsPerPage, handlePageChange: changeOriginPage } = loadTripsTable;
  const visibleOrigins = availableOrigins.slice(originPage * originsPerPage, (originPage + 1) * originsPerPage);
  useEffect(() => {
    if (originPage > 0 && originPage * originsPerPage >= availableOrigins.length) {
      changeOriginPage(null, 0);
    }
  }, [availableOrigins.length, originPage, originsPerPage, changeOriginPage]);
  const unloadCustomers = useMemo(() => (customerMasters || []).filter((item) => item.active !== false)
    .map((item) => ({ cliente: { cod_cliente: item.cod_navision || item.codNavision || item.code, des_cliente: item.name || item.code } }))
    .filter((item) => item.cliente.cod_cliente)
    .sort((left, right) => left.cliente.des_cliente.localeCompare(right.cliente.des_cliente, 'es')), [customerMasters]);
  const changeUnloadCustomer = (code) => {
    setSelectedUnloadCustomer(code);
    changeUnloadPage(null, 0);
  };
  useEffect(() => {
    if (customerMasters !== null && !loading.customers && selectedUnloadCustomer
      && !unloadCustomers.some((item) => item.cliente.cod_cliente === selectedUnloadCustomer)) {
      setSelectedUnloadCustomer('');
      changeUnloadPage(null, 0);
    }
  }, [customerMasters, loading.customers, selectedUnloadCustomer, unloadCustomers, changeUnloadPage]);
  const applyUnloadDates = (range) => {
    setDateRangeInput(range);
    setUnloadDateRange(range);
    changeUnloadPage(null, 0);
  };
  const selectedLineKeySet = useMemo(() => new Set(selectedLineIds), [selectedLineIds]);
  const selectedRows = useMemo(
    () => availableRows.filter((row) => selectedLineKeySet.has(getLineKey(row))),
    [availableRows, selectedLineKeySet]
  );
  const loadingAny = Object.values(loading).some(Boolean);

  const handleToggleLine = (row) => {
    const key = getLineKey(row);
    setSelectedLineIds((current) =>
      current.includes(key) ? current.filter((value) => value !== key) : [...current, key]
    );
  };

  const handleOpenDialog = async (rowsToUnload = selectedRows) => {
    if (hasMultipleCustomers(rowsToUnload)) {
      setMessage({ severity: 'warning', text: 'Selecciona lineas de un solo cliente para crear el viaje.' });
      return;
    }
    if (new Set(rowsToUnload.map((row) => row.load_order_id)).size > 1) {
      setMessage({ severity: 'warning', text: 'Selecciona lineas de un mismo viaje de carga o etapa para crear la descarga.' });
      return;
    }
    setSelectedLineIds(rowsToUnload.map(getLineKey));
    await loadMasters();
    setDialogOpen(true);
  };

  const handleSaveUnloadTrip = async (payload) => {
    setActionLoading(true);
    setMessage(null);
    try {
      await createLogisticsLoadOrder(payload);
      setDialogOpen(false);
      setSelectedLineIds([]);
      setMessage({ severity: 'success', text: 'Viaje de descarga creado.' });
      await Promise.all([loadLoadTrips(), loadIntermediateTrips(), loadUnloadTrips()]);
    } catch (error) {
      setMessage(apiMessage(error, 'No se ha podido crear el viaje de descarga.'));
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <AnimatedPage>
      <Box className="suite-page transport-page">
        <PageHeader page={page} />
        {message ? (
          <Alert severity={message.severity} onClose={() => setMessage(null)}>
            {message.text}
          </Alert>
        ) : null}
        {loadingAny || actionLoading ? <LinearProgress /> : null}

        <AnimatedSection className="transport-view-section">
          <Paper className="dashboard-section-card suite-view-card" elevation={0}>
            <Box className="transport-section-header transport-section-header-spread">
              <Box>
                <Typography className="section-title">Viajes de carga pendientes de descarga</Typography>
                <Typography className="transport-section-subtitle">
                  Viajes de carga y etapas con mercancia disponible para crear una descarga.
                </Typography>
              </Box>
              <Stack direction="row" gap={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <Chip className="suite-status-chip" label={`${selectedRows.length} seleccionadas`} />
                <Chip className="transport-count-chip transport-count-chip-warning" label={`${availableOrigins.length} origenes`} />
                <Button size="small" variant="outlined" startIcon={<RefreshRoundedIcon />} disabled={actionLoading}
                  onClick={() => Promise.all([loadLoadTrips(), loadIntermediateTrips(), loadCustomers()])}>
                  Actualizar pendientes
                </Button>
                <Button
                  size="small"
                  variant="text"
                  disabled={!selectedRows.length || actionLoading}
                  onClick={() => setSelectedLineIds([])}
                >
                  Limpiar seleccion
                </Button>
                <Button
                  size="small"
                  className="suite-primary-button"
                  variant="contained"
                  startIcon={<LocalShippingRoundedIcon />}
                  disabled={!selectedRows.length || actionLoading}
                  onClick={() => handleOpenDialog()}
                >
                  Crear descarga
                </Button>
              </Stack>
            </Box>
            <CustomerTabs customers={customers} value={selectedCustomer} onChange={handleCustomerChange} searchable />
            {!availableOrigins.length ? (
              <Box className="transport-empty-state">{loadingAny ? 'Cargando viajes y etapas...' : 'No hay viajes de carga ni etapas pendientes de descarga.'}</Box>
            ) : visibleOrigins.map((origin) => {
              const first = origin.rows[0];
              const pending = origin.rows.reduce((total, row) => total + lineUnloadPendingPallets(row), 0);
              return (
                <Accordion key={origin.id} defaultExpanded disableGutters className="transport-order-accordion">
                  <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
                    <Box className="transport-section-header transport-section-header-spread" sx={{ width: '100%' }}>
                      <Box>
                        <LoadDocumentHeading order={{ ...first.document_heading, lines: origin.rows }}
                          label={first.stage_label === 'Carga' ? 'Viaje de carga' : [first.stage_label, first.origin_location].filter(Boolean).join(' - ')} />
                      </Box>
                      <Chip size="small" label={`${formatQuantity(pending)} palets pendientes`} />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack direction="row" gap={1} sx={{ justifyContent: 'flex-end', flexWrap: 'wrap', mb: 1 }}>
                      <Button size="small" variant="outlined" disabled={actionLoading}
                        onClick={() => setSelectedLineIds(origin.rows.map(getLineKey))}>Seleccionar lineas de este origen</Button>
                      <Button size="small" variant="contained" startIcon={<LocalShippingRoundedIcon />} disabled={actionLoading}
                        aria-label={`Crear descarga desde ${first.load_order_number}`}
                        onClick={() => handleOpenDialog(origin.rows)}>Crear descarga de este origen</Button>
                    </Stack>
                    <DynamicRowsTable columns={UNLOAD_TRIP_COLUMNS.filter((column) => column.field !== 'source_position_label')}
                      rows={origin.rows} paginated={false} selectable selectedKeys={selectedLineKeySet} onToggleRow={handleToggleLine} />
                  </AccordionDetails>
                </Accordion>
              );
            })}
            {availableOrigins.length ? <TablePager table={loadTripsTable} count={availableOrigins.length} /> : null}
          </Paper>
        </AnimatedSection>

        <LoadOrdersPanel
          rows={unloadTrips}
          meta={unloadTripsTable.meta}
          table={unloadTripsTable}
          onRefresh={() => Promise.all([loadLoadTrips(), loadIntermediateTrips(), loadUnloadTrips(), loadCustomers()])}
          title="Viajes de descarga"
          subtitle="Viajes de entrega creados, destinos, matriculas y palets descargados."
          emptyText="No hay viajes de descarga."
          countLabel="viajes"
          showScopeEmailAction={false}
          filters={(
            <Box role="group" aria-label="Filtros de viajes de descarga creados" sx={{ mb: 2 }}>
              <Box className="transport-filter-bar" sx={{ mb: 1 }}>
                <Button size="small" disabled={actionLoading} variant="outlined"
                  onClick={() => applyUnloadDates(applyFechaDateRangePreset(dateRangeInput, 'today'))}>Hoy</Button>
                <Button size="small" disabled={actionLoading} variant="outlined"
                  onClick={() => applyUnloadDates(applyFechaDateRangePreset(dateRangeInput, 'week'))}>Semana</Button>
                <TextField label="Fecha desde" size="small" type="date" value={dateRangeInput.fechaDesde}
                  disabled={actionLoading} onChange={(event) => setDateRangeInput((current) => ({ ...current, fechaDesde: event.target.value }))}
                  slotProps={{ inputLabel: { shrink: true } }} />
                <TextField label="Fecha hasta" size="small" type="date" value={dateRangeInput.fechaHasta}
                  disabled={actionLoading} onChange={(event) => setDateRangeInput((current) => ({ ...current, fechaHasta: event.target.value }))}
                  error={Boolean(dateRangeInput.fechaDesde && dateRangeInput.fechaHasta && dateRangeInput.fechaDesde > dateRangeInput.fechaHasta)}
                  helperText={dateRangeInput.fechaDesde && dateRangeInput.fechaHasta && dateRangeInput.fechaDesde > dateRangeInput.fechaHasta ? 'La fecha hasta debe ser igual o posterior a la fecha desde' : ''}
                  slotProps={{ inputLabel: { shrink: true } }} />
                <Button size="small" variant="outlined" disabled={actionLoading || Boolean(dateRangeInput.fechaDesde && dateRangeInput.fechaHasta && dateRangeInput.fechaDesde > dateRangeInput.fechaHasta)}
                  onClick={() => applyUnloadDates({ ...dateRangeInput })}>Aplicar fechas</Button>
                <Tooltip title="Quitar filtro de fechas">
                  <span><IconButton size="small" aria-label="Quitar filtro de fechas"
                    disabled={actionLoading || !(unloadDateRange.fechaDesde || unloadDateRange.fechaHasta || dateRangeInput.fechaDesde || dateRangeInput.fechaHasta)}
                    onClick={() => applyUnloadDates({ fechaDesde: '', fechaHasta: '' })}><FilterAltOffRoundedIcon fontSize="small" /></IconButton></span>
                </Tooltip>
              </Box>
              <CustomerTabs customers={unloadCustomers} value={selectedUnloadCustomer} onChange={changeUnloadCustomer}
                searchable showTabs={false} searchLabel="Buscar cliente de descargas" clearLabel="Quitar filtro de cliente de descargas"
                emptyText="No hay clientes activos para filtrar." />
            </Box>
          )}
        />

        <UnloadTripDialog
          open={dialogOpen}
          selectedRows={selectedRows}
          masterOptions={masters}
          submitting={actionLoading}
          onClose={() => setDialogOpen(false)}
          onSubmit={handleSaveUnloadTrip}
        />
      </Box>
    </AnimatedPage>
  );
}

function TraceabilityPage({ page }) {
  const table = useRemoteTableState('source_document_no', 'asc');
  const tableApiParams = table.apiParams;
  const setTableMeta = table.setMeta;
  const [filtersInput, setFiltersInput] = useState({
    ...defaultDateRange(),
    search: '',
    sourceDocumentNo: '',
  });
  const filters = useDebouncedValue(filtersInput, DATE_FILTER_DEBOUNCE_MS);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const loadTraceability = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getLogisticsTraceability({
        ...tableApiParams,
        documentType: 'PEDIDO',
        fechaDesde: filters.fechaDesde,
        fechaHasta: filters.fechaHasta,
        search: filters.search || undefined,
        sourceDocumentNo: filters.sourceDocumentNo || undefined,
      });
      setTableMeta(response?.meta);
      setRows(responseList(response));
    } catch (error) {
      setRows([]);
      setMessage(apiMessage(error, 'No se ha podido cargar la trazabilidad.'));
    } finally {
      setLoading(false);
    }
  }, [
    filters.fechaDesde,
    filters.fechaHasta,
    filters.search,
    filters.sourceDocumentNo,
    setTableMeta,
    tableApiParams,
  ]);

  useEffect(() => {
    loadTraceability();
  }, [loadTraceability]);

  return (
    <AnimatedPage>
      <Box className="suite-page transport-page">
        <PageHeader page={page} />
        {message ? (
          <Alert severity={message.severity} onClose={() => setMessage(null)}>
            {message.text}
          </Alert>
        ) : null}
        {loading ? <LinearProgress /> : null}

        <Paper className="dashboard-section-card transport-date-filter-card" elevation={0}>
          <Box className="transport-filter-bar">
            <Button
              size="small"
              variant={isFechaDateRangePreset(filtersInput, 'today') ? 'contained' : 'outlined'}
              onClick={() => setFiltersInput((current) => applyFechaDateRangePreset(current, 'today'))}
            >
              Hoy
            </Button>
            <Button
              size="small"
              variant={isFechaDateRangePreset(filtersInput, 'week') ? 'contained' : 'outlined'}
              onClick={() => setFiltersInput((current) => applyFechaDateRangePreset(current, 'week'))}
            >
              Semana
            </Button>
            <TextField
              label="Fecha desde"
              size="small"
              type="date"
              value={filtersInput.fechaDesde}
              onChange={(event) => setFiltersInput((current) => ({ ...current, fechaDesde: event.target.value }))}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              label="Fecha hasta"
              size="small"
              type="date"
              value={filtersInput.fechaHasta}
              onChange={(event) => setFiltersInput((current) => ({ ...current, fechaHasta: event.target.value }))}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              label="Pedido"
              size="small"
              value={filtersInput.sourceDocumentNo}
              onChange={(event) => setFiltersInput((current) => ({ ...current, sourceDocumentNo: event.target.value }))}
            />
            <TextField
              label="Buscar"
              size="small"
              value={filtersInput.search}
              onChange={(event) => setFiltersInput((current) => ({ ...current, search: event.target.value }))}
            />
            <Button
              variant="outlined"
              startIcon={<RefreshRoundedIcon />}
              disabled={loading}
              onClick={loadTraceability}
            >
              Actualizar
            </Button>
          </Box>
        </Paper>

        <AnimatedSection className="transport-view-section">
          <Paper className="dashboard-section-card suite-view-card" elevation={0}>
            <Box className="transport-section-header transport-section-header-spread">
              <Box>
                <Typography className="section-title">Trazabilidad de lineas</Typography>
                <Typography className="transport-section-subtitle">
                  Pedido, viajes, etapas, matriculas, destinos, documentos y palets pendientes.
                </Typography>
              </Box>
              <Chip className="transport-count-chip transport-count-chip-warning" label={`${table.meta.total || rows.length} lineas`} />
            </Box>

            {!rows.length ? (
              <Box className="transport-empty-state">No hay lineas con trazabilidad para los filtros indicados.</Box>
            ) : (
              <Stack gap={1.5}>
                {rows.map((row) => (
                  <TraceabilityLineCard key={row.line_id} row={row} />
                ))}
              </Stack>
            )}
            <TablePagination
              component="div"
              count={table.meta.total || 0}
              page={table.page}
              rowsPerPage={table.rowsPerPage}
              onPageChange={table.handlePageChange}
              onRowsPerPageChange={table.handleRowsPerPageChange}
              rowsPerPageOptions={TABLE_ROWS_PER_PAGE_OPTIONS}
              labelRowsPerPage="Filas por página:"
              labelDisplayedRows={({ from, to, count: total }) => `${from}-${to} de ${total}`}
            />
          </Paper>
        </AnimatedSection>
      </Box>
    </AnimatedPage>
  );
}

function TraceabilityLineCard({ row }) {
  const events = [...(Array.isArray(row.events) ? row.events : [])].sort(compareTraceabilityEvents);
  return (
    <Accordion className="transport-order-accordion" disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
        <Box className="transport-section-header transport-section-header-spread" sx={{ width: '100%' }}>
          <Box>
            <Typography className="transport-split-title">
              Pedido {row.source_document_no} / Linea {row.source_line_no}
            </Typography>
            <Typography className="transport-section-subtitle">
              {row.cliente || '-'} - {row.producto || '-'}
            </Typography>
          </Box>
          <Stack direction="row" gap={1} sx={{ flexWrap: 'wrap' }}>
            <Chip size="small" label={`Original ${formatQuantity(row.palets_originales)}`} />
            <Chip size="small" label={`Cargados ${formatQuantity(row.palets_cargados)}`} />
            <Chip size="small" label={`Pendientes ${formatQuantity(row.palets_pendientes_origen)}`} />
          </Stack>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Etapa</TableCell>
                <TableCell>Fecha</TableCell>
                <TableCell>Registrado</TableCell>
                <TableCell>Orden/Viaje</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Ubicacion</TableCell>
                <TableCell>Matricula</TableCell>
                <TableCell align="right">Palets</TableCell>
                <TableCell align="right">Pendientes</TableCell>
                <TableCell>Usuario</TableCell>
                <TableCell>Observaciones</TableCell>
                <TableCell>Adjuntos</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.map((event, index) => (
                <TableRow key={`${event.event_type}-${event.load_order_line_id || row.line_id}-${index}`}>
                  <TableCell>{traceabilityStageLabel(event)}</TableCell>
                  <TableCell>{formatShortDate(event.event_at)}</TableCell>
                  <TableCell>{formatTraceabilityDate(event.registered_at || event.event_at)}</TableCell>
                  <TableCell>{event.load_order_number || event.source_document_no || '-'}</TableCell>
                  <TableCell>
                    {traceabilityEventStatusLabel(event) ? (
                      <Chip
                        size="small"
                        label={traceabilityEventStatusLabel(event)}
                        className={traceabilityEventStatusChipClass(event)}
                      />
                    ) : (
                      '-'
                    )}
                    {event.event_type === 'ORDER_LINE' && event.current_load_order_status ? (
                      <Typography variant="caption" component="div">
                        Actual: {loadOrderStatusLabel(event.current_load_order_status)}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>{event.location || '-'}</TableCell>
                  <TableCell>{event.matricula || '-'}</TableCell>
                  <TableCell align="right">{event.cantidad_pales == null ? '-' : formatQuantity(event.cantidad_pales)}</TableCell>
                  <TableCell align="right">{event.pending_pallets == null ? '-' : formatQuantity(event.pending_pallets)}</TableCell>
                  <TableCell>{event.user_name || '-'}</TableCell>
                  <TableCell>{event.notes || '-'}</TableCell>
                  <TableCell>{traceabilityAttachmentLabel(event.attachments)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>
    </Accordion>
  );
}

function LoadOrderStatusesPage({ page }) {
  const table = useMasterRecordsTableState('displayOrder');
  const tableApiParams = table.apiParams;
  const setTableMeta = table.setMeta;
  const [statuses, setStatuses] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStatus, setEditingStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  const loadStatuses = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getLogisticsLoadOrderStatuses(tableApiParams);
      setTableMeta(response?.meta);
      setStatuses(responseList(response));
    } catch (error) {
      setStatuses([]);
      setMessage(apiMessage(error, 'No se han podido cargar los estados.'));
    } finally {
      setLoading(false);
    }
  }, [setTableMeta, tableApiParams]);

  useEffect(() => {
    loadStatuses();
  }, [loadStatuses]);

  const handleSaveStatus = async (payload) => {
    setLoading(true);
    setMessage(null);
    try {
      if (editingStatus?.code) {
        await updateLogisticsLoadOrderStatus(editingStatus.code, {
          label: payload.label,
          description: payload.description,
          displayOrder: payload.displayOrder,
          active: payload.active,
        });
        setMessage({ severity: 'success', text: 'Estado actualizado.' });
      } else {
        await createLogisticsLoadOrderStatus(payload);
        setMessage({ severity: 'success', text: 'Estado creado.' });
      }
      setDialogOpen(false);
      setEditingStatus(null);
      await loadStatuses();
    } catch (error) {
      setMessage(apiMessage(error, 'No se ha podido guardar el estado.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatedPage>
      <Box className="suite-page">
        <PageHeader
          page={page}
          actions={
            <Button
              className="suite-primary-button"
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={() => {
                setEditingStatus(null);
                setDialogOpen(true);
              }}
            >
              Nuevo estado
            </Button>
          }
        />

        {message ? (
          <Alert severity={message.severity} onClose={() => setMessage(null)}>
            {message.text}
          </Alert>
        ) : null}
        {loading ? <LinearProgress /> : null}

        <Paper className="dashboard-section-card" elevation={0}>
          <Box className="transport-section-header transport-section-header-spread">
            <Typography className="section-title">Estados de orden de carga</Typography>
            <MasterActivityFilter table={table} />
          </Box>
          <StaticRowsTable
            columns={[
              { field: 'code', label: 'Codigo' },
              { field: 'label', label: 'Nombre' },
              { field: 'description', label: 'Descripcion' },
              { field: 'displayOrder', label: 'Orden' },
              { field: 'active', label: 'Activo' },
            ]}
            rows={statuses}
            meta={table.meta}
            table={table}
            onEdit={(status) => {
              setEditingStatus(status);
              setDialogOpen(true);
            }}
          />
        </Paper>

        <LoadOrderStatusDialog
          open={dialogOpen}
          status={editingStatus}
          loading={loading}
          onClose={() => {
            setDialogOpen(false);
            setEditingStatus(null);
          }}
          onSubmit={handleSaveStatus}
        />
      </Box>
    </AnimatedPage>
  );
}

function LoadTripDialog({ open, selectedRows, masterOptions, submitting, onClose, onSubmit }) {
  const [form, setForm] = useState(() => defaultLoadTripForm());
  const [lines, setLines] = useState([]);
  const [error, setError] = useState('');
  const transportistas = useMemo(
    () => masterOptions.transportistas || [],
    [masterOptions.transportistas]
  );
  const selectedTransportista = useMemo(
    () => transportistas.find((transportista) => getId(transportista) === form.transportistaId) || null,
    [form.transportistaId, transportistas]
  );
  const matriculas = getMatriculas(selectedTransportista);

  useEffect(() => {
    if (!open) return;
    setForm(defaultLoadTripForm());
    setLines(
      selectedRows.map((row) => ({
        key: getLineKey(row),
        row,
        cantidadPales: formatFormQuantity(linePendingPallets(row)),
      }))
    );
    setError('');
  }, [open, selectedRows]);

  const handleFormChange = (field, value) => {
    setForm((current) => {
      const next = { ...current, [field]: value };
      if (field === 'transportistaId') {
        next.matriculaId = '';
        next.matricula = '';
      }
      return next;
    });
  };

  const handleLinePalletsChange = (key, value) => {
    setLines((current) =>
      current.map((line) => (line.key === key ? { ...line, cantidadPales: value } : line))
    );
  };

  const handleSubmit = () => {
    const validationError = validateLoadTripForm(form, lines);
    if (validationError) {
      setError(validationError);
      return;
    }
    onSubmit(buildLoadTripPayload(form, lines));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle className="transport-dialog-title">
        <Box>
          <Typography variant="h5">Viaje de carga</Typography>
          <Typography>Ordenes de origen: {[...new Set(selectedRows.map((row) => row.load_order_number))].join(', ') || '-'}.</Typography>
        </Box>
      </DialogTitle>
      <DialogContent className="transport-dialog-content">
        {error ? (
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        ) : null}

        <Box className="transport-form-grid">
          <TextField
            label="Fecha carga"
            type="date"
            value={form.loadDate}
            onChange={(event) => handleFormChange('loadDate', event.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <FormControl>
            <InputLabel>Transportista</InputLabel>
            <Select
              value={form.transportistaId}
              label="Transportista"
              onChange={(event) => handleFormChange('transportistaId', event.target.value)}
            >
              <MenuItem value="">Sin asignar</MenuItem>
              {transportistas.map((transportista) => (
                <MenuItem key={getId(transportista)} value={getId(transportista)}>
                  {transportista.name || transportista.code}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl disabled={!form.transportistaId || matriculas.length === 0}>
            <InputLabel>Matricula</InputLabel>
            <Select
              value={form.matriculaId}
              label="Matricula"
              onChange={(event) => {
                const matriculaId = event.target.value;
                const selected = matriculas.find((item) => getId(item) === matriculaId);
                handleFormChange('matriculaId', matriculaId);
                handleFormChange('matricula', selected?.matricula || '');
              }}
            >
              <MenuItem value="">Sin asignar / manual</MenuItem>
              {matriculas.map((matricula) => (
                <MenuItem key={getId(matricula)} value={getId(matricula)}>
                  {matricula.matricula}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Matricula manual (opcional)"
            value={form.matricula}
            onChange={(event) => handleFormChange('matricula', event.target.value)}
          />
          <TextField
            label="Notas"
            value={form.notes}
            onChange={(event) => handleFormChange('notes', event.target.value)}
            multiline
            minRows={2}
            className="transport-form-notes"
          />
        </Box>

        <Box className="transport-split-list">
          <Box className="transport-section-header transport-section-header-spread">
            <Typography className="section-title">Palets a recoger</Typography>
            <Chip className="suite-status-chip" label={`${lines.length} lineas`} />
          </Box>
          {!lines.length ? (
            <Box className="transport-empty-state">Selecciona lineas con palets disponibles.</Box>
          ) : (
            lines.map((line) => {
              const total = lineTotalPallets(line.row);
              const assigned = decimalValue(line.row.cantidad_pales_asignados);
              const pending = linePendingPallets(line.row);
              const requested = decimalValue(line.cantidadPales);
              const exceeds = requested > pending;
              return (
                <Box
                  className={`transport-split-card ${exceeds ? 'transport-split-card-error' : ''}`}
                  key={line.key}
                >
                  <Box className="transport-split-card-header">
                    <Box>
                      <Typography className="transport-split-title">{lineLabel(line.row)}</Typography>
                      <Typography className="transport-section-subtitle">
                        Orden {line.row.load_order_number} · Pedido {line.row.origin_pedido_no || line.row.source_document_no || '-'}
                      </Typography>
                    </Box>
                    <Stack direction="row" gap={1} sx={{ flexWrap: 'wrap' }}>
                      <Chip size="small" label={`Orden ${formatQuantity(total)}`} />
                      <Chip size="small" label={`Asignados ${formatQuantity(assigned)}`} />
                      <Chip size="small" label={`Pendientes ${formatQuantity(pending)}`} />
                    </Stack>
                  </Box>
                  <TextField
                    label="Palets a recoger"
                    type="number"
                    value={line.cantidadPales}
                    onChange={(event) => handleLinePalletsChange(line.key, event.target.value)}
                    inputProps={{ min: 0, step: 0.01, max: pending }}
                    error={exceeds}
                    helperText={exceeds ? 'Supera los palets pendientes' : ''}
                  />
                </Box>
              );
            })
          )}
        </Box>
      </DialogContent>
      <DialogActions className="transport-dialog-actions">
        <Button onClick={onClose} disabled={submitting}>Cancelar</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting || !lines.length}>
          Crear viaje
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function UnloadTripDialog({ open, selectedRows, masterOptions, submitting, onClose, onSubmit }) {
  const [form, setForm] = useState(() => defaultUnloadTripForm());
  const [lines, setLines] = useState([]);
  const [error, setError] = useState('');
  const transportistas = useMemo(
    () => masterOptions.transportistas || [],
    [masterOptions.transportistas]
  );
  const customerDestinations = useMemo(
    () => flattenCustomerDestinations(masterOptions.clientes || []),
    [masterOptions.clientes]
  );
  const selectedTransportista = useMemo(
    () => transportistas.find((transportista) => getId(transportista) === form.transportistaId) || null,
    [form.transportistaId, transportistas]
  );
  const matriculas = getMatriculas(selectedTransportista);

  useEffect(() => {
    if (!open) return;
    const firstRow = selectedRows[0] || {};
    setForm(
      defaultUnloadTripForm({
        sameAsLoad: true,
        loadDate: toInputDateValue(firstRow.load_date) || toInputDate(new Date()),
        transportistaId: stringOrEmpty(firstRow.transportista_id),
        matriculaId: stringOrEmpty(firstRow.transportista_matricula_id),
        matricula: firstRow.matricula || '',
        customerDestinationId: stringOrEmpty(firstRow.customer_destination_id),
      })
    );
    setLines(
      selectedRows.map((row) => ({
        key: getLineKey(row),
        row,
        cantidadPales: formatFormQuantity(lineUnloadPendingPallets(row)),
      }))
    );
    setError('');
  }, [open, selectedRows]);

  const handleFormChange = (field, value) => {
    setForm((current) => {
      const next = { ...current, [field]: value };
      if (field === 'transportistaId') {
        next.matriculaId = '';
        next.matricula = '';
      }
      if (field === 'sameAsLoad' && value) {
        const firstRow = selectedRows[0] || {};
        next.loadDate = toInputDateValue(firstRow.load_date) || current.loadDate;
        next.transportistaId = stringOrEmpty(firstRow.transportista_id);
        next.matriculaId = stringOrEmpty(firstRow.transportista_matricula_id);
        next.matricula = firstRow.matricula || '';
        next.customerDestinationId = stringOrEmpty(firstRow.customer_destination_id);
      }
      return next;
    });
  };

  const handleLinePalletsChange = (key, value) => {
    setLines((current) =>
      current.map((line) => (line.key === key ? { ...line, cantidadPales: value } : line))
    );
  };

  const handleSubmit = () => {
    const validationError = validateUnloadTripForm(form, lines);
    if (validationError) {
      setError(validationError);
      return;
    }
    onSubmit(buildUnloadTripPayload(form, lines));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle className="transport-dialog-title">
        <Box>
          <Typography variant="h5">Viaje de descarga</Typography>
          <Typography>Asigna los palets que entrega una matricula de descarga.</Typography>
        </Box>
      </DialogTitle>
      <DialogContent className="transport-dialog-content">
        {error ? (
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        ) : null}

        <Box className="transport-form-grid">
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(form.sameAsLoad)}
                onChange={(event) => handleFormChange('sameAsLoad', event.target.checked)}
              />
            }
            label="Descarga con la misma matricula de carga"
          />
          <TextField
            label="Fecha descarga"
            type="date"
            value={form.loadDate}
            onChange={(event) => handleFormChange('loadDate', event.target.value)}
            disabled={form.sameAsLoad}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <FormControl disabled={form.sameAsLoad}>
            <InputLabel>Transportista</InputLabel>
            <Select
              value={form.transportistaId}
              label="Transportista"
              onChange={(event) => handleFormChange('transportistaId', event.target.value)}
            >
              <MenuItem value="">Sin asignar</MenuItem>
              {transportistas.map((transportista) => (
                <MenuItem key={getId(transportista)} value={getId(transportista)}>
                  {transportista.name || transportista.code}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl disabled={form.sameAsLoad || !form.transportistaId || matriculas.length === 0}>
            <InputLabel>Matricula</InputLabel>
            <Select
              value={form.matriculaId}
              label="Matricula"
              onChange={(event) => {
                const matriculaId = event.target.value;
                const selected = matriculas.find((item) => getId(item) === matriculaId);
                handleFormChange('matriculaId', matriculaId);
                handleFormChange('matricula', selected?.matricula || '');
              }}
            >
              <MenuItem value="">Sin asignar / manual</MenuItem>
              {matriculas.map((matricula) => (
                <MenuItem key={getId(matricula)} value={getId(matricula)}>
                  {matricula.matricula}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Matricula manual (opcional)"
            value={form.matricula}
            disabled={form.sameAsLoad}
            onChange={(event) => handleFormChange('matricula', event.target.value)}
          />
          <FormControl disabled={form.sameAsLoad || customerDestinations.length === 0}>
            <InputLabel>Destino descarga</InputLabel>
            <Select
              value={form.customerDestinationId}
              label="Destino descarga"
              onChange={(event) => handleFormChange('customerDestinationId', event.target.value)}
            >
              <MenuItem value="">Sin destino</MenuItem>
              {customerDestinations.map((destination) => (
                <MenuItem key={destinationOptionKey(destination)} value={getId(destination)}>
                  {destinationOptionLabel(destination)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Notas"
            value={form.notes}
            onChange={(event) => handleFormChange('notes', event.target.value)}
            multiline
            minRows={2}
            className="transport-form-notes"
          />
        </Box>

        <Box className="transport-split-list">
          <Box className="transport-section-header transport-section-header-spread">
            <Typography className="section-title">Palets a descargar</Typography>
            <Chip className="suite-status-chip" label={`${lines.length} lineas`} />
          </Box>
          {!lines.length ? (
            <Box className="transport-empty-state">Selecciona lineas con palets pendientes de descarga.</Box>
          ) : (
            lines.map((line) => {
              const loaded = decimalValue(line.row.cantidad_pales_cargados);
              const assigned = decimalValue(line.row.cantidad_pales_descarga_asignados);
              const pending = lineUnloadPendingPallets(line.row);
              const requested = decimalValue(line.cantidadPales);
              const exceeds = requested > pending;
              return (
                <Box
                  className={`transport-split-card ${exceeds ? 'transport-split-card-error' : ''}`}
                  key={line.key}
                >
                  <Box className="transport-split-card-header">
                    <Box>
                      <Typography className="transport-split-title">{lineLabel(line.row)}</Typography>
                      <Typography className="transport-section-subtitle">
                        Etapa origen {line.row.load_order_number || '-'} ({line.row.stage_label || 'Carga'}) - Matricula {line.row.matricula || '-'}
                        {' · Origen de la linea: '}{line.row.source_line_origin || '-'}
                      </Typography>
                    </Box>
                    <Stack direction="row" gap={1} sx={{ flexWrap: 'wrap' }}>
                      <Chip size="small" label={`Cargados ${formatQuantity(loaded)}`} />
                      <Chip size="small" label={`Asignados descarga ${formatQuantity(assigned)}`} />
                      <Chip size="small" label={`Pendientes ${formatQuantity(pending)}`} />
                    </Stack>
                  </Box>
                  <TextField
                    label="Palets a descargar"
                    type="number"
                    value={line.cantidadPales}
                    onChange={(event) => handleLinePalletsChange(line.key, event.target.value)}
                    inputProps={{ min: 0, step: 0.01, max: pending }}
                    error={exceeds}
                    helperText={exceeds ? 'Supera los palets pendientes de descarga' : ''}
                  />
                </Box>
              );
            })
          )}
        </Box>
      </DialogContent>
      <DialogActions className="transport-dialog-actions">
        <Button onClick={onClose} disabled={submitting}>Cancelar</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting || !lines.length}>
          Crear descarga
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function IntermediateStageDialog({ open, order, masterOptions, submitting, onClose, onSubmit }) {
  const [form, setForm] = useState(() => defaultIntermediateStageForm());
  const [lines, setLines] = useState([]);
  const [error, setError] = useState('');
  const logisticCenters = useMemo(
    () => masterOptions.logisticCenters || [],
    [masterOptions.logisticCenters]
  );

  useEffect(() => {
    if (!open || !order) return;
    setForm(defaultIntermediateStageForm({
      loadDate: toInputDateValue(order.load_date) || toInputDate(new Date()),
      matricula: order.matricula || '',
    }));
    setLines(
      (order.lines || [])
        .map((line) => {
          const pending = lineStagePendingPallets(line);
          return {
            key: String(line.id),
            row: line,
            cantidadPales: formatFormQuantity(pending),
          };
        })
        .filter((line) => decimalValue(line.cantidadPales) > 0)
    );
    setError('');
  }, [open, order]);

  const selectedStage = INTERMEDIATE_STAGE_OPTIONS.find((option) => option.value === form.stageType);

  const handleFormChange = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleLinePalletsChange = (key, value) => {
    setLines((current) =>
      current.map((line) => (line.key === key ? { ...line, cantidadPales: value } : line))
    );
  };

  const handleSubmit = () => {
    const validationError = validateIntermediateStageForm(form, lines);
    if (validationError) {
      setError(validationError);
      return;
    }
    onSubmit(buildIntermediateStagePayload(order, form, lines));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle className="transport-dialog-title">
        <Box>
          <Typography variant="h5">Añadir etapa</Typography>
          <Typography>
            Registra tránsito, aduanas, centro logístico o cambio de matrícula desde el viaje seleccionado.
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent className="transport-dialog-content">
        {error ? (
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        ) : null}

        <Box className="transport-form-grid">
          <FormControl>
            <InputLabel>Tipo de etapa</InputLabel>
            <Select
              value={form.stageType}
              label="Tipo de etapa"
              onChange={(event) => handleFormChange('stageType', event.target.value)}
            >
              {INTERMEDIATE_STAGE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Fecha etapa"
            type="date"
            value={form.loadDate}
            onChange={(event) => handleFormChange('loadDate', event.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label="Matricula"
            value={form.matricula}
            onChange={(event) => handleFormChange('matricula', event.target.value)}
          />
          {form.stageType === 'LOGISTICS_CENTER' ? (
            <FormControl disabled={!logisticCenters.length}>
              <InputLabel>Centro logístico</InputLabel>
              <Select
                value={form.logisticCenterId}
                label="Centro logístico"
                onChange={(event) => handleFormChange('logisticCenterId', event.target.value)}
              >
                <MenuItem value="">Seleccionar centro</MenuItem>
                {logisticCenters.map((center) => (
                  <MenuItem key={getId(center)} value={getId(center)}>
                    {center.name || center.code}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <TextField
              label={form.stageType === 'VEHICLE_CHANGE' ? 'Ubicación cambio' : 'Ubicación'}
              value={form.locationName}
              onChange={(event) => handleFormChange('locationName', event.target.value)}
            />
          )}
          <TextField
            label="Observaciones"
            value={form.notes}
            onChange={(event) => handleFormChange('notes', event.target.value)}
            multiline
            minRows={2}
            className="transport-form-notes"
          />
        </Box>

        <Box className="transport-split-list">
          <Box className="transport-section-header transport-section-header-spread">
            <Typography className="section-title">Palets afectados</Typography>
            <Chip className="suite-status-chip" label={`${lines.length} lineas`} />
          </Box>
          {!lines.length ? (
            <Box className="transport-empty-state">No hay palets disponibles para una etapa posterior.</Box>
          ) : (
            lines.map((line) => {
              const pending = lineStagePendingPallets(line.row);
              const requested = decimalValue(line.cantidadPales);
              const exceeds = requested > pending;
              return (
                <Box
                  className={`transport-split-card ${exceeds ? 'transport-split-card-error' : ''}`}
                  key={line.key}
                >
                  <Box className="transport-split-card-header">
                    <Box>
                      <Typography className="transport-split-title">{lineLabel(line.row)}</Typography>
                      <Typography className="transport-section-subtitle">
                        Pendientes desde esta etapa: {formatQuantity(pending)} palets
                        {' · Origen: '}{loadTripOriginLabel(line.row, order) || '-'}
                      </Typography>
                    </Box>
                  </Box>
                  <TextField
                    label="Palets afectados"
                    type="number"
                    value={line.cantidadPales}
                    onChange={(event) => handleLinePalletsChange(line.key, event.target.value)}
                    inputProps={{ min: 0, step: 0.01, max: pending }}
                    error={exceeds}
                    helperText={exceeds ? 'Supera los palets disponibles en esta etapa' : ''}
                  />
                </Box>
              );
            })
          )}
        </Box>
      </DialogContent>
      <DialogActions className="transport-dialog-actions">
        <Button onClick={onClose} disabled={submitting}>Cancelar</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting || !lines.length}>
          Crear {selectedStage?.label || 'etapa'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ImportPage({ page }) {
  const table = useRemoteTableState('fecha_carga', 'asc');
  const tableApiParams = table.apiParams;
  const setTableMeta = table.setMeta;
  const [dateRangeInput, setDateRangeInput] = useState(defaultDateRange());
  const dateRange = useDebouncedValue(dateRangeInput, DATE_FILTER_DEBOUNCE_MS);
  const [filterCustomers, setFilterCustomers] = useState([]);
  const [rows, setRows] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState(readStoredPendingImportColumns);
  const [selected, setSelected] = useState([]);
  const [selectedRowsByKey, setSelectedRowsByKey] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const loadFilterCustomers = useCallback(async () => {
    try {
      const response = await getLogisticsClientes({
        page: 1,
        limit: 500,
        active: true,
        sortBy: 'name',
        sortDirection: 'asc',
      });
      setFilterCustomers(responseList(response));
    } catch (error) {
      setMessage(apiMessage(error, 'No se han podido cargar los clientes para filtrar.'));
    }
  }, []);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getLogisticsPendingImports({
        documentType: 'PEDIDO',
        fechaDesde: dateRange.fechaDesde,
        fechaHasta: dateRange.fechaHasta,
        codCliente: selectedCustomerCodesParam(dateRange.codClientes),
        destino: selectedDestinationFiltersParam(dateRange.destinos),
        ...tableApiParams,
      });
      setTableMeta(response?.meta);
      setRows(responseList(response).filter((row) => row.document_type === 'PEDIDO'));
      setSelected([]);
      setSelectedRowsByKey({});
    } catch (error) {
      setRows([]);
      setMessage(apiMessage(error, 'No se han podido cargar pendientes.'));
    } finally {
      setLoading(false);
    }
  }, [
    dateRange.codClientes,
    dateRange.destinos,
    dateRange.fechaDesde,
    dateRange.fechaHasta,
    tableApiParams,
    setTableMeta,
  ]);

  useEffect(() => {
    loadFilterCustomers();
  }, [loadFilterCustomers]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const selectedKeys = useMemo(() => new Set(selected), [selected]);
  const columnOptions = useMemo(
    () => pendingImportColumnOptions(rows, selectedColumns),
    [rows, selectedColumns]
  );
  const visibleColumns = useMemo(
    () => selectedColumnsToDefinitions(selectedColumns, columnOptions),
    [columnOptions, selectedColumns]
  );

  const handleColumnsChange = (nextColumns) => {
    const fields = nextColumns.map((column) => column.field).filter(Boolean);
    const safeFields = fields.length
      ? fields
      : DEFAULT_PENDING_IMPORT_COLUMNS.map((column) => column.field);
    setSelectedColumns(safeFields);
    writeStoredPendingImportColumns(safeFields);
  };

  const payload = useMemo(() => {
    const pedidos = new Set();
    Object.values(selectedRowsByKey).forEach((row) => {
      if (row.document_type === 'PEDIDO') {
        pedidos.add(row.source_document_no);
      }
    });
    return {
      ...(pedidos.size ? { pedidos: [...pedidos] } : {}),
    };
  }, [selectedRowsByKey]);

  const handleToggle = (row) => {
    if (row.document_type !== 'PEDIDO') return;
    const key = importKey(row);
    setSelected((current) => {
      if (current.includes(key)) {
        setSelectedRowsByKey((selectedRows) => {
          const next = { ...selectedRows };
          delete next[key];
          return next;
        });
        return current.filter((value) => value !== key);
      }

      setSelectedRowsByKey((selectedRows) => ({
        ...selectedRows,
        [key]: row,
      }));
      return [...current, key];
    });
  };

  const handleSelectAllFiltered = async () => {
    setLoading(true);
    setMessage(null);
    try {
      const allRows = await loadAllPendingImports({
        documentType: 'PEDIDO',
        fechaDesde: dateRange.fechaDesde,
        fechaHasta: dateRange.fechaHasta,
        codCliente: selectedCustomerCodesParam(dateRange.codClientes),
        destino: selectedDestinationFiltersParam(dateRange.destinos),
        sortBy: table.orderBy,
        sortDirection: table.order,
      });
      const nextRowsByKey = Object.fromEntries(
        allRows.filter((row) => row.document_type === 'PEDIDO').map((row) => [importKey(row), row])
      );
      setSelected(Object.keys(nextRowsByKey));
      setSelectedRowsByKey(nextRowsByKey);
      setMessage({
        severity: 'success',
        text: `${Object.keys(nextRowsByKey).length} lineas filtradas seleccionadas.`,
      });
    } catch (error) {
      setMessage(apiMessage(error, 'No se ha podido seleccionar todo lo filtrado.'));
    } finally {
      setLoading(false);
    }
  };

  const handleClearSelection = () => {
    setSelected([]);
    setSelectedRowsByKey({});
  };

  const handleImport = async () => {
    if (!payload.pedidos?.length) return;
    setLoading(true);
    setMessage(null);
    try {
      await importLogisticsDocuments({ ...payload, notifyChanges: true });
      setMessage({ severity: 'success', text: 'Pedidos importados.' });
      await loadRows();
    } catch (error) {
      setMessage(apiMessage(error, 'No se han podido importar pedidos.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatedPage>
      <Box className="suite-page transport-page import-dynamics-page">
        <PageHeader page={page} />
        {message ? (
          <Alert severity={message.severity} onClose={() => setMessage(null)}>
            {message.text}
          </Alert>
        ) : null}
        {loading ? <LinearProgress /> : null}
        <PendingImportsPanel
          rows={rows}
          meta={table.meta}
          table={table}
          customerOptions={filterCustomers}
          dateRange={dateRangeInput}
          selectedKeys={selectedKeys}
          actionLoading={loading}
          onDateRangeChange={setDateRangeInput}
          onRefresh={loadRows}
          onToggle={handleToggle}
          onSelectAllFiltered={handleSelectAllFiltered}
          onClearSelection={handleClearSelection}
          onImportSelected={handleImport}
          columnOptions={columnOptions}
          visibleColumns={visibleColumns}
          onColumnsChange={handleColumnsChange}
        />
      </Box>
    </AnimatedPage>
  );
}

function LoadOrderStatusDialog({
  open,
  status,
  loading,
  onClose,
  onSubmit,
}) {
  const [form, setForm] = useState(() => loadOrderStatusForm(status));
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setForm(loadOrderStatusForm(status));
      setError('');
    }
  }, [open, status]);

  const handleChange = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = () => {
    if (!form.code.trim() || !form.label.trim()) {
      setError('Codigo y nombre son obligatorios.');
      return;
    }
    onSubmit({
      code: form.code.trim().toUpperCase(),
      label: form.label.trim(),
      description: form.description.trim() || undefined,
      displayOrder: Number(form.displayOrder || 0),
      active: form.active,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ className: 'master-dialog-paper' }}
    >
      <DialogTitle className="master-dialog-title">
        {status?.code ? 'Editar estado' : 'Nuevo estado'}
      </DialogTitle>
      <DialogContent className="master-dialog-content">
        <Stack className="load-order-status-form">
          {error ? <Alert severity="warning">{error}</Alert> : null}
          <TextField
            label="Codigo"
            value={form.code}
            required
            disabled={Boolean(status?.code)}
            onChange={(event) => handleChange('code', event.target.value)}
          />
          <TextField
            label="Nombre"
            value={form.label}
            required
            onChange={(event) => handleChange('label', event.target.value)}
          />
          <TextField
            label="Descripcion"
            value={form.description}
            multiline
            minRows={2}
            onChange={(event) => handleChange('description', event.target.value)}
          />
          <TextField
            label="Orden"
            type="number"
            value={form.displayOrder}
            onChange={(event) => handleChange('displayOrder', event.target.value)}
          />
          <FormControlLabel
            className="master-dialog-checkbox"
            control={
              <Checkbox
                checked={form.active}
                onChange={(event) => handleChange('active', event.target.checked)}
              />
            }
            label="Activo"
          />
        </Stack>
      </DialogContent>
      <DialogActions className="master-dialog-actions">
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          className="suite-primary-button"
          variant="contained"
          disabled={loading}
          onClick={handleSubmit}
        >
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function TransportResourcesPage({ page }) {
  const transportistasTable = useMasterRecordsTableState();
  const tariffsTable = useRemoteTableState('name', 'asc');
  const transportistasApiParams = transportistasTable.apiParams;
  const setTransportistasMeta = transportistasTable.setMeta;
  const tariffsApiParams = tariffsTable.apiParams;
  const setTariffsMeta = tariffsTable.setMeta;
  const transportistaConfig = useMemo(() => getTransportistaConfig(), []);
  const transportistasTableColumns = useMemo(() => getTransportistasTableColumns(), []);
  const tariffsTableColumns = useMemo(() => getTariffsTableColumns(), []);
  const transportistasColumns = useMasterTableColumns('transportistas', transportistasTableColumns);
  const tariffsColumns = useMasterTableColumns('tarifas', tariffsTableColumns);
  const [state, setState] = useState({
    transportistas: [],
    tariffs: [],
    loading: true,
    error: null,
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTransportista, setEditingTransportista] = useState(null);
  const [message, setMessage] = useState(null);
  const [syncingTransportistas, setSyncingTransportistas] = useState(false);

  const loadTransportResources = useCallback(async () => {
    setState((current) => ({ ...current, loading: true }));
    Promise.all([
      getLogisticsTransportistas({
        ...transportistasApiParams,
      }),
      getLogisticsTransportTariffs({
        ...tariffsApiParams,
        active: true,
      }),
    ])
      .then(([transportistasResponse, tariffsResponse]) => {
        setTransportistasMeta(transportistasResponse?.meta);
        setTariffsMeta(tariffsResponse?.meta);
        setState({
          transportistas: responseList(transportistasResponse),
          tariffs: responseList(tariffsResponse),
          loading: false,
          error: null,
        });
      })
      .catch((error) => {
        setState((current) => ({
          ...current,
          loading: false,
          error: apiMessage(error, 'No se han podido cargar transportes.'),
        }));
      });
  }, [
    tariffsApiParams,
    setTariffsMeta,
    setTransportistasMeta,
    transportistasApiParams,
  ]);

  useEffect(() => {
    loadTransportResources();
  }, [loadTransportResources]);

  const handleCreateTransportista = () => {
    setEditingTransportista(null);
    setDialogOpen(true);
  };

  const handleEditTransportista = (row) => {
    setEditingTransportista(row);
    setDialogOpen(true);
  };

  const handleSubmitTransportista = async (payload) => {
    setState((current) => ({ ...current, loading: true }));
    setMessage(null);
    try {
      if (editingTransportista?.id) {
        await updateLogisticsTransportista(editingTransportista.id, payload);
        setMessage({ severity: 'success', text: 'Transportista actualizado.' });
      } else {
        await createLogisticsTransportista(payload);
        setMessage({ severity: 'success', text: 'Transportista creado.' });
      }
      setDialogOpen(false);
      setEditingTransportista(null);
      await loadTransportResources();
    } catch (error) {
      setState((current) => ({ ...current, loading: false }));
      setMessage(apiMessage(error, 'No se ha podido guardar el transportista.'));
    }
  };

  const handleSyncTransportistas = async () => {
    setSyncingTransportistas(true);
    setMessage(null);
    try {
      const result = await syncLogisticsBusinessCentralMasterData('transportistas');
      await loadTransportResources();
      setMessage({
        severity: result?.status === 'PARTIAL' ? 'warning' : 'success',
        text: `Sincronizacion finalizada: ${result?.recordsCreated || 0} creados y ${result?.recordsUpdated || 0} actualizados.`,
      });
    } catch (error) {
      setMessage(apiMessage(error, 'No se han podido sincronizar los transportistas.'));
    } finally {
      setSyncingTransportistas(false);
    }
  };

  return (
    <AnimatedPage>
      <Box className="suite-page">
        <PageHeader
          page={page}
          actions={
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<RefreshRoundedIcon />}
                onClick={handleSyncTransportistas}
                disabled={syncingTransportistas}
              >
                {syncingTransportistas ? 'Sincronizando...' : 'Sincronizar Dynamics'}
              </Button>
              <Button
                className="suite-primary-button"
                variant="contained"
                startIcon={<AddRoundedIcon />}
                onClick={handleCreateTransportista}
              >
                Nuevo transportista
              </Button>
            </Stack>
          }
        />
        {message ? (
          <Alert severity={message.severity} onClose={() => setMessage(null)}>
            {message.text}
          </Alert>
        ) : null}
        {state.loading ? <LinearProgress /> : null}
        {state.error ? <Alert severity="error">{state.error.text}</Alert> : null}
        <Box className="transport-two-column-grid">
          <Paper className="dashboard-section-card" elevation={0}>
            <Box className="transport-section-header transport-section-header-spread">
              <Typography className="section-title">Transportistas</Typography>
              <MasterActivityFilter table={transportistasTable} />
            </Box>
            <StaticRowsTable
              columns={transportistasColumns}
              rows={state.transportistas}
              meta={transportistasTable.meta}
              table={transportistasTable}
              onEdit={handleEditTransportista}
            />
          </Paper>

          <Paper className="dashboard-section-card" elevation={0}>
            <Typography className="section-title">Tarifas</Typography>
            <StaticRowsTable
              columns={tariffsColumns}
              rows={state.tariffs}
              meta={tariffsTable.meta}
              table={tariffsTable}
            />
          </Paper>
        </Box>
        <MasterDataDialog
          open={dialogOpen}
          config={transportistaConfig}
          row={editingTransportista}
          loading={state.loading}
          onClose={() => setDialogOpen(false)}
          onSubmit={handleSubmitTransportista}
        />
      </Box>
    </AnimatedPage>
  );
}

function MasterDataPage({ page, entity }) {
  const table = useMasterRecordsTableState();
  const tableApiParams = table.apiParams;
  const setTableMeta = table.setMeta;
  const config = useMemo(() => getMasterConfig(entity), [entity]);
  const visibleColumns = useMasterTableColumns(entity, config.columns);
  const [state, setState] = useState({ rows: [], loading: true, error: null });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [message, setMessage] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const loadRows = useCallback(async () => {
    setState((current) => ({ ...current, loading: true }));
    try {
      const response = await config.loader({
        ...tableApiParams,
      });
      setTableMeta(response?.meta);
      setState({ rows: responseList(response), loading: false, error: null });
    } catch (error) {
      setState({
        rows: [],
        loading: false,
        error: apiMessage(error, `No se han podido cargar ${page.label}.`),
      });
    }
  }, [config, page.label, setTableMeta, tableApiParams]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const handleCreate = () => {
    setEditingRow(null);
    setDialogOpen(true);
  };

  const handleEdit = (row) => {
    setEditingRow(row);
    setDialogOpen(true);
  };

  const handleSubmit = async (payload) => {
    setState((current) => ({ ...current, loading: true }));
    setMessage(null);
    try {
      if (editingRow?.id) {
        await config.updater(editingRow.id, payload);
        setMessage({ severity: 'success', text: 'Registro actualizado.' });
      } else {
        await config.creator(payload);
        setMessage({ severity: 'success', text: 'Registro creado.' });
      }
      setDialogOpen(false);
      setEditingRow(null);
      await loadRows();
    } catch (error) {
      setState((current) => ({ ...current, loading: false }));
      setMessage(apiMessage(error, 'No se ha podido guardar el registro.'));
    }
  };

  const handleSync = async () => {
    if (!config.syncGroup) {
      return;
    }
    setSyncing(true);
    setMessage(null);
    try {
      const result = await syncLogisticsBusinessCentralMasterData(config.syncGroup);
      await loadRows();
      setMessage({
        severity: result?.status === 'PARTIAL' ? 'warning' : 'success',
        text: `Sincronizacion finalizada: ${result?.recordsCreated || 0} creados y ${result?.recordsUpdated || 0} actualizados.`,
      });
    } catch (error) {
      setMessage(apiMessage(error, `No se han podido sincronizar ${config.title.toLowerCase()}.`));
    } finally {
      setSyncing(false);
    }
  };

  return (
    <AnimatedPage>
      <Box className="suite-page">
        <PageHeader
          page={page}
          actions={
            <Stack direction="row" spacing={1}>
              {config.syncGroup ? (
                <Button
                  variant="outlined"
                  startIcon={<RefreshRoundedIcon />}
                  onClick={handleSync}
                  disabled={syncing}
                >
                  {syncing ? 'Sincronizando...' : 'Sincronizar Dynamics'}
                </Button>
              ) : null}
              <Button
                className="suite-primary-button"
                variant="contained"
                startIcon={<AddRoundedIcon />}
                onClick={handleCreate}
              >
                Nuevo
              </Button>
            </Stack>
          }
        />
        {message ? (
          <Alert severity={message.severity} onClose={() => setMessage(null)}>
            {message.text}
          </Alert>
        ) : null}
        {state.loading ? <LinearProgress /> : null}
        {state.error ? <Alert severity="error">{state.error.text}</Alert> : null}
        <Paper className="dashboard-section-card" elevation={0}>
          <Box className="transport-section-header transport-section-header-spread">
            <Typography className="section-title">{config.title}</Typography>
            <Stack direction="row" gap={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
              <MasterActivityFilter table={table} />
              <Chip className="suite-status-chip" label={`${state.rows.length} registros`} />
            </Stack>
          </Box>
          <StaticRowsTable
            columns={visibleColumns}
            rows={state.rows}
            meta={table.meta}
            table={table}
            onEdit={handleEdit}
          />
        </Paper>
        <MasterDataDialog
          open={dialogOpen}
          config={config}
          row={editingRow}
          loading={state.loading}
          onClose={() => setDialogOpen(false)}
          onSubmit={handleSubmit}
        />
      </Box>
    </AnimatedPage>
  );
}

function ReportsPage({ page }) {
  const cmrTable = useRemoteTableState('code', 'asc');
  const cmrApiParams = cmrTable.apiParams;
  const setCmrMeta = cmrTable.setMeta;
  const [state, setState] = useState({
    cmrProfiles: [],
    cmrFields: { slots: [], fields: [] },
    clientes: [],
    loading: true,
    error: null,
  });
  const [cmrDialogOpen, setCmrDialogOpen] = useState(false);
  const [editingCmrProfile, setEditingCmrProfile] = useState(null);
  const [message, setMessage] = useState(null);

  const loadReports = useCallback(async () => {
    setState((current) => ({ ...current, loading: true }));
    try {
      const [
        cmrResponse,
        cmrFieldsResponse,
        clientesResponse,
      ] =
        await Promise.all([
          getLogisticsCmrProfiles({ ...cmrApiParams, active: true }),
          getLogisticsCmrFields(),
          getLogisticsClientes({ page: 1, limit: PAGE_SIZE, active: true }),
        ]);
      setCmrMeta(cmrResponse?.meta);
      setState({
        cmrProfiles: responseList(cmrResponse),
        cmrFields: cmrFieldsResponse || { slots: [], fields: [] },
        clientes: responseList(clientesResponse),
        loading: false,
        error: null,
      });
    } catch (error) {
      setState((current) => ({
        ...current,
        loading: false,
        error: apiMessage(error, 'No se han podido cargar informes.'),
      }));
    }
  }, [cmrApiParams, setCmrMeta]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const handleSaveCmrProfile = async (payload) => {
    try {
      if (editingCmrProfile?.id) {
        await updateLogisticsCmrProfile(editingCmrProfile.id, payload);
        setMessage({ severity: 'success', text: 'Configuracion CMR actualizada.' });
      } else {
        await createLogisticsCmrProfile(payload);
        setMessage({ severity: 'success', text: 'Configuracion CMR creada.' });
      }
      setCmrDialogOpen(false);
      setEditingCmrProfile(null);
      await loadReports();
    } catch (error) {
      setMessage(apiMessage(error, 'No se ha podido guardar la configuracion CMR.'));
    }
  };

  return (
    <AnimatedPage>
      <Box className="suite-page">
        <PageHeader page={page} />
        {message ? (
          <Alert severity={message.severity} onClose={() => setMessage(null)}>
            {message.text}
          </Alert>
        ) : null}
        {state.loading ? <LinearProgress /> : null}
        {state.error ? <Alert severity="error">{state.error.text}</Alert> : null}
        <Paper className="dashboard-section-card" elevation={0}>
          <Box className="transport-section-header transport-section-header-spread">
            <Box>
              <Typography className="section-title">CMR</Typography>
              <Typography className="section-subtitle">
                Parametriza por cliente o cliente + destino que dato rellena cada parte del CMR.
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={() => {
                setEditingCmrProfile(null);
                setCmrDialogOpen(true);
              }}
            >
              Nuevo CMR
            </Button>
          </Box>
          <StaticRowsTable
            columns={[
              { field: 'code', label: 'Codigo' },
              { field: 'name', label: 'Nombre' },
              {
                field: 'cliente',
                label: 'Cliente',
                render: (row) => row.cliente?.name || row.cliente?.code || '-',
              },
              {
                field: 'customerDestination',
                label: 'Destino',
                render: (row) => row.customerDestination?.name || 'Todos',
              },
              { field: 'language', label: 'Idioma' },
              { field: 'active', label: 'Activo' },
            ]}
            rows={state.cmrProfiles}
            meta={cmrTable.meta}
            table={cmrTable}
            onEdit={(row) => {
              setEditingCmrProfile(row);
              setCmrDialogOpen(true);
            }}
          />
        </Paper>
        <CmrProfileDialog
          open={cmrDialogOpen}
          profile={editingCmrProfile}
          fields={state.cmrFields.fields}
          slots={state.cmrFields.slots}
          clientes={state.clientes}
          loading={state.loading}
          onClose={() => setCmrDialogOpen(false)}
          onSubmit={handleSaveCmrProfile}
        />
      </Box>
    </AnimatedPage>
  );
}

function LoadOrderEmailSettingsPage({ page }) {
  const emailTable = useRemoteTableState('code', 'asc');
  const [state, setState] = useState({
    emailSettings: [],
    emailAccounts: [],
    notificationTemplates: [],
    clientes: [],
    cmrProfiles: [],
    loading: true,
    error: null,
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEmailSetting, setEditingEmailSetting] = useState(null);
  const [message, setMessage] = useState(null);

  const loadEmailSettings = useCallback(async () => {
    setState((current) => ({ ...current, loading: true }));
    try {
      const [
        emailSettingsResponse,
        emailAccountsResponse,
        notificationTemplatesResponse,
        clientesResponse,
        cmrProfilesResponse,
      ] = await Promise.all([
        getLogisticsLoadOrderEmailSettings(),
        getNotificationEmailAccounts(),
        getNotificationTemplates(),
        getLogisticsClientes({ page: 1, limit: PAGE_SIZE, active: true }),
        getLogisticsCmrProfiles({ page: 1, limit: PAGE_SIZE, active: true }),
      ]);
      setState({
        emailSettings: responseList(emailSettingsResponse),
        emailAccounts: responseList(emailAccountsResponse),
        notificationTemplates: responseList(notificationTemplatesResponse),
        clientes: responseList(clientesResponse),
        cmrProfiles: responseList(cmrProfilesResponse),
        loading: false,
        error: null,
      });
    } catch (error) {
      setState((current) => ({
        ...current,
        loading: false,
        error: apiMessage(error, 'No se han podido cargar los correos de carga.'),
      }));
    }
  }, []);

  useEffect(() => {
    loadEmailSettings();
  }, [loadEmailSettings]);

  const handleSaveEmailSetting = async ({ setting, template }) => {
    try {
      let templateId = setting.templateId;
      if (template) {
        const createdTemplate = await createNotificationTemplate(template);
        templateId = String(createdTemplate?.id);
      }
      const payload = { ...setting, templateId };
      if (editingEmailSetting?.id) {
        await updateLogisticsLoadOrderEmailSetting(editingEmailSetting.id, payload);
        setMessage({ severity: 'success', text: 'Plantilla de correo actualizada.' });
      } else {
        await createLogisticsLoadOrderEmailSetting(payload);
        setMessage({ severity: 'success', text: 'Plantilla de correo creada.' });
      }
      setDialogOpen(false);
      setEditingEmailSetting(null);
      await loadEmailSettings();
    } catch (error) {
      setMessage(apiMessage(error, 'No se ha podido guardar la plantilla de correo.'));
    }
  };

  return (
    <AnimatedPage>
      <Box className="suite-page">
        <PageHeader
          page={page}
          actions={
            <Button
              className="suite-primary-button"
              variant="contained"
              startIcon={<MailOutlineRoundedIcon />}
              onClick={() => {
                setEditingEmailSetting(null);
                setDialogOpen(true);
              }}
            >
              Nuevo correo
            </Button>
          }
        />
        {message ? (
          <Alert severity={message.severity} onClose={() => setMessage(null)}>
            {message.text}
          </Alert>
        ) : null}
        {state.loading ? <LinearProgress /> : null}
        {state.error ? <Alert severity="error">{state.error.text}</Alert> : null}
        <Paper className="dashboard-section-card" elevation={0}>
          <Box className="transport-section-header transport-section-header-spread">
            <Box>
              <Typography className="section-title">Correos de carga</Typography>
              <Typography className="section-subtitle">
                Define la cuenta, plantilla, cliente/destino y el informe que se adjunta al aviso.
              </Typography>
            </Box>
            <Chip
              className="suite-status-chip"
              label={`${state.emailSettings.length} plantillas`}
            />
          </Box>
          <StaticRowsTable
            columns={[
              { field: 'code', label: 'Codigo' },
              { field: 'name', label: 'Nombre' },
              {
                field: 'cliente',
                label: 'Cliente',
                render: (row) => row.cliente?.name || 'Por defecto',
              },
              {
                field: 'customer_destination',
                label: 'Destino',
                render: (row) => row.customer_destination?.name || 'Todos',
              },
              {
                field: 'email_account',
                label: 'Cuenta',
                render: (row) => row.email_account?.name || row.email_account?.code || '-',
              },
              {
                field: 'template',
                label: 'Plantilla',
                render: (row) => row.template?.name || row.template?.code || '-',
              },
              {
                field: 'report_type',
                label: 'Adjunto',
                render: (row) => emailReportTypeLabel(row.report_type),
              },
              {
                field: 'recipient_source',
                label: 'Destinatario',
                render: (row) => emailRecipientSourceLabel(row.recipient_source),
              },
              { field: 'fixed_cc', label: 'CC fijo' },
              { field: 'is_default', label: 'Defecto' },
              { field: 'active', label: 'Activo' },
            ]}
            rows={state.emailSettings}
            table={emailTable}
            onEdit={(row) => {
              setEditingEmailSetting(row);
              setDialogOpen(true);
            }}
          />
        </Paper>
        <LoadOrderEmailSettingDialog
          open={dialogOpen}
          setting={editingEmailSetting}
          clientes={state.clientes}
          emailAccounts={state.emailAccounts}
          notificationTemplates={state.notificationTemplates}
          cmrProfiles={state.cmrProfiles}
          loading={state.loading}
          onClose={() => setDialogOpen(false)}
          onSubmit={handleSaveEmailSetting}
        />
      </Box>
    </AnimatedPage>
  );
}

function CmrProfileDialog({
  open,
  profile,
  fields,
  slots,
  clientes,
  loading,
  onClose,
  onSubmit,
}) {
  const [form, setForm] = useState(() => cmrProfileForm(profile, slots));

  useEffect(() => {
    if (open) {
      setForm(cmrProfileForm(profile, slots));
    }
  }, [open, profile, slots]);

  const selectedCliente = clientes.find((cliente) => String(cliente.id) === String(form.clienteId));
  const destinos = selectedCliente?.destinos || [];

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const updateMapping = (slotCode, value) => {
    setForm((current) => ({
      ...current,
      mappings: { ...current.mappings, [slotCode]: value },
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit({
      code: form.code,
      name: form.name,
      description: form.description,
      clienteId: form.clienteId,
      customerDestinationId: form.customerDestinationId || undefined,
      language: form.language,
      mappings: form.mappings,
      active: form.active,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <Box component="form" onSubmit={handleSubmit}>
        <DialogTitle>{profile?.id ? 'Editar CMR' : 'Nuevo CMR'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5}>
            <Stack className="dialog-grid dialog-grid-two">
              <TextField
                label="Codigo"
                value={form.code}
                onChange={(event) => updateField('code', event.target.value)}
                required
                disabled={Boolean(profile?.id)}
              />
              <TextField
                label="Nombre"
                value={form.name}
                onChange={(event) => updateField('name', event.target.value)}
                required
              />
              <FormControl required>
                <InputLabel>Cliente</InputLabel>
                <Select
                  label="Cliente"
                  value={form.clienteId}
                  onChange={(event) => {
                    updateField('clienteId', event.target.value);
                    updateField('customerDestinationId', '');
                  }}
                >
                  {clientes.map((cliente) => (
                    <MenuItem key={cliente.id} value={String(cliente.id)}>
                      {cliente.name || cliente.code}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <InputLabel>Destino</InputLabel>
                <Select
                  label="Destino"
                  value={form.customerDestinationId}
                  onChange={(event) => updateField('customerDestinationId', event.target.value)}
                >
                  <MenuItem value="">Todos los destinos</MenuItem>
                  {destinos.map((destino) => (
                    <MenuItem key={destino.id} value={String(destino.id)}>
                      {destino.name || destino.code}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <InputLabel>Idioma</InputLabel>
                <Select
                  label="Idioma"
                  value={form.language}
                  onChange={(event) => updateField('language', event.target.value)}
                >
                  <MenuItem value="es">Castellano</MenuItem>
                  <MenuItem value="en">Ingles</MenuItem>
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.active}
                    onChange={(event) => updateField('active', event.target.checked)}
                  />
                }
                label="Activo"
              />
            </Stack>
            <TextField
              label="Descripcion"
              value={form.description}
              onChange={(event) => updateField('description', event.target.value)}
              multiline
              minRows={2}
            />
            <Paper variant="outlined" className="nested-editor-card">
              <Box className="transport-section-header transport-section-header-spread">
                <Box>
                  <Typography className="section-title">Campos CMR</Typography>
                  <Typography className="section-subtitle">
                    El formato es fijo; aqui eliges que campo alimenta cada apartado.
                  </Typography>
                </Box>
                <Chip className="suite-status-chip" label={`${slots.length} partes`} />
              </Box>
              <TableContainer className="responsive-table-container">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Parte</TableCell>
                      <TableCell>Seccion</TableCell>
                      <TableCell>Campo origen</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {slots.map((slot) => (
                      <TableRow key={slot.code}>
                        <TableCell>{slot.label}</TableCell>
                        <TableCell>{slot.section}</TableCell>
                        <TableCell>
                          <FormControl fullWidth size="small">
                            <Select
                              value={form.mappings[slot.code] || slot.defaultField || ''}
                              onChange={(event) => updateMapping(slot.code, event.target.value)}
                            >
                              {fields.map((field) => (
                                <MenuItem key={field.field} value={field.field}>
                                  {field.label} ({field.group})
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="contained" disabled={loading}>
            Guardar CMR
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

function LoadOrderEmailSettingDialog({
  open,
  setting,
  clientes,
  emailAccounts,
  notificationTemplates,
  cmrProfiles,
  loading,
  onClose,
  onSubmit,
}) {
  const [form, setForm] = useState(() => emailSettingForm(setting));

  useEffect(() => {
    if (open) {
      setForm(emailSettingForm(setting));
    }
  }, [open, setting]);

  const selectedCliente = clientes.find((cliente) => String(cliente.id) === String(form.clienteId));
  const destinos = selectedCliente?.destinos || [];
  const selectedReportType = form.reportType;

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const template =
      form.templateMode === 'new'
        ? {
            code: form.newTemplateCode,
            name: form.newTemplateName,
            description: form.newTemplateDescription,
            subjectTemplate: form.subjectTemplate,
            bodyTemplate: form.bodyTemplate,
            bodyFormat: form.bodyFormat,
            active: true,
          }
        : null;

    onSubmit({
      template,
      setting: {
        code: form.code,
        name: form.name,
        clienteId: form.clienteId || undefined,
        customerDestinationId: form.customerDestinationId || undefined,
        emailAccountId: form.emailAccountId,
        templateId: form.templateMode === 'existing' ? form.templateId : undefined,
        recipientSource: form.recipientSource,
        fixedCc: form.fixedCc || undefined,
        reportType: form.reportType,
        cmrProfileId: form.reportType === 'CMR_PDF' ? form.cmrProfileId || undefined : undefined,
        isDefault: form.isDefault,
        active: form.active,
        notes: form.notes,
      },
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <Box component="form" onSubmit={handleSubmit}>
        <DialogTitle>{setting?.id ? 'Editar correo de carga' : 'Nuevo correo de carga'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5}>
            <Stack className="dialog-grid dialog-grid-two">
              <TextField
                label="Codigo"
                value={form.code}
                onChange={(event) => updateField('code', event.target.value)}
                required
                disabled={Boolean(setting?.id)}
              />
              <TextField
                label="Nombre"
                value={form.name}
                onChange={(event) => updateField('name', event.target.value)}
                required
              />
              <FormControl>
                <InputLabel>Cliente</InputLabel>
                <Select
                  label="Cliente"
                  value={form.clienteId}
                  onChange={(event) => {
                    updateField('clienteId', event.target.value);
                    updateField('customerDestinationId', '');
                  }}
                >
                  <MenuItem value="">Por defecto</MenuItem>
                  {clientes.map((cliente) => (
                    <MenuItem key={cliente.id} value={String(cliente.id)}>
                      {cliente.name || cliente.code}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <InputLabel>Destino</InputLabel>
                <Select
                  label="Destino"
                  value={form.customerDestinationId}
                  onChange={(event) => updateField('customerDestinationId', event.target.value)}
                  disabled={!form.clienteId}
                >
                  <MenuItem value="">Todos los destinos</MenuItem>
                  {destinos.map((destino) => (
                    <MenuItem key={destino.id} value={String(destino.id)}>
                      {destino.name || destino.code}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl required>
                <InputLabel>Cuenta envio</InputLabel>
                <Select
                  label="Cuenta envio"
                  value={form.emailAccountId}
                  onChange={(event) => updateField('emailAccountId', event.target.value)}
                >
                  {emailAccounts.map((account) => (
                    <MenuItem key={account.id} value={String(account.id)}>
                      {account.name || account.code}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <InputLabel>Plantilla</InputLabel>
                <Select
                  label="Plantilla"
                  value={form.templateMode}
                  onChange={(event) => updateField('templateMode', event.target.value)}
                >
                  <MenuItem value="existing">Usar existente</MenuItem>
                  <MenuItem value="new">Crear nueva</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            {form.templateMode === 'existing' ? (
              <FormControl required>
                <InputLabel>Plantilla notifications</InputLabel>
                <Select
                  label="Plantilla notifications"
                  value={form.templateId}
                  onChange={(event) => updateField('templateId', event.target.value)}
                >
                  {notificationTemplates.map((template) => (
                    <MenuItem key={template.id} value={String(template.id)}>
                      {template.name || template.code}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Paper variant="outlined" className="nested-editor-card">
                <Stack spacing={2}>
                  <Stack className="dialog-grid dialog-grid-two">
                    <TextField
                      label="Codigo plantilla"
                      value={form.newTemplateCode}
                      onChange={(event) => updateField('newTemplateCode', event.target.value)}
                      required={form.templateMode === 'new'}
                    />
                    <TextField
                      label="Nombre plantilla"
                      value={form.newTemplateName}
                      onChange={(event) => updateField('newTemplateName', event.target.value)}
                      required={form.templateMode === 'new'}
                    />
                  </Stack>
                  <TextField
                    label="Asunto"
                    value={form.subjectTemplate}
                    onChange={(event) => updateField('subjectTemplate', event.target.value)}
                    required={form.templateMode === 'new'}
                    placeholder="CARGA EXPEDIENTES {Nº PEDIDO}"
                  />
                  <TextField
                    label="Cuerpo"
                    value={form.bodyTemplate}
                    onChange={(event) => updateField('bodyTemplate', event.target.value)}
                    required={form.templateMode === 'new'}
                    multiline
                    minRows={5}
                  />
                  <Stack className="dialog-grid dialog-grid-two">
                    <FormControl>
                      <InputLabel>Formato</InputLabel>
                      <Select
                        label="Formato"
                        value={form.bodyFormat}
                        onChange={(event) => updateField('bodyFormat', event.target.value)}
                      >
                        <MenuItem value="HTML">HTML</MenuItem>
                        <MenuItem value="TEXT">Texto</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      label="Descripcion plantilla"
                      value={form.newTemplateDescription}
                      onChange={(event) => updateField('newTemplateDescription', event.target.value)}
                    />
                  </Stack>
                </Stack>
              </Paper>
            )}
            <Stack className="dialog-grid dialog-grid-two">
              <FormControl>
                <InputLabel>Destinatario por defecto</InputLabel>
                <Select
                  label="Destinatario por defecto"
                  value={form.recipientSource}
                  onChange={(event) => updateField('recipientSource', event.target.value)}
                >
                  <MenuItem value="PROVEEDOR">Proveedor / almacen carga</MenuItem>
                  <MenuItem value="CLIENTE">Cliente / destino</MenuItem>
                </Select>
              </FormControl>
              <FormControl>
                <InputLabel>Informe adjunto</InputLabel>
                <Select
                  label="Informe adjunto"
                  value={form.reportType}
                  onChange={(event) => updateField('reportType', event.target.value)}
                >
                  <MenuItem value="NONE">Sin adjunto</MenuItem>
                  <MenuItem value="CMR_PDF">CMR PDF</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="CC fijo"
                value={form.fixedCc}
                onChange={(event) => updateField('fixedCc', event.target.value)}
                placeholder="correo1@dominio.com; correo2@dominio.com"
              />
              {selectedReportType === 'CMR_PDF' ? (
                <FormControl>
                  <InputLabel>Perfil CMR</InputLabel>
                  <Select
                    label="Perfil CMR"
                    value={form.cmrProfileId}
                    onChange={(event) => updateField('cmrProfileId', event.target.value)}
                  >
                    <MenuItem value="">Automatico por cliente/destino</MenuItem>
                    {cmrProfiles.map((profile) => (
                      <MenuItem key={profile.id} value={String(profile.id)}>
                        {profile.name || profile.code}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : null}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.isDefault}
                    onChange={(event) => updateField('isDefault', event.target.checked)}
                  />
                }
                label="Por defecto"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.active}
                    onChange={(event) => updateField('active', event.target.checked)}
                  />
                }
                label="Activo"
              />
            </Stack>
            <TextField
              label="Notas"
              value={form.notes}
              onChange={(event) => updateField('notes', event.target.value)}
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="contained" disabled={loading}>
            Guardar correo
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

function LogisticsConfigurationPage({ page }) {
  const profilesTable = useRemoteTableState('code', 'asc');
  const profilesApiParams = profilesTable.apiParams;
  const setProfilesMeta = profilesTable.setMeta;
  const [state, setState] = useState({
    fields: [],
    profiles: [],
    customerProfiles: [],
    clientes: [],
    loading: true,
    error: null,
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [message, setMessage] = useState(null);

  const loadViewPersonalization = useCallback(async () => {
    setState((current) => ({ ...current, loading: true }));
    try {
      const [
        fieldsResponse,
        profilesResponse,
        clientesResponse,
        customerProfilesResponse,
      ] = await Promise.all([
        getLogisticsViewFields(),
        getLogisticsViewProfiles(profilesApiParams),
        getLogisticsClientes({ page: 1, limit: PAGE_SIZE, active: true }),
        getLogisticsViewCustomers({ page: 1, limit: PAGE_SIZE }),
      ]);
      setProfilesMeta(profilesResponse?.meta);
      setState({
        fields: responseList(fieldsResponse),
        profiles: responseList(profilesResponse),
        customerProfiles: responseList(customerProfilesResponse),
        clientes: responseList(clientesResponse),
        loading: false,
        error: null,
      });
    } catch (error) {
      setState((current) => ({
        ...current,
        loading: false,
        error: apiMessage(error, 'No se ha podido cargar la personalizacion.'),
      }));
    }
  }, [profilesApiParams, setProfilesMeta]);

  useEffect(() => {
    loadViewPersonalization();
  }, [loadViewPersonalization]);

  const handleCreateProfile = async (payload) => {
    setState((current) => ({ ...current, loading: true }));
    setMessage(null);
    try {
      let profileId = editingProfile?.id ? String(editingProfile.id) : '';
      if (editingProfile?.id) {
        await updateLogisticsViewProfile(editingProfile.id, payload.profile);
        setMessage({ severity: 'success', text: 'Vista actualizada.' });
      } else {
        const created = await createLogisticsViewProfile(payload.profile);
        profileId = created?.id ? String(created.id) : '';
        setMessage({ severity: 'success', text: 'Vista creada.' });
      }
      if (profileId && payload.profile.code?.trim().toUpperCase() !== GENERAL_VIEW_CUSTOMER_CODE) {
        await setLogisticsViewProfileCustomers(profileId, {
          codClientes: payload.codClientes || [],
        });
      }
      setDialogOpen(false);
      setEditingProfile(null);
      await loadViewPersonalization();
    } catch (error) {
      setState((current) => ({ ...current, loading: false }));
      setMessage(apiMessage(error, 'No se ha podido guardar la vista.'));
    }
  };

  return (
    <AnimatedPage>
      <Box className="suite-page">
        <PageHeader
          page={page}
          actions={
            <Button
              className="suite-primary-button"
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={() => {
                setEditingProfile(null);
                setDialogOpen(true);
              }}
            >
              Nueva vista
            </Button>
          }
        />
        {message ? (
          <Alert severity={message.severity} onClose={() => setMessage(null)}>
            {message.text}
          </Alert>
        ) : null}
        {state.loading ? <LinearProgress /> : null}
        {state.error ? <Alert severity="error">{state.error.text}</Alert> : null}
        <Paper className="dashboard-section-card" elevation={0}>
          <Typography className="section-title">Perfiles de vista</Typography>
          <StaticRowsTable
            columns={[
              { field: 'code', label: 'Codigo' },
              { field: 'name', label: 'Nombre' },
              { field: 'documentType', label: 'Tipo' },
              {
                field: 'fields',
                label: 'Campos',
                render: (row) => summarizeList(row.fields, 'field'),
              },
              {
                field: 'clientes',
                label: 'Clientes',
                render: (row) => viewProfileCustomersSummary(row, state.customerProfiles),
              },
              { field: 'active', label: 'Activo' },
            ]}
            rows={state.profiles}
            meta={profilesTable.meta}
            table={profilesTable}
            onEdit={(profile) => {
              setEditingProfile(profile);
              setDialogOpen(true);
            }}
          />
        </Paper>
        <ViewProfileDialog
          open={dialogOpen}
          profile={editingProfile}
          fields={state.fields}
          clientes={state.clientes}
          customerProfiles={state.customerProfiles}
          loading={state.loading}
          onClose={() => setDialogOpen(false)}
          onSubmit={handleCreateProfile}
        />
      </Box>
    </AnimatedPage>
  );
}

function AssignedLinesCustomizationPage({ page }) {
  const [lineFields, setLineFields] = useState([]);
  const [selectedFields, setSelectedFields] = useState(readStoredLoadOrderAssignedLineFields);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    let active = true;
    getLogisticsViewFields()
      .then((response) => {
        if (active) setLineFields(responseList(response));
      })
      .catch((error) => {
        if (active) setMessage({ severity: 'error', text: apiMessage(error, 'No se han podido cargar los campos.') });
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, []);

  const options = useMemo(() => [
    ...LOAD_ORDER_HEADER_FIELD_OPTIONS,
    ...lineFields.map((field) => ({
      field: `line.${field.field}`,
      label: field.label || humanizeFieldName(field.field),
      section: 'Linea de pedido',
    })),
  ], [lineFields]);

  const toggleField = (field) => {
    setSelectedFields((current) =>
      current.includes(field)
        ? current.length > 1 ? current.filter((item) => item !== field) : current
        : [...current, field]
    );
  };

  const moveField = (field, direction) => {
    setSelectedFields((current) => {
      const index = current.indexOf(field);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= current.length) return current;
      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const selectedOptions = selectedFields.map((field) =>
    options.find((option) => option.field === field) || {
      field,
      label: loadOrderAssignedLineFieldLabel(field),
      section: 'Campo anterior',
    }
  );

  return (
    <AnimatedPage>
      <PageHeader
        page={page}
        title="Personalización Líneas asignadas"
        description="Configura las columnas informativas que se muestran al crear o editar una orden de carga."
        actions={
          <Button
            className="suite-primary-button"
            variant="contained"
            onClick={() => {
              writeStoredLoadOrderAssignedLineFields(selectedFields);
              setMessage({ severity: 'success', text: 'Personalización guardada.' });
            }}
          >
            Guardar
          </Button>
        }
      />
      <AnimatedSection>
        <Paper className="suite-content-card">
          {message ? <Alert severity={message.severity} onClose={() => setMessage(null)}>{message.text}</Alert> : null}
          {loading ? <LinearProgress /> : (
            <Box className="assigned-lines-customization-grid">
              <Box>
                <Typography className="section-title">Campos disponibles</Typography>
                {['Cabecera orden', 'Linea de pedido'].map((section) => (
                  <Box className="assigned-lines-field-section" key={section}>
                    <Typography className="transport-section-subtitle">{section}</Typography>
                    {options.filter((option) => option.section === section).map((option) => (
                      <FormControlLabel
                        key={option.field}
                        control={<Checkbox checked={selectedFields.includes(option.field)} onChange={() => toggleField(option.field)} />}
                        label={option.label}
                      />
                    ))}
                  </Box>
                ))}
              </Box>
              <Box>
                <Typography className="section-title">Orden de visualización</Typography>
                <Box className="pending-import-column-order-list">
                  {selectedOptions.map((option, index) => (
                    <Box className="pending-import-column-order-row" key={option.field}>
                      <Box><Typography className="pending-import-column-order-title">{option.label}</Typography><Typography className="transport-section-subtitle">{option.section}</Typography></Box>
                      <Stack direction="row" gap={0.5}>
                        <IconButton size="small" title="Subir" disabled={index === 0} onClick={() => moveField(option.field, -1)}><KeyboardArrowUpRoundedIcon fontSize="small" /></IconButton>
                        <IconButton size="small" title="Bajar" disabled={index === selectedOptions.length - 1} onClick={() => moveField(option.field, 1)}><KeyboardArrowDownRoundedIcon fontSize="small" /></IconButton>
                      </Stack>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          )}
        </Paper>
      </AnimatedSection>
    </AnimatedPage>
  );
}

function MasterTablesCustomizationPage({ page }) {
  const [selectedEntity, setSelectedEntity] = useState(MASTER_TABLE_ENTITIES[0].entity);
  const [selectedColumns, setSelectedColumns] = useState(readStoredMasterTableColumns);
  const [message, setMessage] = useState(null);
  const columnOptions = useMemo(() => getMasterTableColumnOptions(), []);
  const currentOptions = columnOptions[selectedEntity] || [];
  const currentSelected = selectedColumns[selectedEntity] || currentOptions.map((column) => column.field);
  const selectedOptionRows = currentSelected
    .map((field) => currentOptions.find((column) => column.field === field))
    .filter(Boolean);

  const toggleColumn = (field) => {
    setSelectedColumns((current) => {
      const options = columnOptions[selectedEntity] || [];
      const fallback = options.map((column) => column.field);
      const selected = current[selectedEntity] || fallback;
      const nextSelected = selected.includes(field)
        ? selected.length > 1
          ? selected.filter((item) => item !== field)
          : selected
        : [...selected, field];

      return { ...current, [selectedEntity]: nextSelected };
    });
  };

  const moveColumn = (field, direction) => {
    setSelectedColumns((current) => {
      const options = columnOptions[selectedEntity] || [];
      const fallback = options.map((column) => column.field);
      const selected = current[selectedEntity] || fallback;
      const index = selected.indexOf(field);
      const target = index + direction;

      if (index < 0 || target < 0 || target >= selected.length) {
        return current;
      }

      const nextSelected = [...selected];
      [nextSelected[index], nextSelected[target]] = [nextSelected[target], nextSelected[index]];
      return { ...current, [selectedEntity]: nextSelected };
    });
  };

  const resetCurrentEntity = () => {
    setSelectedColumns((current) => ({
      ...current,
      [selectedEntity]: currentOptions.map((column) => column.field),
    }));
  };

  const saveColumns = () => {
    writeStoredMasterTableColumns(selectedColumns);
    setMessage({ severity: 'success', text: 'Personalizacion guardada.' });
  };

  return (
    <AnimatedPage>
      <PageHeader
        page={page}
        title="Personalizacion Maestros"
        description="Configura las columnas visibles en las tablas de datos maestros."
        actions={
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={resetCurrentEntity}>
              Restaurar tabla
            </Button>
            <Button
              className="suite-primary-button"
              variant="contained"
              onClick={saveColumns}
            >
              Guardar
            </Button>
          </Stack>
        }
      />
      <AnimatedSection>
        <Paper className="suite-content-card">
          {message ? (
            <Alert severity={message.severity} onClose={() => setMessage(null)}>
              {message.text}
            </Alert>
          ) : null}
          <Stack className="master-dialog-form">
            <FormControl>
              <InputLabel>Tabla</InputLabel>
              <Select
                label="Tabla"
                value={selectedEntity}
                onChange={(event) => setSelectedEntity(event.target.value)}
              >
                {MASTER_TABLE_ENTITIES.map((item) => (
                  <MenuItem key={item.entity} value={item.entity}>
                    {item.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box className="assigned-lines-customization-grid">
              <Box>
                <Typography className="section-title">Campos disponibles</Typography>
                <Box className="assigned-lines-field-section">
                  {currentOptions.map((column) => (
                    <FormControlLabel
                      key={column.field}
                      control={
                        <Checkbox
                          checked={currentSelected.includes(column.field)}
                          onChange={() => toggleColumn(column.field)}
                        />
                      }
                      label={column.label}
                    />
                  ))}
                </Box>
              </Box>
              <Box>
                <Typography className="section-title">Orden de visualizacion</Typography>
                <Box className="pending-import-column-order-list">
                  {selectedOptionRows.map((column, index) => (
                    <Box className="pending-import-column-order-row" key={column.field}>
                      <Box>
                        <Typography className="pending-import-column-order-title">
                          {column.label}
                        </Typography>
                        <Typography className="transport-section-subtitle">
                          {column.field}
                        </Typography>
                      </Box>
                      <Stack direction="row" gap={0.5}>
                        <IconButton
                          size="small"
                          title="Subir"
                          disabled={index === 0}
                          onClick={() => moveColumn(column.field, -1)}
                        >
                          <KeyboardArrowUpRoundedIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          title="Bajar"
                          disabled={index === selectedOptionRows.length - 1}
                          onClick={() => moveColumn(column.field, 1)}
                        >
                          <KeyboardArrowDownRoundedIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Stack>
        </Paper>
      </AnimatedSection>
    </AnimatedPage>
  );
}

export function AdminHomePage() {
  return (
    <DashboardPage
      eyebrow="Panel administrador"
      title="Centro de plataforma"
      description="Acceso rapido a integraciones y auditoria con la cabecera de aplicacion SUITE_HOME."
      cards={[
        {
          title: 'Aplicacion',
          value: 'SUITE_HOME',
          detail: 'Codigo usado por los endpoints de plataforma.',
        },
        {
          title: 'Logistica',
          value: 'LOGISTICS',
          detail: 'Codigo usado por los endpoints operativos.',
        },
        {
          title: 'API',
          value: 'v1',
          detail: 'Contrato preparado para cookies y cabecera de aplicacion.',
        },
      ]}
      actionLabel="Abrir integraciones"
      actionTo="/admin/integraciones"
      statusLabel="Administrador"
    >
      <QuickLinks title="Modulos de plataforma" links={platformPages} />
    </DashboardPage>
  );
}

function PendingImportsPanel({
  rows,
  meta,
  table,
  customerOptions = [],
  dateRange,
  selectedKeys,
  actionLoading,
  onDateRangeChange,
  onRefresh,
  onToggle,
  onSelectAllFiltered,
  onClearSelection,
  onImportSelected,
  columnOptions,
  visibleColumns,
  onColumnsChange,
}) {
  const [columnsDialogOpen, setColumnsDialogOpen] = useState(false);
  const destinationOptions = useMemo(
    () => flattenCustomerDestinations(customerOptions, dateRange.codClientes),
    [customerOptions, dateRange.codClientes]
  );

  return (
    <AnimatedSection>
      <Paper className="dashboard-section-card pending-imports-card" elevation={0}>
        <Box className="transport-section-header transport-section-header-spread">
          <Box>
            <Typography className="section-title">Pendiente de importar</Typography>
            <Typography className="transport-section-subtitle">
              Pedidos disponibles en Dynamics.
            </Typography>
          </Box>
          <Chip
            className="transport-count-chip transport-count-chip-pending"
            label={`${rows.length} lineas`}
          />
        </Box>

        <Box className="transport-filter-bar">
          <Autocomplete
            multiple
            size="small"
            options={customerOptions}
            value={selectedCustomerOptions(customerOptions, dateRange.codClientes)}
            getOptionLabel={customerOptionLabel}
            isOptionEqualToValue={(option, value) =>
              customerOptionCode(option) === customerOptionCode(value)
            }
            onChange={(_, value) =>
              onDateRangeChange((current) => ({
                ...current,
                codClientes: value.map(customerOptionCode).filter(Boolean),
              }))
            }
            renderValue={(value, getItemProps) =>
              value.map((option, index) => (
                <AutocompleteChip
                  key={customerOptionCode(option)}
                  props={getItemProps({ index })}
                  label={customerOptionLabel(option)}
                />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label="Clientes" placeholder="Seleccionar clientes" />
            )}
            sx={{ minWidth: { xs: '100%', md: 340 }, flex: '1 1 340px' }}
          />
          <Autocomplete
            multiple
            size="small"
            options={destinationOptions}
            value={selectedDestinationOptions(destinationOptions, dateRange.destinos)}
            getOptionLabel={destinationOptionLabel}
            isOptionEqualToValue={(option, value) =>
              destinationOptionKey(option) === destinationOptionKey(value)
            }
            onChange={(_, value) =>
              onDateRangeChange((current) => ({
                ...current,
                destinos: value.map(destinationOptionFilterValue).filter(Boolean),
              }))
            }
            renderValue={(value, getItemProps) =>
              value.map((option, index) => (
                <AutocompleteChip
                  key={destinationOptionKey(option)}
                  props={getItemProps({ index })}
                  label={destinationOptionLabel(option)}
                />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label="Destinos" placeholder="Seleccionar destinos" />
            )}
            sx={{ minWidth: { xs: '100%', md: 340 }, flex: '1 1 340px' }}
          />
          <Button
            size="small"
            variant={isFechaDateRangePreset(dateRange, 'today') ? 'contained' : 'outlined'}
            onClick={() =>
              onDateRangeChange((current) => applyFechaDateRangePreset(current, 'today'))
            }
          >
            Hoy
          </Button>
          <Button
            size="small"
            variant={isFechaDateRangePreset(dateRange, 'week') ? 'contained' : 'outlined'}
            onClick={() =>
              onDateRangeChange((current) => applyFechaDateRangePreset(current, 'week'))
            }
          >
            Semana
          </Button>
          <TextField
            label="Desde"
            size="small"
            type="date"
            value={dateRange.fechaDesde}
            onChange={(event) =>
              onDateRangeChange((current) => ({
                ...current,
                fechaDesde: event.target.value,
              }))
            }
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label="Hasta"
            size="small"
            type="date"
            value={dateRange.fechaHasta}
            onChange={(event) =>
              onDateRangeChange((current) => ({
                ...current,
                fechaHasta: event.target.value,
              }))
            }
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <Button
            variant="outlined"
            startIcon={<RefreshRoundedIcon />}
            disabled={actionLoading}
            onClick={onRefresh}
          >
            Actualizar
          </Button>
          <Button
            variant="outlined"
            startIcon={<SelectAllRoundedIcon />}
            disabled={actionLoading || (meta?.total ?? rows.length) === 0}
            onClick={onSelectAllFiltered}
          >
            Seleccionar filtrado
          </Button>
          <Button
            variant="text"
            disabled={actionLoading || selectedKeys.size === 0}
            onClick={onClearSelection}
          >
            Limpiar seleccion
          </Button>
          <Button
            className="suite-primary-button"
            variant="contained"
            startIcon={<FileDownloadRoundedIcon />}
            disabled={selectedKeys.size === 0 || actionLoading}
            onClick={onImportSelected}
          >
            Importar seleccionados
          </Button>
        </Box>

        <Box className="pending-import-columns-bar">
          <Autocomplete
            multiple
            size="small"
            options={columnOptions}
            value={visibleColumns}
            getOptionLabel={(option) => option.label || option.field}
            isOptionEqualToValue={(option, value) => option.field === value.field}
            onChange={(_, value) => onColumnsChange(value)}
            renderValue={(value, getItemProps) =>
              value.map((option, index) => (
                <AutocompleteChip
                  key={option.field}
                  props={getItemProps({ index })}
                  label={option.label || option.field}
                />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label="Campos tabla" placeholder="Seleccionar campos" />
            )}
            sx={{ minWidth: 0, flex: '1 1 420px' }}
          />
          <Button
            variant="outlined"
            startIcon={<TuneRoundedIcon />}
            disabled={visibleColumns.length <= 1}
            onClick={() => setColumnsDialogOpen(true)}
          >
            Ordenar columnas
          </Button>
          <Button
            variant="text"
            onClick={() => onColumnsChange(DEFAULT_PENDING_IMPORT_COLUMNS)}
          >
            Por defecto
          </Button>
        </Box>

        <Box className="pending-imports-table-shell">
          <PendingImportsTable
            rows={rows}
            meta={meta}
            table={table}
            columns={visibleColumns}
            selectedKeys={selectedKeys}
            onToggle={onToggle}
          />
        </Box>
        <PendingImportColumnsDialog
          open={columnsDialogOpen}
          columns={visibleColumns}
          onClose={() => setColumnsDialogOpen(false)}
          onChange={onColumnsChange}
        />
      </Paper>
    </AnimatedSection>
  );
}

function AutocompleteChip({ props, label }) {
  const { key, ...chipProps } = props;
  return <Chip key={key} {...chipProps} label={label} size="small" />;
}

function PendingImportColumnsDialog({ open, columns, onClose, onChange }) {
  const moveColumn = (index, direction) => {
    const next = [...columns];
    const target = index + direction;
    if (target < 0 || target >= next.length) {
      return;
    }
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  const removeColumn = (field) => {
    if (columns.length <= 1) {
      return;
    }
    onChange(columns.filter((column) => column.field !== field));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle className="master-dialog-title">Orden columnas importacion</DialogTitle>
      <DialogContent className="master-dialog-content">
        <Box className="pending-import-column-order-list">
          {columns.map((column, index) => (
            <Box className="pending-import-column-order-row" key={column.field}>
              <Box>
                <Typography className="pending-import-column-order-title">
                  {column.label || column.field}
                </Typography>
                <Typography className="transport-section-subtitle">
                  {column.field}
                </Typography>
              </Box>
              <Stack direction="row" gap={0.5}>
                <IconButton
                  size="small"
                  title="Subir"
                  disabled={index === 0}
                  onClick={() => moveColumn(index, -1)}
                >
                  <KeyboardArrowUpRoundedIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  title="Bajar"
                  disabled={index === columns.length - 1}
                  onClick={() => moveColumn(index, 1)}
                >
                  <KeyboardArrowDownRoundedIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  title="Quitar columna"
                  disabled={columns.length <= 1}
                  onClick={() => removeColumn(column.field)}
                >
                  <DeleteOutlineRoundedIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions className="master-dialog-actions">
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}

function CustomerLoadOrdersPreview({ orders, onEdit }) {
  const orderIds = useMemo(
    () => orders.map((order) => String(order.id)).filter(Boolean),
    [orders]
  );
  const [expandedOrderIds, setExpandedOrderIds] = useState(orderIds);
  const [busyOrderId, setBusyOrderId] = useState(null);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    setExpandedOrderIds(orderIds);
  }, [orderIds]);

  const handleToggleOrder = (orderId, isExpanded) => {
    setExpandedOrderIds((current) =>
      isExpanded
        ? [...new Set([...current, orderId])]
        : current.filter((id) => id !== orderId)
    );
  };

  const handleExpandAll = () => {
    setExpandedOrderIds(orderIds);
  };

  const handleCollapseAll = () => {
    setExpandedOrderIds([]);
  };

  const handlePrintCmr = async (order) => {
    setBusyOrderId(order.id);
    setMessage(null);
    try {
      await printLoadOrderCmr(order.id);
      setMessage({ severity: 'success', text: 'CMR generado.' });
    } catch (error) {
      setMessage(apiMessage(error, 'No se ha podido imprimir el CMR.'));
    } finally {
      setBusyOrderId(null);
    }
  };

  return (
    <Box className="customer-load-orders-block">
      <Box className="transport-section-header transport-section-header-spread">
        <Box>
          <Typography className="section-title">Ordenes de transporte</Typography>
          <Typography className="transport-section-subtitle">
            Cargas ya asignadas para este cliente.
          </Typography>
        </Box>
        <Stack direction="row" gap={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <Chip
            className="transport-count-chip transport-count-chip-managed"
            label={`${orders.length} ordenes`}
          />
          {orders.length ? (
            <>
              <Button
                size="small"
                variant="outlined"
                onClick={handleExpandAll}
                disabled={expandedOrderIds.length === orderIds.length}
              >
                Expandir todo
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={handleCollapseAll}
                disabled={expandedOrderIds.length === 0}
              >
                Contraer todo
              </Button>
            </>
          ) : null}
        </Stack>
      </Box>

      {message ? (
        <Alert
          severity={message.severity}
          onClose={() => setMessage(null)}
          className="transport-inline-alert"
        >
          {message.text}
        </Alert>
      ) : null}

      {!orders.length ? (
        <Box className="transport-empty-state transport-empty-state-soft">
          No hay ordenes de transporte para este cliente.
        </Box>
      ) : (
        <Box className="customer-load-order-list">
          {orders.map((order) => (
            <Accordion
              key={order.id}
              expanded={expandedOrderIds.includes(String(order.id))}
              onChange={(_, isExpanded) =>
                handleToggleOrder(String(order.id), isExpanded)
              }
              disableGutters
              elevation={0}
              square
              className="customer-load-order-card"
            >
              <AccordionSummary
                expandIcon={<ExpandMoreRoundedIcon />}
                className="customer-load-order-card-header"
              >
                <Box className="customer-load-order-card-title">
                  <LoadDocumentHeading order={order} />
                </Box>

                <Stack direction="row" gap={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                  <Typography className="transport-managed-metric">
                    {formatQuantity(order.totals?.cantidad_pales)} palets
                  </Typography>
                  <Chip
                    label={order.status_label || loadOrderStatusLabel(order.status)}
                    className="transport-status-chip transport-status-chip-info"
                    size="small"
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<FileDownloadRoundedIcon />}
                    disabled={busyOrderId === order.id}
                    onClick={(event) => {
                      event.stopPropagation();
                      handlePrintCmr(order);
                    }}
                  >
                    Imprimir CMR
                  </Button>
                  {onEdit ? (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<TuneRoundedIcon />}
                      onClick={(event) => {
                        event.stopPropagation();
                        onEdit(order);
                      }}
                    >
                      Editar
                    </Button>
                  ) : null}
                </Stack>
              </AccordionSummary>

              <AccordionDetails className="customer-load-order-card-details">
                <StaticRowsTable
                  columns={[
                    { field: 'pedido_no', label: 'Pedido' },
                    { field: 'expedicion_no', label: 'Expedicion' },
                    {
                      field: 'linea',
                      label: 'Linea',
                      render: (row) =>
                        row.source_line_no ||
                        row.source_line?.source_line_no ||
                        row.document_line_id,
                    },
                    { field: 'cantidad_pales', label: 'Palets' },
                    {
                      field: 'provider_warehouse',
                      label: 'Carga',
                      render: (row) => entityLabel(row.provider_warehouse),
                    },
                    {
                      field: 'customer_destination',
                      label: 'Descarga',
                      render: (row) => entityLabel(row.customer_destination),
                    },
                  ]}
                  rows={Array.isArray(order.lines) ? order.lines : []}
                  paginated={false}
                />
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}
    </Box>
  );
}

function LoadOrdersPanel({
  rows,
  meta,
  table,
  onRefresh,
  onEdit,
  title = 'Ordenes de carga',
  subtitle = 'Rutas, matriculas y palets asignados por linea.',
  emptyText = 'No hay ordenes de carga.',
  countLabel = 'ordenes',
  showScopeEmailAction = true,
  onAddStage,
  childStagesByParent,
  filters,
}) {
  const [expandedLoad, setExpandedLoad] = useState(rows[0]?.id || false);
  const [busyOrderId, setBusyOrderId] = useState(null);
  const [emailDialog, setEmailDialog] = useState({
    open: false,
    order: null,
    preview: null,
    attachments: [],
  });
  const [editDialog, setEditDialog] = useState(null);
  const [editMasters, setEditMasters] = useState(emptyMasters());
  const [editError, setEditError] = useState('');
  const [documentsDialog, setDocumentsDialog] = useState({
    open: false,
    order: null,
    attachments: [],
  });
  const [deliveriesDialog, setDeliveriesDialog] = useState({
    open: false,
    order: null,
    deliveries: [],
  });
  const [scopeDialog, setScopeDialog] = useState({
    open: false,
    scopeKey: '',
    retryFailed: false,
  });
  const [message, setMessage] = useState(null);
  const emailScopeOptions = useMemo(() => loadOrderEmailScopeOptions(rows), [rows]);
  const [statusOptions, setStatusOptions] = useState([]);
  const loadStatusOptions = useCallback(async () => {
    try {
      const options = [];
      let page = 1;
      let totalPages = 1;
      do {
        const response = await getLogisticsLoadOrderStatuses({ page, limit: PAGE_SIZE, active: true });
        options.push(...responseList(response));
        totalPages = response?.meta?.totalPages || 1;
        page += 1;
      } while (page <= totalPages);
      setStatusOptions(options.filter((item) => item.active !== false));
    } catch (error) {
      setStatusOptions([]);
      setMessage(apiMessage(error, 'No se han podido cargar los estados.'));
    }
  }, []);
  useEffect(() => { loadStatusOptions(); }, [loadStatusOptions]);

  const handleEditDocument = async (order) => {
    setBusyOrderId(order.id);
    setMessage(null);
    setEditError('');
    try {
      const [document, response, statuses] = await Promise.all([
        getLogisticsLoadOrderById(order.id),
        getLogisticsTransportistas({ page: 1, limit: PAGE_SIZE, active: true }),
        getLogisticsLoadOrderStatuses({ page: 1, limit: PAGE_SIZE, active: true }),
      ]);
      setEditMasters({ ...emptyMasters(), transportistas: responseList(response), loadOrderStatuses: responseList(statuses) });
      setEditDialog(document);
    } catch (error) {
      setMessage(apiMessage(error, 'No se ha podido cargar el documento para editar.'));
    } finally {
      setBusyOrderId(null);
    }
  };

  const handleSaveDocument = async (payload) => {
    if (!editDialog) return;
    setBusyOrderId(editDialog.id);
    setMessage(null);
    setEditError('');
    try {
      await updateLogisticsLoadOrder(editDialog.id, payload);
      setEditDialog(null);
      setMessage({ severity: 'success', text: 'Documento actualizado.' });
      await onRefresh?.();
    } catch (error) {
      setEditError(apiMessage(error, 'No se ha podido actualizar el documento.').text);
    } finally {
      setBusyOrderId(null);
    }
  };

  useEffect(() => {
    setExpandedLoad((current) => {
      if (rows.some((row) => row.id === current)) {
        return current;
      }
      return rows[0]?.id || false;
    });
  }, [rows]);

  const handlePreviewEmail = async (order) => {
    setBusyOrderId(order.id);
    setMessage(null);
    try {
      const [preview, attachmentsResponse] = await Promise.all([
        previewLogisticsLoadOrderEmails(order.id, {}),
        getLogisticsLoadOrderAttachments(order.id),
      ]);
      setEmailDialog({
        open: true,
        order,
        preview,
        attachments: responseList(attachmentsResponse),
      });
    } catch (error) {
      setMessage(apiMessage(error, 'No se ha podido previsualizar el correo.'));
    } finally {
      setBusyOrderId(null);
    }
  };

  const handleSendEmail = async (order) => {
    await handlePreviewEmail(order);
  };

  const refreshEmailAttachments = async () => {
    if (!emailDialog.order?.id) {
      return;
    }
    const attachmentsResponse = await getLogisticsLoadOrderAttachments(emailDialog.order.id);
    setEmailDialog((current) => ({
      ...current,
      attachments: responseList(attachmentsResponse),
    }));
  };

  const handleUploadEmailAttachment = async (file) => {
    if (!emailDialog.order?.id || !file) {
      return;
    }
    setBusyOrderId(emailDialog.order.id);
    setMessage(null);
    try {
      await createLogisticsLoadOrderAttachment(emailDialog.order.id, {
        file,
        category: 'EMAIL',
        description: 'Adjunto correo',
      });
      await refreshEmailAttachments();
    } catch (error) {
      setMessage(apiMessage(error, 'No se ha podido adjuntar el documento.'));
    } finally {
      setBusyOrderId(null);
    }
  };

  const handleShowDocuments = async (order) => {
    setBusyOrderId(order.id);
    setMessage(null);
    try {
      const attachmentsResponse = await getLogisticsLoadOrderAttachments(order.id);
      setDocumentsDialog({
        open: true,
        order,
        attachments: responseList(attachmentsResponse),
      });
    } catch (error) {
      setMessage(apiMessage(error, 'No se han podido cargar los documentos de la orden.'));
    } finally {
      setBusyOrderId(null);
    }
  };

  const refreshOrderDocuments = async () => {
    if (!documentsDialog.order?.id) {
      return;
    }
    const attachmentsResponse = await getLogisticsLoadOrderAttachments(documentsDialog.order.id);
    setDocumentsDialog((current) => ({
      ...current,
      attachments: responseList(attachmentsResponse),
    }));
  };

  const handleUploadOrderAttachment = async (file) => {
    if (!documentsDialog.order?.id || !file) {
      return;
    }
    setBusyOrderId(documentsDialog.order.id);
    setMessage(null);
    try {
      await createLogisticsLoadOrderAttachment(documentsDialog.order.id, {
        file,
        category: 'OTRO',
        description: 'Documento orden',
      });
      await refreshOrderDocuments();
    } catch (error) {
      setMessage(apiMessage(error, 'No se ha podido adjuntar el documento.'));
    } finally {
      setBusyOrderId(null);
    }
  };

  const handleShowDeliveries = async (order) => {
    setBusyOrderId(order.id);
    setMessage(null);
    try {
      const response = await getLogisticsLoadOrderEmailDeliveries(order.id);
      setDeliveriesDialog({
        open: true,
        order,
        deliveries: responseList(response),
      });
    } catch (error) {
      setMessage(apiMessage(error, 'No se han podido cargar los correos enviados.'));
    } finally {
      setBusyOrderId(null);
    }
  };

  const handleSendReviewedEmail = async (payload) => {
    if (!emailDialog.order?.id) {
      return;
    }
    setBusyOrderId(emailDialog.order.id);
    setMessage(null);
    try {
      await sendLogisticsLoadOrderEmails(emailDialog.order.id, payload);
      setEmailDialog({ open: false, order: null, preview: null, attachments: [] });
      setMessage({ severity: 'success', text: 'Correos enviados.' });
    } catch (error) {
      setMessage(apiMessage(error, 'No se han podido enviar los correos.'));
    } finally {
      setBusyOrderId(null);
    }
  };

  const handleSendScopeEmails = async () => {
    const scope = emailScopeOptions.find((item) => item.key === scopeDialog.scopeKey);
    if (!scope) {
      setMessage({ severity: 'error', text: 'Selecciona almacen y cliente.' });
      return;
    }
    setBusyOrderId('scope-email');
    setMessage(null);
    try {
      const response = await sendLogisticsLoadOrderEmailsByScope({
        providerWarehouseId: scope.providerWarehouseId,
        clienteId: scope.clienteId,
        retryFailed: scopeDialog.retryFailed,
        skipAlreadyQueuedOrSent: true,
      });
      const results = response?.results || [];
      const queued = results.filter((item) => item.status === 'QUEUED').length;
      const retried = results.filter((item) => item.status === 'RETRIED').length;
      const skipped = results.filter((item) => item.status === 'SKIPPED').length;
      setScopeDialog({ open: false, scopeKey: '', retryFailed: false });
      setMessage({
        severity: 'success',
        text: `Envio procesado. Nuevos: ${queued}. Reintentados: ${retried}. Omitidos: ${skipped}.`,
      });
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      setMessage(apiMessage(error, 'No se han podido enviar las cargas del almacen y cliente.'));
    } finally {
      setBusyOrderId(null);
    }
  };

  const handlePrintCmr = async (order) => {
    setBusyOrderId(order.id);
    setMessage(null);
    try {
      await printLoadOrderCmr(order.id);
      setMessage({ severity: 'success', text: 'CMR generado.' });
    } catch (error) {
      setMessage(apiMessage(error, 'No se ha podido imprimir el CMR.'));
    } finally {
      setBusyOrderId(null);
    }
  };

  const handleExportDocument = async (order) => {
    setBusyOrderId(order.id);
    setMessage(null);
    try {
      const document = await getLogisticsLoadOrderById(order.id);
      const exported = loadOrderDocumentExcel(document);
      await auditLogisticsExport({
        exportType: exported.exportType,
        fileName: exported.fileName,
        rowCount: exported.rows.length,
        selectedLineIds: (document.lines || []).map(getId).filter(Boolean),
        columns: exported.columns.map((column) => column.field),
      });
      await exportRowsToExcel(exported);
      setMessage({ severity: 'success', text: 'Documento exportado a Excel.' });
    } catch (error) {
      setMessage(apiMessage(error, 'No se ha podido exportar el documento a Excel.'));
    } finally {
      setBusyOrderId(null);
    }
  };

  const handleCancelOrder = async (order) => {
    if (isCancelledLoadOrder(order)) {
      return;
    }
    const confirmed = window.confirm(
      'Se cancelara el documento y sus palets quedaran disponibles para reasignarse. El historial se conservara.',
    );
    if (!confirmed) {
      return;
    }
    setBusyOrderId(order.id);
    setMessage(null);
    try {
      await cancelLogisticsLoadOrder(order.id);
      setMessage({ severity: 'success', text: 'Documento cancelado.' });
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      setMessage(apiMessage(error, 'No se ha podido cancelar el documento.'));
    } finally {
      setBusyOrderId(null);
    }
  };

  const handlePreviewEmailCmr = async () => {
    if (!emailDialog.order?.id) {
      return;
    }
    const profileId =
      emailDialog.preview?.setting?.cmrProfileId ||
      emailDialog.preview?.setting?.cmr_profile_id;
    setBusyOrderId(emailDialog.order.id);
    setMessage(null);
    try {
      await printLoadOrderCmr(emailDialog.order.id, {
        ...(profileId ? { profileId } : {}),
      });
    } catch (error) {
      setMessage(apiMessage(error, 'No se ha podido abrir el CMR del correo.'));
    } finally {
      setBusyOrderId(null);
    }
  };

  return (
    <AnimatedSection>
      <Paper className="dashboard-section-card transport-managed-card" elevation={0}>
        <Box className="transport-section-header transport-section-header-spread">
          <Box>
            <Typography className="section-title">{title}</Typography>
            <Typography className="transport-section-subtitle">
              {subtitle}
            </Typography>
          </Box>
          <Stack direction="row" gap={1} sx={{ flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 220, maxWidth: '100%' }}>
              <InputLabel id="load-list-status-label" shrink>Filtrar por estado</InputLabel>
              <Select labelId="load-list-status-label" label="Filtrar por estado" displayEmpty notched
                value={table?.statusFilter || ''} onOpen={loadStatusOptions}
                onChange={(event) => table?.handleStatusFilterChange(event.target.value)}>
                <MenuItem value="">Todos los estados</MenuItem>
                {table?.statusFilter && !statusOptions.some((item) => item.code === table.statusFilter) ? (
                  <MenuItem value={table.statusFilter} disabled>{table.statusFilter} (no disponible)</MenuItem>
                ) : null}
                {statusOptions.map((item) => <MenuItem key={item.code} value={item.code}>{item.label || item.code}</MenuItem>)}
              </Select>
            </FormControl>
            <Tooltip title="Quitar filtro de estado">
              <span>
                <IconButton size="small" aria-label="Quitar filtro de estado" disabled={!table?.statusFilter}
                  onClick={() => table?.handleStatusFilterChange('')}>
                  <FilterAltOffRoundedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Chip
              className="transport-count-chip transport-count-chip-managed"
                label={`${rows.length} ${countLabel}`}
            />
            {onRefresh ? (
              <IconButton size="small" title="Actualizar" onClick={onRefresh}>
                <RefreshRoundedIcon fontSize="small" />
              </IconButton>
            ) : null}
            {showScopeEmailAction ? (
              <Button
                size="small"
                variant="outlined"
                startIcon={<MailOutlineRoundedIcon />}
                disabled={!emailScopeOptions.length || busyOrderId === 'scope-email'}
                onClick={() =>
                  setScopeDialog({
                    open: true,
                    scopeKey: emailScopeOptions[0]?.key || '',
                    retryFailed: false,
                  })
                }
              >
                Enviar almacen/cliente
              </Button>
            ) : null}
          </Stack>
        </Box>

        {filters}
        {message ? (
          <Alert
            severity={message.severity}
            onClose={() => setMessage(null)}
            className="transport-inline-alert"
          >
            {message.text}
          </Alert>
        ) : null}

        {!rows.length ? (
          <Box className="transport-empty-state">{emptyText}</Box>
        ) : (
          <Box className="transport-accordion-list">
            {rows.map((order) => {
              const cancelled = isCancelledLoadOrder(order);
              const childStages = childStagesByParent?.get(String(order.id)) || [];
              return (
              <Accordion
                key={order.id}
                expanded={expandedLoad === order.id}
                onChange={(_, isExpanded) =>
                  setExpandedLoad(isExpanded ? order.id : false)
                }
                disableGutters
                elevation={0}
                square
                className="transport-managed-accordion"
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreRoundedIcon />}
                  className="transport-managed-summary"
                >
                  <Box className="transport-managed-summary-left">
                    <TaskAltRoundedIcon
                      className="transport-managed-check"
                      fontSize="small"
                    />
                    <Box>
                      <LoadDocumentHeading order={order} />
                    </Box>
                  </Box>

                  <Box className="transport-managed-summary-right">
                    <Typography className="transport-managed-metric">
                      {formatQuantity(order.totals?.cantidad_pales)} palets
                    </Typography>
                    <Chip
                      label={order.status_label || loadOrderStatusLabel(order.status)}
                      className="transport-status-chip transport-status-chip-info"
                      size="small"
                    />
                  </Box>
                </AccordionSummary>

                <AccordionDetails className="transport-managed-details">
                  <Box className="transport-order-actions">
                    {!cancelled ? (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<TuneRoundedIcon />}
                        disabled={busyOrderId === order.id}
                        onClick={() => onEdit ? onEdit(order) : handleEditDocument(order)}
                      >
                        Editar
                      </Button>
                    ) : null}
                    {onAddStage && !cancelled ? (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<RouteRoundedIcon />}
                        disabled={busyOrderId === order.id}
                        onClick={() => onAddStage(order)}
                      >
                        Añadir etapa
                      </Button>
                    ) : null}
                    <Button size="small" variant="outlined" startIcon={<FileDownloadRoundedIcon />}
                      aria-label={`Exportar Excel del parte ${order.number}`}
                      disabled={busyOrderId === order.id} onClick={() => handleExportDocument(order)}>
                      Exportar Excel
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<FileDownloadRoundedIcon />}
                      disabled={busyOrderId === order.id || cancelled}
                      onClick={() => handlePrintCmr(order)}
                    >
                      Imprimir CMR
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<MailOutlineRoundedIcon />}
                      disabled={busyOrderId === order.id || cancelled}
                      onClick={() => handleSendEmail(order)}
                    >
                      Enviar correo
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AttachFileRoundedIcon />}
                      disabled={busyOrderId === order.id}
                      onClick={() => handleShowDocuments(order)}
                    >
                      Documentos
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<MarkEmailReadRoundedIcon />}
                      disabled={busyOrderId === order.id}
                      onClick={() => handleShowDeliveries(order)}
                    >
                      Correos enviados
                    </Button>
                    {!cancelled ? (
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteOutlineRoundedIcon />}
                        disabled={busyOrderId === order.id}
                        onClick={() => handleCancelOrder(order)}
                      >
                        Cancelar
                      </Button>
                    ) : null}
                  </Box>

                  <StaticRowsTable
                    columns={[
                      ...(order.operation_type === 'LOAD_TRIP' ? [{ field: 'source_load_orders', label: 'Orden de carga origen', render: (row) => loadTripOriginLabel(row, order) }] : []),
                      { field: 'pedido_no', label: 'Pedido' },
                      { field: 'expedicion_no', label: 'Expedicion' },
                      {
                        field: 'linea',
                        label: 'Linea',
                        render: (row) =>
                          row.source_line_no ||
                          row.source_line?.source_line_no ||
                          row.document_line_id,
                      },
                      { field: 'cantidad_pales', label: 'Palets' },
                      {
                        field: 'provider_warehouse',
                        label: 'Carga',
                        render: (row) => entityLabel(row.provider_warehouse),
                      },
                      {
                        field: 'customer_destination',
                        label: 'Descarga',
                        render: (row) => entityLabel(row.customer_destination),
                      },
                    ]}
                    rows={Array.isArray(order.lines) ? order.lines : []}
                  />
                  {childStages.length ? (
                    <Box className="transport-split-list">
                      <Box className="transport-section-header transport-section-header-spread">
                        <Box>
                          <Typography className="section-title">Etapas intermedias</Typography>
                          <Typography className="transport-section-subtitle">
                            Movimientos derivados de este viaje. Las etapas con movimientos posteriores activos no se pueden cancelar hasta cancelar primero el movimiento posterior.
                          </Typography>
                        </Box>
                        <Chip className="suite-status-chip" label={`${childStages.length} etapas`} />
                      </Box>
                      <StaticRowsTable
                        columns={[
                          { field: 'number', label: 'Etapa' },
                          {
                            field: 'stage',
                            label: 'Tipo',
                            render: (stage) => loadOrderStageLabel(stage),
                          },
                          {
                            field: 'location',
                            label: 'Ubicacion',
                            render: (stage) => loadOrderCurrentLocation(stage),
                          },
                          {
                            field: 'load_date',
                            label: 'Fecha',
                            render: (stage) => formatShortDate(stage.load_date),
                          },
                          { field: 'matricula', label: 'Matricula' },
                          {
                            field: 'pallets',
                            label: 'Palets',
                            render: (stage) => formatQuantity(stage.totals?.cantidad_pales),
                          },
                          {
                            field: 'status',
                            label: 'Estado',
                            render: (stage) =>
                              stage.status_label || loadOrderStatusLabel(stage.status),
                          },
                        ]}
                        rows={childStages}
                        paginated={false}
                      />
                      <Stack direction="row" gap={1} sx={{ flexWrap: 'wrap' }}>
                        {childStages.map((stage) => (
                          <Button
                            key={stage.id}
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteOutlineRoundedIcon />}
                            disabled={busyOrderId === stage.id || isCancelledLoadOrder(stage)}
                            onClick={() => handleCancelOrder(stage)}
                          >
                            Cancelar etapa {stage.number}
                          </Button>
                        ))}
                      </Stack>
                    </Box>
                  ) : null}
                </AccordionDetails>
              </Accordion>
              );
            })}
          </Box>
        )}
      </Paper>
      <LoadOrderEmailReviewDialog
        open={emailDialog.open}
        order={emailDialog.order}
        preview={emailDialog.preview}
        attachments={emailDialog.attachments}
        loading={busyOrderId === emailDialog.order?.id}
        onClose={() => setEmailDialog({ open: false, order: null, preview: null, attachments: [] })}
        onUploadAttachment={handleUploadEmailAttachment}
        onDownloadAttachment={downloadAttachment}
        onPreviewCmr={handlePreviewEmailCmr}
        onSubmit={handleSendReviewedEmail}
      />
      <LoadOrderDocumentsDialog
        open={documentsDialog.open}
        order={documentsDialog.order}
        attachments={documentsDialog.attachments}
        loading={busyOrderId === documentsDialog.order?.id}
        onClose={() =>
          setDocumentsDialog({ open: false, order: null, attachments: [] })
        }
        onUploadAttachment={handleUploadOrderAttachment}
        onDownloadAttachment={downloadAttachment}
      />
      <LoadOrderDeliveriesDialog
        open={deliveriesDialog.open}
        order={deliveriesDialog.order}
        deliveries={deliveriesDialog.deliveries}
        onClose={() =>
          setDeliveriesDialog({ open: false, order: null, deliveries: [] })
        }
        onRetry={async (delivery) => {
          setBusyOrderId(delivery.id);
          setMessage(null);
          try {
            await retryLogisticsEmailDelivery(delivery.id);
            if (deliveriesDialog.order?.id) {
              const response = await getLogisticsLoadOrderEmailDeliveries(deliveriesDialog.order.id);
              setDeliveriesDialog((current) => ({
                ...current,
                deliveries: responseList(response),
              }));
            }
            setMessage({ severity: 'success', text: 'Envio reintentado.' });
          } catch (error) {
            setMessage(apiMessage(error, 'No se ha podido reintentar el envio.'));
          } finally {
            setBusyOrderId(null);
          }
        }}
      />
      <LoadOrderScopeEmailDialog
        open={scopeDialog.open}
        scope={scopeDialog}
        options={emailScopeOptions}
        loading={busyOrderId === 'scope-email'}
        onChange={setScopeDialog}
        onClose={() => setScopeDialog({ open: false, scopeKey: '', retryFailed: false })}
        onSubmit={handleSendScopeEmails}
      />
      <LoadDocumentEditDialog
        order={editDialog}
        masters={editMasters}
        error={editError}
        loading={Boolean(editDialog && busyOrderId === editDialog.id)}
        onClose={() => setEditDialog(null)}
        onSubmit={handleSaveDocument}
      />
      {table ? <TablePager table={table} count={meta?.total ?? rows.length} /> : null}
    </AnimatedSection>
  );
}

function LoadDocumentEditDialog({ order, masters, error, loading, onClose, onSubmit }) {
  const [form, setForm] = useState(defaultLoadOrderForm);
  const [quantities, setQuantities] = useState({});
  const [editableLines, setEditableLines] = useState([]);
  const [showAvailable, setShowAvailable] = useState(false);
  const [available, setAvailable] = useState({ rows: [], total: 0, page: 0, limit: DEFAULT_TABLE_PAGE_SIZE });
  const [availableSearch, setAvailableSearch] = useState('');
  const [availableLoading, setAvailableLoading] = useState(false);
  const [availableError, setAvailableError] = useState('');
  const availableRequest = useRef(0);
  useEffect(() => {
    availableRequest.current += 1;
    setShowAvailable(false);
    setAvailable({ rows: [], total: 0, page: 0, limit: DEFAULT_TABLE_PAGE_SIZE });
    setAvailableSearch('');
    setAvailableLoading(false);
    setAvailableError('');
    if (order) {
      setForm(loadOrderFormFromOrder(order));
      setQuantities(Object.fromEntries((order.lines || []).map((line) => [getId(line), formatFormQuantity(line.cantidad_pales)])));
      setEditableLines(order.lines || []);
    }
    return () => { availableRequest.current += 1; };
  }, [order]);
  if (!order) return null;

  const internalOrder = order.operation_type === 'INTERNAL_LOAD_ORDER';
  const customerCode = order.lines?.find((line) => line.source_line?.cod_cliente)?.source_line.cod_cliente
    || order.cliente?.cod_navision || order.cliente?.code;
  const loadAvailable = async (page = 0, limit = available.limit) => {
    if (!customerCode) {
      setAvailableError('No se ha podido identificar el cliente de la orden.');
      return;
    }
    const request = ++availableRequest.current;
    setAvailableLoading(true);
    setAvailableError('');
    try {
      const response = await getLogisticsViewDocuments({
        codCliente: customerCode, documentType: 'PEDIDO', operationType: 'INTERNAL_LOAD_ORDER',
        page: page + 1, limit, ...(availableSearch.trim() ? { search: availableSearch.trim() } : {}),
      });
      if (request !== availableRequest.current) return;
      const result = normalizeViewDocuments(response, customerCode);
      setAvailable({ rows: result.rows, total: Number(response?.meta?.total || 0), page, limit });
    } catch (failure) {
      if (request === availableRequest.current) setAvailableError(apiMessage(failure, 'No se han podido cargar las lineas disponibles.').text);
    } finally {
      if (request === availableRequest.current) setAvailableLoading(false);
    }
  };
  const addAvailableLine = (row) => {
    const documentLineId = getLineId(row);
    if (editableLines.some((line) => String(line.document_line_id) === documentLineId)) return;
    const id = `new:${documentLineId}`;
    const amount = decimalValue(row.cantidad_pales_pendientes ?? row.cantidad_pales);
    if (amount <= 0) return;
    setEditableLines((current) => [...current, { id, document_line_id: documentLineId,
      cantidad_pales: amount, available_pallets: amount, source_line: row, incoming_links: [], outgoing_links: [] }]);
    setQuantities((current) => ({ ...current, [id]: formatFormQuantity(amount) }));
  };
  const initial = loadOrderFormFromOrder(order);
  const fields = ['loadDate', 'transportistaId', 'matriculaId', 'matricula', 'driverCount', 'notes', 'status'];
  const changes = Object.fromEntries(fields.filter((field) => form[field] !== initial[field]).map((field) => [field, form[field]]));
  const lines = editableLines;
  const originalIds = new Set((order.lines || []).map(getId));
  const removedLines = (order.lines || []).filter((line) => !lines.some((item) => getId(item) === getId(line)));
  const originalSourceIds = new Set((order.lines || []).map((line) => String(line.document_line_id)));
  const candidateLines = available.rows.filter((row) => !originalSourceIds.has(getLineId(row))
    && !lines.some((line) => String(line.document_line_id) === getLineId(row)));
  const minimumPallets = (line) => Math.max(0.01, Math.round(activeOutgoingPallets(line) * 100) / 100);
  const invalidQuantities = !lines.length || lines.some((line) => decimalValue(quantities[getId(line)]) < minimumPallets(line)
    || (line.available_pallets !== undefined && decimalValue(quantities[getId(line)]) > line.available_pallets));
  if (lines.length !== (order.lines || []).length || lines.some((line) => !originalIds.has(getId(line))
    || decimalValue(quantities[getId(line)]) !== decimalValue(line.cantidad_pales))) {
    changes.lines = lines.map((line) => ({
      ...(originalIds.has(getId(line)) ? { id: getId(line) } : { documentLineId: String(line.document_line_id) }),
      cantidadPales: formatFormQuantity(quantities[getId(line)]),
    }));
  }
  const statuses = (masters.loadOrderStatuses || []).filter((item) => item.active !== false);
  const carriers = [...(masters.transportistas || [])];
  if (order.transportista_id && !carriers.some((carrier) => getId(carrier) === String(order.transportista_id))) {
    carriers.push(order.transportista || { id: order.transportista_id, name: `Transportista ${order.transportista_id}` });
  }
  const carrier = carriers.find((item) => getId(item) === form.transportistaId);
  const plates = [...getMatriculas(carrier)];
  if (form.matriculaId && !plates.some((plate) => getId(plate) === form.matriculaId)) {
    plates.push({ id: form.matriculaId, matricula: order.matricula || form.matriculaId });
  }
  const title = order.operation_type === 'INTERNAL_LOAD_ORDER' ? 'Editar orden de carga'
    : order.trip_type === 'UNLOAD' ? 'Editar viaje de descarga' : 'Editar viaje de carga';

  return (
    <Dialog open onClose={loading ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>{title} - {order.number}</DialogTitle>
      <DialogContent className="transport-dialog-content">
        {error ? <Alert severity="error">{error}</Alert> : null}
        <Box className="transport-form-grid">
          <TextField label="Fecha" type="date" value={form.loadDate} disabled={loading}
            onChange={(event) => setForm((current) => ({ ...current, loadDate: event.target.value }))}
            slotProps={{ inputLabel: { shrink: true } }} />
          <FormControl disabled={loading}>
            <InputLabel id="edit-document-status-label">Estado</InputLabel>
            <Select labelId="edit-document-status-label" label="Estado" value={form.status}
              onChange={(event) => setForm((current) => ({ ...current, status: event.target.value }))}>
              {!statuses.some((item) => item.code === form.status) ? (
                <MenuItem value={form.status} disabled>{order.status_label || loadOrderStatusLabel(form.status)} (no disponible)</MenuItem>
              ) : null}
              {statuses.map((item) => <MenuItem key={item.code} value={item.code}>{item.label || item.code}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl disabled={loading}>
            <InputLabel id="edit-document-carrier-label">Transportista</InputLabel>
            <Select labelId="edit-document-carrier-label" label="Transportista" value={form.transportistaId}
              onChange={(event) => setForm((current) => ({ ...current, transportistaId: event.target.value, matriculaId: '', matricula: '' }))}>
              <MenuItem value="">Sin asignar</MenuItem>
              {carriers.map((item) => <MenuItem key={getId(item)} value={getId(item)}>{item.name || item.code}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl disabled={loading || !form.transportistaId || !plates.length}>
            <InputLabel id="edit-document-plate-label">Matricula</InputLabel>
            <Select labelId="edit-document-plate-label" label="Matricula" value={form.matriculaId}
              onChange={(event) => {
                const plate = plates.find((item) => getId(item) === event.target.value);
                setForm((current) => ({ ...current, matriculaId: event.target.value, matricula: plate?.matricula || '' }));
              }}>
              <MenuItem value="">Sin asignar / manual</MenuItem>
              {plates.map((item) => <MenuItem key={getId(item)} value={getId(item)}>{item.matricula}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Matricula manual (opcional)" value={form.matricula} disabled={loading}
            onChange={(event) => setForm((current) => ({ ...current, matricula: event.target.value, matriculaId: '' }))}
            slotProps={{ htmlInput: { maxLength: 20 } }} />
          <FormControl disabled={loading}>
            <InputLabel id="edit-document-drivers-label">Chóferes</InputLabel>
            <Select labelId="edit-document-drivers-label" label="Chóferes" value={form.driverCount}
              onChange={(event) => setForm((current) => ({ ...current, driverCount: Number(event.target.value) }))}>
              <MenuItem value={1}>1 chófer</MenuItem>
              <MenuItem value={2}>Doble chófer</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Observaciones" value={form.notes} multiline minRows={2} disabled={loading}
            className="transport-form-notes" onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))} />
        </Box>
        <Box className="transport-section-header transport-section-header-spread">
          <Typography className="section-title">Lineas asignadas</Typography>
          {internalOrder ? <Button startIcon={<AddRoundedIcon />} disabled={loading || availableLoading} onClick={() => {
            setShowAvailable((current) => !current);
            if (!showAvailable) loadAvailable();
          }}>{showAvailable ? 'Ocultar lineas disponibles' : 'Añadir lineas'}</Button> : null}
        </Box>
        {internalOrder && removedLines.length > 0 ? <Alert severity="info"
          action={<Button disabled={loading} onClick={() => setEditableLines((current) => [
            ...(order.lines || []).filter((line) => !current.some((item) => getId(item) === getId(line))), ...current,
          ])}>Restaurar retiradas</Button>}>
          Los palets retirados volveran al listado disponible al guardar.
        </Alert> : null}
        {!lines.length ? <Alert severity="warning">La orden debe conservar al menos una linea.</Alert> : null}
        <StaticRowsTable rows={lines} paginated={false} columns={[
          { field: 'source_document_no', label: 'Pedido', render: (line) => line.source_line?.source_document_no || line.pedido_no || '-' },
          { field: 'source_line_no', label: 'Linea', render: (line) => line.source_line?.source_line_no || '-' },
          { field: 'producto', label: 'Producto', render: (line) => line.source_line?.des_variedad || line.manual_product_name || '-' },
          { field: 'cantidad_pales', label: 'Palets', render: (line) => (
            <TextField size="small" type="number" label={`Palets linea ${getId(line)}`} value={quantities[getId(line)] ?? ''} disabled={loading}
              onChange={(event) => setQuantities((current) => ({ ...current, [getId(line)]: event.target.value }))}
              error={decimalValue(quantities[getId(line)]) < minimumPallets(line) || (line.available_pallets !== undefined && decimalValue(quantities[getId(line)]) > line.available_pallets)}
              helperText={minimumPallets(line) > 0.01 ? `Minimo ${formatQuantity(minimumPallets(line))} palets ya trasladados` : ''}
              slotProps={{ htmlInput: { min: minimumPallets(line), max: line.available_pallets, step: 0.01 } }} sx={{ width: 210 }} />
          ) },
          ...(internalOrder ? [{ field: 'retirar', label: 'Acciones', render: (line) => (
            <Tooltip title={(line.outgoing_links?.length || line.incoming_links?.length) ? 'Tiene viajes o historial asociado; solo se pueden ajustar sus palets libres' : 'Libera los palets de esta orden al guardar'}>
              <span><Button size="small" color="error" startIcon={<DeleteOutlineRoundedIcon />}
                aria-label={`Retirar linea ${getId(line)}`} disabled={loading || Boolean(line.outgoing_links?.length || line.incoming_links?.length)}
                onClick={() => setEditableLines((current) => current.filter((item) => getId(item) !== getId(line)))}>Retirar</Button></span>
            </Tooltip>
          ) }] : []),
        ]} />
        {internalOrder && showAvailable ? <Box sx={{ mt: 2 }}>
          <Typography className="section-title">Lineas disponibles del mismo cliente</Typography>
          <Stack direction="row" gap={1} sx={{ my: 1 }}>
            <TextField size="small" label="Buscar pedido o producto" value={availableSearch} disabled={availableLoading || loading}
              onChange={(event) => setAvailableSearch(event.target.value)}
              onKeyDown={(event) => { if (event.key === 'Enter') loadAvailable(0); }} />
            <Button disabled={availableLoading || loading} onClick={() => loadAvailable(0)}>Buscar</Button>
          </Stack>
          {availableError ? <Alert severity="error">{availableError}</Alert> : null}
          {availableLoading ? <LinearProgress /> : <StaticRowsTable rows={candidateLines} paginated={false}
            emptyText="No hay otras lineas disponibles para este cliente en esta pagina." columns={[
              { field: 'source_document_no', label: 'Pedido' },
              { field: 'source_line_no', label: 'Linea' },
              { field: 'des_variedad', label: 'Producto' },
              { field: 'cantidad_pales_pendientes', label: 'Palets disponibles', render: (row) => formatQuantity(row.cantidad_pales_pendientes ?? row.cantidad_pales) },
              { field: 'add', label: 'Acciones', render: (row) => <Button size="small" disabled={loading}
                aria-label={`Añadir ${lineLabel(row)}`} onClick={() => addAvailableLine(row)}>Añadir</Button> },
            ]} />}
          <TablePagination component="div" count={available.total} page={available.page} rowsPerPage={available.limit}
            rowsPerPageOptions={TABLE_ROWS_PER_PAGE_OPTIONS} labelRowsPerPage="Filas por pagina:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            onPageChange={(_, page) => { if (!availableLoading) loadAvailable(page); }}
            onRowsPerPageChange={(event) => loadAvailable(0, Number(event.target.value))} />
        </Box> : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancelar</Button>
        <Button variant="contained" disabled={loading || availableLoading || !form.loadDate || invalidQuantities || !Object.keys(changes).length} onClick={() => onSubmit(changes)}>Guardar cambios</Button>
      </DialogActions>
    </Dialog>
  );
}

function LoadOrderScopeEmailDialog({ open, scope, options, loading, onChange, onClose, onSubmit }) {
  const selected = options.find((item) => item.key === scope.scopeKey);
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Enviar cargas por almacen y cliente</DialogTitle>
      <DialogContent className="transport-dialog-content">
        <FormControl fullWidth>
          <InputLabel>Almacen / cliente</InputLabel>
          <Select
            label="Almacen / cliente"
            value={scope.scopeKey}
            onChange={(event) =>
              onChange((current) => ({ ...current, scopeKey: event.target.value }))
            }
          >
            {options.map((option) => (
              <MenuItem key={option.key} value={option.key}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(scope.retryFailed)}
              onChange={(event) =>
                onChange((current) => ({ ...current, retryFailed: event.target.checked }))
              }
            />
          }
          label="Reintentar envios fallidos existentes"
        />
        {selected ? (
          <Alert severity="info">
            Se procesaran {selected.orderCount} ordenes del ambito seleccionado. Las ya enviadas o en cola se omitiran.
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancelar</Button>
        <Button variant="contained" onClick={onSubmit} disabled={loading || !selected}>
          Enviar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function LoadOrderEmailReviewDialog({
  open,
  order,
  preview,
  attachments,
  loading,
  onClose,
  onUploadAttachment,
  onDownloadAttachment,
  onPreviewCmr,
  onSubmit,
}) {
  const [drafts, setDrafts] = useState([]);
  const fileInputRef = useRef(null);
  const draftHasCmrAttachment = useCallback(
    (draft) =>
      preview?.setting?.reportType === 'CMR_PDF' ||
      preview?.setting?.report_type === 'CMR_PDF' ||
      (draft.attachments || []).some(
        (attachment) =>
          attachment.reportType === 'CMR_PDF' || attachment.report_type === 'CMR_PDF'
      ),
    [preview?.setting?.reportType, preview?.setting?.report_type]
  );
  const draftHasAutomaticAttachment = useCallback((draft) => {
    const reportType = preview?.setting?.reportType || preview?.setting?.report_type;
    return (reportType && reportType !== 'NONE') || (draft.attachments || []).length > 0;
  }, [preview?.setting?.reportType, preview?.setting?.report_type]);

  useEffect(() => {
    if (open) {
      setDrafts(
        responseList(preview?.drafts ? { data: preview.drafts } : preview).map((draft) => ({
          ...draft,
          recipientsToText: (draft.recipientsTo || []).join(', '),
          recipientsCcText: (draft.recipientsCc || []).join(', '),
          attachmentFileIds: [],
          includeAutomaticAttachment: draftHasAutomaticAttachment(draft),
        }))
      );
    }
  }, [draftHasAutomaticAttachment, open, preview]);

  const updateDraft = (index, field, value) => {
    setDrafts((current) =>
      current.map((draft, draftIndex) =>
        draftIndex === index ? { ...draft, [field]: value } : draft
      )
    );
  };

  const toggleAttachment = (draftIndex, fileId) => {
    setDrafts((current) =>
      current.map((draft, index) => {
        if (index !== draftIndex) {
          return draft;
        }
        const selected = new Set(draft.attachmentFileIds || []);
        if (selected.has(fileId)) {
          selected.delete(fileId);
        } else {
          selected.add(fileId);
        }
        return { ...draft, attachmentFileIds: [...selected] };
      })
    );
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit({
      settingId: preview?.setting?.id,
      drafts: drafts.map((draft) => ({
        groupKey: draft.groupKey,
        recipientsTo: splitEmails(draft.recipientsToText),
        recipientsCc: splitEmails(draft.recipientsCcText),
        subject: draft.subject,
        body: draft.body,
        attachmentFileIds: draft.attachmentFileIds || [],
        includeAutomaticAttachment: Boolean(draft.includeAutomaticAttachment),
      })),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <Box component="form" onSubmit={handleSubmit}>
        <DialogTitle>
          Revisar correo {order?.number ? `- ${order.number}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5}>
            <Box className="transport-section-header transport-section-header-spread">
              <Box>
                <Typography className="section-title">
                  {preview?.setting?.name || preview?.setting?.code || 'Plantilla aplicada'}
                </Typography>
                <Typography className="section-subtitle">
                  Revisa destinatarios, asunto, cuerpo y adjuntos antes de enviar.
                </Typography>
              </Box>
              <input
                ref={fileInputRef}
                type="file"
                hidden
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = '';
                  if (file) {
                    onUploadAttachment(file);
                  }
                }}
              />
            </Box>
            {!drafts.length ? (
              <Box className="transport-empty-state">No hay correos preparados.</Box>
            ) : (
              drafts.map((draft, index) => (
                <Accordion key={draft.groupKey} defaultExpanded disableGutters elevation={0}>
                  <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
                    <Stack direction="row" gap={1} sx={{ alignItems: 'center', minWidth: 0 }}>
                      <MailOutlineRoundedIcon fontSize="small" />
                      <Typography className="section-title">
                        {draft.providerWarehouse?.name || draft.cliente?.des_cliente || draft.groupKey}
                      </Typography>
                      <Chip
                        size="small"
                        className="suite-status-chip"
                        label={`${draft.lines?.length || 0} lineas`}
                      />
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={2}>
                      <TextField
                        label="Para"
                        value={draft.recipientsToText}
                        onChange={(event) =>
                          updateDraft(index, 'recipientsToText', event.target.value)
                        }
                        required
                        helperText="Separa varios correos con coma o punto y coma."
                      />
                      <TextField
                        label="CC"
                        value={draft.recipientsCcText}
                        onChange={(event) =>
                          updateDraft(index, 'recipientsCcText', event.target.value)
                        }
                        helperText="Opcional."
                      />
                      <TextField
                        label="Asunto"
                        value={draft.subject || ''}
                        onChange={(event) => updateDraft(index, 'subject', event.target.value)}
                        required
                      />
                      <TextField
                        label="Cuerpo"
                        value={draft.body || ''}
                        onChange={(event) => updateDraft(index, 'body', event.target.value)}
                        multiline
                        minRows={8}
                        required
                      />
                      <Box className="master-subform">
                        <Box className="master-subform-header">
                          <Box>
                            <Typography className="section-title">Documentos de la orden</Typography>
                            <Typography className="section-subtitle">
                              Revisa el CMR y marca los documentos que quieres adjuntar a este correo.
                            </Typography>
                          </Box>
                          <Stack direction="row" gap={1} sx={{ flexWrap: 'wrap' }}>
                            <Chip
                              className="suite-status-chip"
                              label={`${
                                attachments.length + (draftHasCmrAttachment(draft) ? 1 : 0)
                              } documentos`}
                            />
                            <Button
                              variant="outlined"
                              startIcon={<AddRoundedIcon />}
                              onClick={() => fileInputRef.current?.click()}
                              disabled={loading}
                            >
                              Adjuntar
                            </Button>
                          </Stack>
                        </Box>
                        {draft.attachments?.length && !draftHasCmrAttachment(draft) ? (
                          <Typography className="section-subtitle">
                            La plantilla adjuntara automaticamente: {draft.attachments
                              .map((attachment) =>
                                emailReportTypeLabel(attachment.reportType || attachment.report_type)
                              )
                              .join(', ')}
                          </Typography>
                        ) : null}
                        {!attachments.length && !draftHasCmrAttachment(draft) ? (
                          <Box className="transport-empty-state">
                            La orden aun no tiene documentos propios.
                          </Box>
                        ) : (
                          <Stack spacing={1}>
                            {draftHasCmrAttachment(draft) ? (
                              <Box className="email-attachment-row" key="cmr-pdf">
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={Boolean(draft.includeAutomaticAttachment)}
                                      onChange={(event) =>
                                        updateDraft(
                                          index,
                                          'includeAutomaticAttachment',
                                          event.target.checked
                                        )
                                      }
                                    />
                                  }
                                  label="CMR PDF"
                                />
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={onPreviewCmr}
                                  disabled={loading}
                                >
                                  Ver
                                </Button>
                              </Box>
                            ) : null}
                            {attachments.map((attachment) => {
                              const file = attachment.files || {};
                              const fileId = stringOrEmpty(file.id || attachment.file_id);
                              return (
                                <Box className="email-attachment-row" key={fileId}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={(draft.attachmentFileIds || []).includes(fileId)}
                                        onChange={() => toggleAttachment(index, fileId)}
                                      />
                                    }
                                    label={file.original_name || `Archivo ${fileId}`}
                                  />
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => onDownloadAttachment(file)}
                                  >
                                    Ver
                                  </Button>
                                </Box>
                              );
                            })}
                          </Stack>
                        )}
                      </Box>
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              ))
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="contained" disabled={loading || !drafts.length}>
            Enviar
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

function PendingLineEditDialog({ open, line, loading, onClose, onSubmit }) {
  const [form, setForm] = useState(() => pendingLineForm(line));

  useEffect(() => {
    if (open) {
      setForm(pendingLineForm(line));
    }
  }, [line, open]);

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit(cleanPayload(form));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <Box component="form" onSubmit={handleSubmit}>
        <DialogTitle>
          Editar linea pendiente {line?.source_document_no ? `- ${line.source_document_no}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5}>
            <Box className="dialog-grid">
              <TextField label="Documento" value={line?.source_document_no || ''} disabled />
              <TextField label="Linea" value={line?.source_line_no || ''} disabled />
              <TextField
                label="Pedido origen"
                value={form.originPedidoNo}
                onChange={(event) => updateField('originPedidoNo', event.target.value)}
              />
              <TextField
                label="Linea pedido origen"
                type="number"
                value={form.originPedidoLineNo}
                onChange={(event) => updateField('originPedidoLineNo', event.target.value)}
              />
            </Box>

            <Box className="dialog-grid">
              <TextField
                label="Cod. almacen"
                value={form.codAlmacen}
                onChange={(event) => updateField('codAlmacen', event.target.value)}
              />
              <TextField
                label="Cod. proveedor"
                value={form.codProveedor}
                onChange={(event) => updateField('codProveedor', event.target.value)}
              />
              <TextField
                label="Proveedor"
                value={form.desProveedor}
                onChange={(event) => updateField('desProveedor', event.target.value)}
              />
              <TextField
                label="Cod. dir. carga"
                value={form.codDirCarga}
                onChange={(event) => updateField('codDirCarga', event.target.value)}
              />
              <TextField
                label="Dir. carga"
                value={form.desDirCarga}
                onChange={(event) => updateField('desDirCarga', event.target.value)}
              />
            </Box>

            <Box className="dialog-grid">
              <TextField
                label="Cod. variedad"
                value={form.codVariedad}
                onChange={(event) => updateField('codVariedad', event.target.value)}
              />
              <TextField
                label="Variedad"
                value={form.desVariedad}
                onChange={(event) => updateField('desVariedad', event.target.value)}
              />
              <TextField
                label="Cod. confeccion"
                value={form.codConfeccion}
                onChange={(event) => updateField('codConfeccion', event.target.value)}
              />
              <TextField
                label="Confeccion"
                value={form.desConfeccion}
                onChange={(event) => updateField('desConfeccion', event.target.value)}
              />
              <TextField
                label="Cod. envase"
                value={form.codEnvase}
                onChange={(event) => updateField('codEnvase', event.target.value)}
              />
              <TextField
                label="Envase"
                value={form.desEnvase}
                onChange={(event) => updateField('desEnvase', event.target.value)}
              />
              <TextField
                label="Cod. palet"
                value={form.codPalet}
                onChange={(event) => updateField('codPalet', event.target.value)}
              />
              <TextField
                label="Palet"
                value={form.desPalet}
                onChange={(event) => updateField('desPalet', event.target.value)}
              />
            </Box>

            <Box className="dialog-grid">
              <TextField
                label="Palets"
                type="number"
                value={form.cantidadPales}
                onChange={(event) => updateField('cantidadPales', event.target.value)}
              />
              <TextField
                label="Bultos/palet"
                type="number"
                value={form.bultosPalet}
                onChange={(event) => updateField('bultosPalet', event.target.value)}
              />
              <TextField
                label="Kilos brutos"
                type="number"
                value={form.kilosBrutos}
                onChange={(event) => updateField('kilosBrutos', event.target.value)}
              />
              <TextField
                label="Kilos netos"
                type="number"
                value={form.kilosNetos}
                onChange={(event) => updateField('kilosNetos', event.target.value)}
              />
              <TextField
                label="Precio"
                type="number"
                value={form.precio}
                onChange={(event) => updateField('precio', event.target.value)}
              />
            </Box>

            <TextField
              label="Observacion"
              value={form.observacion}
              onChange={(event) => updateField('observacion', event.target.value)}
            />
            <TextField
              label="Notas logisticas"
              value={form.logisticsNotes}
              onChange={(event) => updateField('logisticsNotes', event.target.value)}
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="contained" disabled={loading}>
            Guardar linea
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

function LoadOrderDocumentsDialog({
  open,
  order,
  attachments,
  loading,
  onClose,
  onUploadAttachment,
  onDownloadAttachment,
}) {
  const fileInputRef = useRef(null);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Documentos {order?.number ? `- ${order.number}` : ''}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Box className="transport-section-header transport-section-header-spread">
            <Box>
              <Typography className="section-title">Adjuntos de la orden</Typography>
              <Typography className="section-subtitle">
                Documentos disponibles para revisar o adjuntar al correo.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<AddRoundedIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={loading}
            >
              Adjuntar documento
            </Button>
          </Box>
          <input
            ref={fileInputRef}
            type="file"
            hidden
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (file) {
                onUploadAttachment(file);
              }
            }}
          />
          {loading ? <LinearProgress className="transport-linear-progress" /> : null}
          {!attachments.length ? (
            <Box className="transport-empty-state">La orden aun no tiene documentos.</Box>
          ) : (
            <Stack spacing={1}>
              {attachments.map((attachment) => {
                const file = attachment.files || attachment.file || {};
                const fileId = stringOrEmpty(file.id || attachment.file_id);
                return (
                  <Box className="email-attachment-row" key={fileId || attachment.id}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography className="section-title">
                        {file.original_name || attachment.description || `Archivo ${fileId}`}
                      </Typography>
                      <Typography className="section-subtitle">
                        {attachment.category || 'Documento'} | {formatDateTimeValue(attachment.created_at)}
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => onDownloadAttachment(file)}
                    >
                      Ver
                    </Button>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}

function LoadOrderDeliveriesDialog({ open, order, deliveries, onClose, onRetry }) {
  const [selectedDelivery, setSelectedDelivery] = useState(null);

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
        <DialogTitle>Correos enviados {order?.number ? `- ${order.number}` : ''}</DialogTitle>
        <DialogContent dividers>
          <StaticRowsTable
            columns={emailDeliveryColumns()}
            rows={deliveries}
            paginated={false}
            onEdit={setSelectedDelivery}
            actionLabel="Ver"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cerrar</Button>
        </DialogActions>
      </Dialog>
      <EmailDeliveryDetailDialog
        open={Boolean(selectedDelivery)}
        delivery={selectedDelivery}
        onClose={() => setSelectedDelivery(null)}
        onDownloadAttachment={downloadAttachment}
        onRetry={onRetry}
      />
    </>
  );
}

function EmailDeliveryDetailDialog({ open, delivery, onClose, onDownloadAttachment, onRetry }) {
  const attachments = delivery?.attachments || [];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>Correo {delivery?.subject ? `- ${delivery.subject}` : ''}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          <Box className="master-subform">
            <Box className="master-subform-header">
              <Box>
                <Typography className="section-title">Datos del envio</Typography>
                <Typography className="section-subtitle">
                  Orden {delivery?.source_id || '-'} | {emailDeliveryStatusLabel(delivery?.status)}
                </Typography>
              </Box>
              <Chip
                className="suite-status-chip"
                label={formatDateTimeValue(delivery?.created_at)}
              />
            </Box>
            <Stack spacing={1.25}>
              <Typography>
                <strong>Para:</strong> {formatEmailList(delivery?.recipients_to)}
              </Typography>
              <Typography>
                <strong>CC:</strong> {formatEmailList(delivery?.recipients_cc)}
              </Typography>
              <Typography>
                <strong>Asunto:</strong> {delivery?.subject || '-'}
              </Typography>
              <Typography>
                <strong>Enviado:</strong> {formatDateTimeValue(delivery?.sent_at)}
              </Typography>
              {delivery?.error_message ? (
                <Alert severity="error">{delivery.error_message}</Alert>
              ) : null}
            </Stack>
          </Box>

          <Box className="master-subform">
            <Box className="master-subform-header">
              <Box>
                <Typography className="section-title">Cuerpo del correo</Typography>
                <Typography className="section-subtitle">
                  Formato {delivery?.body_format || 'TEXT'}
                </Typography>
              </Box>
            </Box>
            {String(delivery?.body_format || '').toUpperCase() === 'HTML' ? (
              <Box
                className="email-body-preview"
                dangerouslySetInnerHTML={{ __html: delivery?.body || '' }}
              />
            ) : (
              <Box component="pre" className="email-body-preview">
                {delivery?.body || '-'}
              </Box>
            )}
          </Box>

          <Box className="master-subform">
            <Box className="master-subform-header">
              <Box>
                <Typography className="section-title">Adjuntos</Typography>
                <Typography className="section-subtitle">
                  Documentos incluidos en el envio.
                </Typography>
              </Box>
              <Chip className="suite-status-chip" label={`${attachments.length} adjuntos`} />
            </Box>
            {!attachments.length ? (
              <Box className="transport-empty-state">Este correo no tiene adjuntos.</Box>
            ) : (
              <Stack spacing={1}>
                {attachments.map((attachment) => {
                  const file = attachment.file || attachment.files || {};
                  return (
                    <Box
                      className="email-attachment-row"
                      key={attachment.file_id || file.id || attachment.attachment_name}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography className="section-title">
                          {attachment.attachment_name || file.original_name || 'Adjunto'}
                        </Typography>
                        <Typography className="section-subtitle">
                          {file.mime_type || '-'} | {formatDateTimeValue(attachment.created_at)}
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => onDownloadAttachment(file)}
                        disabled={!file.public_uuid && !file.file_public_uuid}
                      >
                        Ver
                      </Button>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        {onRetry && ['FAILED', 'RETRY', 'CANCELLED'].includes(delivery?.status) ? (
          <Button
            variant="outlined"
            startIcon={<RefreshRoundedIcon />}
            onClick={() => onRetry(delivery)}
          >
            Reintentar
          </Button>
        ) : null}
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}

function LoadOrderDialog({
  open,
  loadOrder,
  onClose,
  onSubmit,
  selectedRows,
  masterOptions,
  statuses = DEFAULT_LOAD_ORDER_STATUSES,
  submitting,
}) {
  const [form, setForm] = useState(defaultLoadOrderForm());
  const [splits, setSplits] = useState([]);
  const [error, setError] = useState('');
  const [assignedLineFields] = useState(readStoredLoadOrderAssignedLineFields);

  const transportistas = useMemo(
    () => masterOptions.transportistas || [],
    [masterOptions.transportistas]
  );
  const selectedTransportista = useMemo(
    () =>
      transportistas.find((transportista) => getId(transportista) === form.transportistaId) ||
      null,
    [form.transportistaId, transportistas]
  );
  const matriculas = getMatriculas(selectedTransportista);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (loadOrder?.id) {
      setForm(loadOrderFormFromOrder(loadOrder));
      setSplits(loadOrderSplitsFromOrder(loadOrder));
      setError('');
      return;
    }

    const firstRow = selectedRows[0] || {};

    const nextForm = defaultLoadOrderForm({
      matricula: firstRow.matricula || '',
    });
    setForm(nextForm);
    setSplits(
      selectedRows.map((row) => {
        const maxPallets = row.cantidad_pales_pendientes === null || row.cantidad_pales_pendientes === undefined
          ? lineTotalPallets(row) : linePendingPallets(row);
        return {
          row,
          key: getLineKey(row),
          label: lineLabel(row),
          matricula: nextForm.matricula,
          maxPallets,
          parts: [
            {
              id: createTempId(),
              cantidadPales: maxPallets ? formatFormQuantity(maxPallets) : '',
              matricula: nextForm.matricula,
            },
          ],
        };
      })
    );
    setError('');
  }, [loadOrder, open, selectedRows]);

  const handleFieldChange = (field, value) => {
    if (field === 'matricula' || field === 'transportistaId') {
      const previousMatricula = form.matricula || '';
      const nextMatricula = field === 'transportistaId' ? '' : value;
      setSplits((currentSplits) =>
        currentSplits.map((split) =>
          !split.matricula || split.matricula === previousMatricula
            ? {
                ...split,
                matricula: nextMatricula,
                parts: split.parts.map((part) =>
                  !part.matricula || part.matricula === previousMatricula
                    ? { ...part, matricula: nextMatricula }
                    : part
                ),
              }
            : split
        )
      );
    }
    setForm((current) => {
      const next = { ...current, [field]: value };
      if (field === 'transportistaId') {
        next.matriculaId = '';
        next.matricula = '';
      }
      return next;
    });
  };

  const handleAssignedPalletsChange = (key, partId, value) => {
    setSplits((current) => current.map((split) => split.key === key
      ? { ...split, parts: split.parts.map((part) => part.id === partId ? { ...part, cantidadPales: value } : part) }
      : split));
    setError('');
  };

  const handleSubmit = async () => {
    const validationError = validateLoadOrderForm(form, splits);
    if (validationError) {
      setError(validationError);
      return;
    }

    const payload = buildLoadOrderPayload(form, splits);
    await onSubmit(payload);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{ className: 'transport-dialog-paper' }}
    >
      <DialogTitle className="transport-dialog-title">
        <Box>
          <Typography variant="h5">
            {loadOrder?.id ? `Editar orden ${loadOrder.number || ''}` : 'Orden de carga'}
          </Typography>
          <Typography>
            Revisa las lineas seleccionadas antes de generar la orden interna.
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent className="transport-dialog-content">
        {error ? (
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        ) : null}

        <Box className="transport-form-grid">
          <TextField
            label="Fecha carga"
            type="date"
            value={form.loadDate}
            onChange={(event) => handleFieldChange('loadDate', event.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <FormControl>
            <InputLabel>Estado</InputLabel>
            <Select
              value={form.status}
              label="Estado"
              onChange={(event) => handleFieldChange('status', event.target.value)}
            >
              {statuses.map((status) => (
                <MenuItem key={status.code} value={status.code}>
                  {status.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <InputLabel>Transportista</InputLabel>
            <Select
              value={form.transportistaId}
              label="Transportista"
              onChange={(event) =>
                handleFieldChange('transportistaId', event.target.value)
              }
            >
              <MenuItem value="">Sin asignar</MenuItem>
              {transportistas.map((transportista) => (
                <MenuItem key={getId(transportista)} value={getId(transportista)}>
                  {transportista.name || transportista.code}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl disabled={!form.transportistaId || matriculas.length === 0}>
            <InputLabel>Matricula</InputLabel>
            <Select
              value={form.matriculaId}
              label="Matricula"
              onChange={(event) => {
                const matriculaId = event.target.value;
                const selected = matriculas.find((item) => getId(item) === matriculaId);
                handleFieldChange('matriculaId', matriculaId);
                handleFieldChange('matricula', selected?.matricula || '');
              }}
            >
              <MenuItem value="">Sin asignar / manual</MenuItem>
              {matriculas.map((matricula) => (
                <MenuItem key={getId(matricula)} value={getId(matricula)}>
                  {matricula.matricula}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Matricula manual (opcional)"
            value={form.matricula}
            onChange={(event) => handleFieldChange('matricula', event.target.value)}
          />
          <FormControl>
            <InputLabel>Choferes</InputLabel>
            <Select
              value={form.driverCount}
              label="Choferes"
              onChange={(event) =>
                handleFieldChange('driverCount', event.target.value)
              }
            >
              <MenuItem value={1}>1 chofer</MenuItem>
              <MenuItem value={2}>2 choferes</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Precio manual"
            value={form.manualPrice}
            onChange={(event) => handleFieldChange('manualPrice', event.target.value)}
          />
          <TextField
            label="Notas"
            value={form.notes}
            onChange={(event) => handleFieldChange('notes', event.target.value)}
            multiline
            minRows={2}
            className="transport-form-notes"
          />
        </Box>

        <Box className="transport-split-list">
          <Box className="transport-section-header transport-section-header-spread">
            <Typography className="section-title">Lineas asignadas</Typography>
            <Chip className="suite-status-chip" label={`${splits.length} lineas`} />
          </Box>
          {!splits.length ? (
            <Box className="transport-empty-state">
              Selecciona lineas de pedido antes de crear la orden de carga.
            </Box>
          ) : (
            splits.map((split) => {
              const assigned = split.parts.reduce(
                (sum, part) => sum + decimalValue(part.cantidadPales),
                0
              );
              const exceeds = (!split.manual || split.maxPallets > 0) && assigned > split.maxPallets;
              return (
                <Box
                  key={split.key}
                  className={`transport-split-card ${
                    exceeds ? 'transport-split-card-error' : ''
                  }`}
                >
                  <Box className="transport-split-card-header">
                    <Box>
                      <Typography className="transport-split-title">
                        {split.label}
                      </Typography>
                      <Typography className="transport-section-subtitle">
                        {split.manual
                          ? 'Linea creada manualmente'
                          : `Pedido ${split.row.origin_pedido_no || split.row.source_document_no || '-'}`}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={
                        split.maxPallets > 0
                          ? `${formatQuantity(assigned)} / ${formatQuantity(split.maxPallets)} palets`
                          : `${formatQuantity(assigned)} palets`
                      }
                      className={
                        exceeds
                          ? 'transport-status-chip transport-status-chip-warning'
                          : 'transport-status-chip transport-status-chip-neutral'
                      }
                    />
                  </Box>
                  {!loadOrder?.id ? <Stack direction="row" gap={1} sx={{ flexWrap: 'wrap', mb: 1 }}>
                    {split.parts.map((part, index) => (
                      <TextField key={part.id} size="small" type="number"
                        label={`Palets linea ${split.label}${split.parts.length > 1 ? ` (${index + 1})` : ''}`}
                        value={part.cantidadPales} disabled={submitting}
                        onChange={(event) => handleAssignedPalletsChange(split.key, part.id, event.target.value)}
                        error={decimalValue(part.cantidadPales) < 0.01 || exceeds}
                        helperText={exceeds ? 'Supera los palets disponibles'
                          : decimalValue(part.cantidadPales) < 0.01 ? 'Indica al menos 0,01 palets'
                            : `Disponibles: ${formatQuantity(split.maxPallets)} palets`}
                        slotProps={{ htmlInput: { min: 0.01, step: 0.01, max: split.maxPallets > 0 ? split.maxPallets : undefined } }}
                        sx={{ width: 210 }} />
                    ))}
                  </Stack> : null}
                  <Box className="transport-assigned-line-summary">
                    {assignedLineFields.map((field) => (
                      <Box key={field} className="transport-assigned-line-summary-item">
                        <Typography className="transport-assigned-line-summary-label">
                          {loadOrderAssignedLineFieldLabel(field)}
                        </Typography>
                        <Typography className="transport-assigned-line-summary-value">
                          {loadOrderAssignedLineFieldValue(split, field, assigned, form, selectedTransportista)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
      </DialogContent>
      <DialogActions className="transport-dialog-actions">
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          className="suite-primary-button"
          variant="contained"
          disabled={submitting || Boolean(validateLoadOrderForm(form, splits))}
          onClick={handleSubmit}
        >
          {loadOrder?.id ? 'Guardar cambios' : 'Guardar orden'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CustomerTabs({ customers, value, onChange, searchable = false, showTabs = true, searchLabel = 'Buscar cliente', clearLabel = 'Quitar filtro de cliente', emptyText = 'No hay clientes con perfil activo.' }) {
  if (!customers.length) {
    return (
      <Box className="transport-empty-state">
        {emptyText}
      </Box>
    );
  }

  return (
    <Box>
      {searchable ? (
        <Stack direction="row" spacing={0.5} sx={{ maxWidth: 530, mb: 1, alignItems: 'center' }}>
        <Autocomplete
          size="small"
          options={customers}
          value={customers.find((item) => item.cliente?.cod_cliente === value) || null}
          getOptionLabel={(item) => [item.cliente?.des_cliente, item.cliente?.cod_cliente].filter(Boolean).join(' - ')}
          isOptionEqualToValue={(option, selected) => option.cliente?.cod_cliente === selected.cliente?.cod_cliente}
          onChange={(_, item) => onChange(item?.cliente?.cod_cliente || '')}
          disableClearable
          noOptionsText="No se han encontrado clientes"
          openText="Mostrar clientes"
          closeText="Cerrar"
          renderInput={(params) => <TextField {...params} label={searchLabel} />}
          sx={{ flex: 1, minWidth: 0 }}
        />
        <Tooltip title={clearLabel}>
          <span>
            <IconButton size="small" aria-label={clearLabel} disabled={!value} onClick={() => onChange('')}>
              <FilterAltOffRoundedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        </Stack>
      ) : null}
      {showTabs ? <Tabs
        value={value || false}
        onChange={(_, nextValue) => onChange(nextValue)}
        variant="scrollable"
        scrollButtons="auto"
        className="transport-customer-tabs"
      >
        {customers.map((item) => {
          const cliente = item.cliente ?? {};
          return (
            <Tab
              key={cliente.cod_cliente}
              value={cliente.cod_cliente}
              label={cliente.des_cliente || cliente.cod_cliente}
            />
          );
        })}
      </Tabs> : null}
    </Box>
  );
}

function useDebouncedValue(value, delayMs) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedValue(value);
    }, delayMs);

    return () => window.clearTimeout(timeout);
  }, [delayMs, value]);

  return debouncedValue;
}

function MasterActivityFilter({ table }) {
  return (
    <Stack direction="row" gap={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
      <TextField select size="small" label="Actividad" value={table.activityFilter}
        onChange={(event) => table.handleActivityFilterChange(event.target.value)}
        sx={{ minWidth: 180, maxWidth: '100%' }}>
        <MenuItem value="all">Todos</MenuItem>
        <MenuItem value="active">Solo activos</MenuItem>
        <MenuItem value="inactive">Solo inactivos</MenuItem>
      </TextField>
      <TextField size="small" type="search" label="Buscar" value={table.searchInput}
        onChange={(event) => table.setSearchInput(event.target.value)}
        sx={{ width: 220, maxWidth: '100%' }} />
    </Stack>
  );
}

function useMasterRecordsTableState(initialSortBy = 'name') {
  const table = useRemoteTableState(initialSortBy, 'asc');
  const [activityFilter, setActivityFilter] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebouncedValue(searchInput.trim(), DATE_FILTER_DEBOUNCE_MS);
  const [search, setSearch] = useState('');
  const resetPage = table.handlePageChange;
  useEffect(() => {
    resetPage(null, 0);
    setSearch(debouncedSearch);
  }, [debouncedSearch, resetPage]);
  const apiParams = useMemo(() => ({
    ...table.apiParams,
    ...(activityFilter === 'all' ? {} : { active: activityFilter === 'active' }),
    ...(search ? { search } : {}),
  }), [table.apiParams, activityFilter, search]);
  const handleActivityFilterChange = (value) => {
    table.handlePageChange(null, 0);
    setActivityFilter(value);
  };
  return { ...table, apiParams, activityFilter, handleActivityFilterChange, searchInput, setSearchInput };
}

function useLoadOrderListState() {
  const table = useRemoteTableState('load_date', 'desc');
  const [statusFilter, setStatusFilter] = useState('');
  const apiParams = useMemo(() => ({
    ...table.apiParams,
    completedLast: true,
    ...(statusFilter ? { status: statusFilter } : {}),
  }), [table.apiParams, statusFilter]);
  const handleStatusFilterChange = (status) => {
    table.handlePageChange(null, 0);
    setStatusFilter(status);
  };
  return { ...table, apiParams, statusFilter, handleStatusFilterChange };
}

function useRemoteTableState(initialSortBy = '', initialSortDirection = 'asc', initialRowsPerPage = DEFAULT_TABLE_PAGE_SIZE) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
  const [sortBy, setSortBy] = useState(initialSortBy);
  const [sortDirection, setSortDirection] = useState(initialSortDirection);
  const [meta, setMetaState] = useState({ total: 0 });

  const apiParams = useMemo(
    () => ({
      page: page + 1,
      limit: rowsPerPage,
      sortBy,
      sortDirection,
    }),
    [page, rowsPerPage, sortBy, sortDirection]
  );

  const handleSort = useCallback((field) => {
    setPage(0);
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      return;
    }
    setSortBy(field);
    setSortDirection('asc');
  }, [sortBy, sortDirection]);

  const handleRowsPerPageChange = useCallback((event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  }, []);

  const handlePageChange = useCallback((_, nextPage) => {
    setPage(nextPage);
  }, []);

  const setMeta = useCallback((nextMeta = {}) => {
    setMetaState(nextMeta || {});
  }, []);

  return useMemo(
    () => ({
      page,
      rowsPerPage,
      order: sortDirection,
      orderBy: sortBy,
      meta,
      apiParams,
      setMeta,
      handleSort,
      handlePageChange,
      handleRowsPerPageChange,
    }),
    [
      apiParams,
      handlePageChange,
      handleRowsPerPageChange,
      handleSort,
      meta,
      page,
      rowsPerPage,
      sortBy,
      sortDirection,
      setMeta,
    ]
  );
}

function useTableDragScroll() {
  const ref = useRef(null);
  const drag = useRef({
    active: false,
    startX: 0,
    scrollLeft: 0,
  });

  const finishDrag = useCallback(() => {
    drag.current.active = false;
    ref.current?.classList.remove('transport-table-wrap-dragging');
  }, []);

  const handleMouseDown = useCallback((event) => {
    if (event.button !== 0) {
      return;
    }

    const target = event.target;
    if (
      typeof target.closest === 'function' &&
      target.closest(
        'button, input, textarea, select, a, [role="button"], .MuiCheckbox-root, .MuiTableSortLabel-root'
      )
    ) {
      return;
    }

    const element = ref.current;
    if (!element || element.scrollWidth <= element.clientWidth) {
      return;
    }

    drag.current = {
      active: true,
      startX: event.clientX,
      scrollLeft: element.scrollLeft,
    };
    element.classList.add('transport-table-wrap-dragging');
    event.preventDefault();
  }, []);

  const handleMouseMove = useCallback((event) => {
    if (!drag.current.active || !ref.current) {
      return;
    }

    ref.current.scrollLeft =
      drag.current.scrollLeft - (event.clientX - drag.current.startX);
    event.preventDefault();
  }, []);

  return {
    ref,
    onMouseDown: handleMouseDown,
    onMouseMove: handleMouseMove,
    onMouseUp: finishDrag,
    onMouseLeave: finishDrag,
  };
}

function useSortablePaginatedRows(
  rows,
  initialOrderBy = '',
  initialOrder = 'asc',
  columns = []
) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_TABLE_PAGE_SIZE);
  const [orderBy, setOrderBy] = useState(initialOrderBy);
  const [order, setOrder] = useState(initialOrder);

  useEffect(() => {
    setPage(0);
  }, [rows]);

  const sortedRows = useMemo(() => {
    if (!orderBy) {
      return rows;
    }

    const column = columns.find((item) => item.field === orderBy);
    return [...rows].sort((left, right) => {
      const result = compareTableValues(
        tableSortValue(left, orderBy, column),
        tableSortValue(right, orderBy, column)
      );
      return order === 'desc' ? -result : result;
    });
  }, [columns, order, orderBy, rows]);

  const pageRows = useMemo(
    () => sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [page, rowsPerPage, sortedRows]
  );

  const handleSort = (field) => {
    setOrderBy((current) => {
      if (current === field) {
        setOrder((currentOrder) => (currentOrder === 'asc' ? 'desc' : 'asc'));
        return current;
      }
      setOrder('asc');
      return field;
    });
  };

  return {
    page,
    rowsPerPage,
    order,
    orderBy,
    pageRows,
    sortedRows,
    handleSort,
    handlePageChange: (_, nextPage) => setPage(nextPage),
    handleRowsPerPageChange: (event) => {
      setRowsPerPage(Number(event.target.value));
      setPage(0);
    },
  };
}

function SortableHeadCell({ column, table, align }) {
  return (
    <TableCell align={align}>
      <TableSortLabel
        active={table.orderBy === column.field}
        direction={table.orderBy === column.field ? table.order : 'asc'}
        onClick={() => table.handleSort(column.field)}
      >
        {column.label || column.field}
      </TableSortLabel>
    </TableCell>
  );
}

function TablePager({ table, count }) {
  return (
    <TablePagination
      component="div"
      count={count}
      page={table.page}
      rowsPerPage={table.rowsPerPage}
      rowsPerPageOptions={TABLE_ROWS_PER_PAGE_OPTIONS}
      onPageChange={table.handlePageChange}
      onRowsPerPageChange={table.handleRowsPerPageChange}
      labelRowsPerPage="Filas por página:"
      labelDisplayedRows={({ from, to, count: total }) => `${from}-${to} de ${total}`}
    />
  );
}

function DynamicRowsTable({
  columns,
  rows,
  meta,
  table,
  emptyText,
  selectable = false,
  selectedKeys = new Set(),
  onToggleRow,
  onEditRow,
  onDeleteRow,
  paginated = true,
}) {
  const dragScroll = useTableDragScroll();
  const visibleColumns =
    columns.length > 0
      ? columns.filter((column) => column.visible !== false)
      : Object.keys(rows[0] || {}).map((field) => ({ field, label: field }));
  const localTable = useSortablePaginatedRows(
    rows,
    visibleColumns[0]?.field || '',
    'asc',
    visibleColumns
  );
  const activeTable = table || localTable;
  const visibleRows = table ? rows : paginated ? localTable.pageRows : localTable.sortedRows;
  const totalRows = meta?.total ?? rows.length;

  if (!rows.length) {
    return <Box className="transport-empty-state">{emptyText}</Box>;
  }

  return (
    <TableContainer
      className="activity-table-wrap transport-table-wrap transport-table-wrap-draggable"
      {...dragScroll}
    >
      <Table size="small" className="transport-table">
        <TableHead>
          <TableRow>
            {selectable ? <TableCell padding="checkbox" /> : null}
            {visibleColumns.map((column) => (
              <SortableHeadCell key={column.field} column={column} table={activeTable} />
            ))}
            {onEditRow || onDeleteRow ? <TableCell align="right">Acciones</TableCell> : null}
          </TableRow>
        </TableHead>
        <TableBody>
          {visibleRows.map((row, rowIndex) => {
            const key = getLineKey(row) || String(rowIndex);
            return (
              <TableRow component={AnimatedTableRow} key={key} hover>
                {selectable ? (
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={selectedKeys.has(key)}
                      onChange={() => onToggleRow?.(row)}
                      slotProps={{ input: {
                        'aria-label': `Seleccionar ${lineLabel(row)}`,
                      } }}
                    />
                  </TableCell>
                ) : null}
                {visibleColumns.map((column) => (
                  <TableCell key={column.field}>
                    {formatCellValue(row[column.field])}
                  </TableCell>
                ))}
                {onEditRow || onDeleteRow ? (
                  <TableCell align="right">
                    <Stack direction="row" gap={0.75} justifyContent="flex-end">
                      {onEditRow ? (
                        <Button size="small" variant="outlined" onClick={() => onEditRow(row)}>
                          Editar
                        </Button>
                      ) : null}
                      {onDeleteRow ? (
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          onClick={() => onDeleteRow(row)}
                        >
                          Eliminar
                        </Button>
                      ) : null}
                    </Stack>
                  </TableCell>
                ) : null}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {paginated ? <TablePager table={activeTable} count={totalRows} /> : null}
    </TableContainer>
  );
}

function StaticRowsTable({
  columns,
  rows,
  onEdit,
  meta,
  table,
  paginated = true,
  actionLabel = 'Editar',
}) {
  const dragScroll = useTableDragScroll();
  const localTable = useSortablePaginatedRows(
    rows,
    columns[0]?.field || '',
    'asc',
    columns
  );
  const activeTable = table || localTable;
  const visibleRows = table ? rows : paginated ? localTable.pageRows : localTable.sortedRows;
  const totalRows = meta?.total ?? rows.length;

  if (!rows.length) {
    return <Box className="transport-empty-state">Sin datos.</Box>;
  }

  return (
    <TableContainer
      className="activity-table-wrap transport-table-wrap transport-table-wrap-draggable"
      {...dragScroll}
    >
      <Table size="small" className="transport-table">
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <SortableHeadCell key={column.field} column={column} table={activeTable} />
            ))}
            {onEdit ? <TableCell align="right">Acciones</TableCell> : null}
          </TableRow>
        </TableHead>
        <TableBody>
          {visibleRows.map((row, rowIndex) => (
            <TableRow
              component={AnimatedTableRow}
              key={row.id || row.code || rowIndex}
              hover
            >
              {columns.map((column) => (
                <TableCell key={column.field}>
                  {formatCellValue(
                    column.render ? column.render(row) : readPath(row, column.field)
                  )}
                </TableCell>
              ))}
              {onEdit ? (
                <TableCell align="right">
                  <Button size="small" variant="outlined" onClick={() => onEdit(row)}>
                    {actionLabel}
                  </Button>
                </TableCell>
              ) : null}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {paginated ? <TablePager table={activeTable} count={totalRows} /> : null}
    </TableContainer>
  );
}

function MasterDataDialog({ open, config, row, loading, onClose, onSubmit }) {
  const [form, setForm] = useState(() => initialMasterForm(config, row));
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setForm(initialMasterForm(config, row));
      setError('');
    }
  }, [config, open, row]);

  const handleChange = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSubformChange = (subformField, index, field, value) => {
    setForm((current) => {
      const rows = Array.isArray(current[subformField])
        ? [...current[subformField]]
        : [];
      rows[index] = { ...rows[index], [field]: value };
      if (field === 'isPrimary' && value) {
        rows.forEach((row, rowIndex) => {
          if (rowIndex !== index) {
            rows[rowIndex] = { ...row, isPrimary: false };
          }
        });
      }
      return { ...current, [subformField]: rows };
    });
  };

  const handleAddSubformRow = (subform) => {
    setForm((current) => ({
      ...current,
      [subform.field]: [
        ...(Array.isArray(current[subform.field]) ? current[subform.field] : []),
        initialSubformRow(subform),
      ],
    }));
  };

  const handleRemoveSubformRow = (subformField, index) => {
    setForm((current) => ({
      ...current,
      [subformField]: (Array.isArray(current[subformField])
        ? current[subformField]
        : []
      ).filter((_, rowIndex) => rowIndex !== index),
    }));
  };

  const handleNestedSubformChange = (subformField, rowIndex, nestedField, index, field, value) => {
    setForm((current) => {
      const rows = Array.isArray(current[subformField]) ? [...current[subformField]] : [];
      const row = { ...rows[rowIndex] };
      const nestedRows = Array.isArray(row[nestedField]) ? [...row[nestedField]] : [];
      nestedRows[index] = { ...nestedRows[index], [field]: value };
      if (field === 'isPrimary' && value) {
        nestedRows.forEach((nestedRow, nestedRowIndex) => {
          if (nestedRowIndex !== index) {
            nestedRows[nestedRowIndex] = { ...nestedRow, isPrimary: false };
          }
        });
      }
      row[nestedField] = nestedRows;
      rows[rowIndex] = row;
      return { ...current, [subformField]: rows };
    });
  };

  const handleAddNestedSubformRow = (subformField, rowIndex, nestedSubform) => {
    setForm((current) => {
      const rows = Array.isArray(current[subformField]) ? [...current[subformField]] : [];
      const row = { ...rows[rowIndex] };
      row[nestedSubform.field] = [
        ...(Array.isArray(row[nestedSubform.field]) ? row[nestedSubform.field] : []),
        initialSubformRow(nestedSubform),
      ];
      rows[rowIndex] = row;
      return { ...current, [subformField]: rows };
    });
  };

  const handleRemoveNestedSubformRow = (subformField, rowIndex, nestedField, index) => {
    setForm((current) => {
      const rows = Array.isArray(current[subformField]) ? [...current[subformField]] : [];
      const row = { ...rows[rowIndex] };
      row[nestedField] = (Array.isArray(row[nestedField]) ? row[nestedField] : []).filter(
        (_, nestedIndex) => nestedIndex !== index
      );
      rows[rowIndex] = row;
      return { ...current, [subformField]: rows };
    });
  };

  const handleSubmit = () => {
    const missingField = config.formFields.find(
      (field) =>
        field.required &&
        String(form[field.field] ?? '').trim() === ''
    );

    if (missingField) {
      setError(`${missingField.label} es obligatorio.`);
      return;
    }

    for (const subform of config.subforms || []) {
      const rows = Array.isArray(form[subform.field]) ? form[subform.field] : [];
      const incompleteRow = rows.find(
        (subformRow) =>
          subformRowHasAnyData(subform, subformRow) &&
          !subformRowHasRequiredData(subform, buildSubformPayload(subform, subformRow))
      );

      if (incompleteRow) {
        setError(`Completa los campos obligatorios de ${subform.title}.`);
        return;
      }
    }

    onSubmit(buildMasterPayload(config, form));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ className: 'master-dialog-paper' }}
    >
      <DialogTitle className="master-dialog-title">
        {row?.id ? `Editar ${config.singularTitle || 'registro'}` : `Nuevo ${config.singularTitle || 'registro'}`}
      </DialogTitle>
      <DialogContent className="master-dialog-content">
        <Stack className="master-dialog-form">
          {error ? <Alert severity="warning">{error}</Alert> : null}
          {config.formFields.map((field) =>
            field.type === 'boolean' ? (
              <FormControlLabel
                key={field.field}
                className="master-dialog-checkbox"
                control={
                  <Checkbox
                    checked={Boolean(form[field.field])}
                    onChange={(event) =>
                      handleChange(field.field, event.target.checked)
                    }
                  />
                }
                label={field.label}
              />
            ) : (
              <TextField
                key={field.field}
                className={field.multiline ? 'master-dialog-field-wide' : undefined}
                label={field.label}
                value={form[field.field] ?? ''}
                required={Boolean(field.required)}
                multiline={Boolean(field.multiline)}
                minRows={field.multiline ? field.minRows ?? 3 : undefined}
                maxRows={field.maxRows}
                helperText={field.helperText}
                onChange={(event) =>
                  handleChange(field.field, event.target.value)
                }
                fullWidth
              />
            )
          )}
          {config.subforms?.map((subform) => (
            <Box className="master-subform" key={subform.field}>
              <Box className="master-subform-header">
                <Box>
                  <Typography className="section-title">{subform.title}</Typography>
                  {subform.description ? (
                    <Typography className="transport-section-subtitle">
                      {subform.description}
                    </Typography>
                  ) : null}
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<AddRoundedIcon />}
                  onClick={() => handleAddSubformRow(subform)}
                >
                  Anadir
                </Button>
              </Box>
              {(Array.isArray(form[subform.field]) ? form[subform.field] : []).map(
                (subformRow, index) => (
                  <Box className="master-subform-row" key={subformRow._key || index}>
                    {subform.fields.map((field) =>
                      field.type === 'boolean' ? (
                        <FormControlLabel
                          key={field.field}
                          className="master-dialog-checkbox"
                          control={
                            <Checkbox
                              checked={Boolean(subformRow[field.field])}
                              onChange={(event) =>
                                handleSubformChange(
                                  subform.field,
                                  index,
                                  field.field,
                                  event.target.checked
                                )
                              }
                            />
                          }
                          label={field.label}
                        />
                      ) : (
                        <TextField
                          key={field.field}
                          className={`master-subform-field master-subform-field-${field.field}`}
                          label={field.label}
                          value={subformRow[field.field] ?? ''}
                          required={Boolean(field.required)}
                          multiline={Boolean(field.multiline)}
                          minRows={field.multiline ? field.minRows ?? 2 : undefined}
                          maxRows={field.maxRows}
                          onChange={(event) =>
                            handleSubformChange(
                              subform.field,
                              index,
                              field.field,
                              event.target.value
                            )
                          }
                          fullWidth
                        />
                      )
                    )}
                    {subform.childSubforms?.map((nestedSubform) => (
                      <Box className="master-nested-subform" key={nestedSubform.field}>
                        <Box className="master-nested-subform-header">
                          <Typography className="master-nested-subform-title">
                            {nestedSubform.title}
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AddRoundedIcon />}
                            onClick={() => handleAddNestedSubformRow(subform.field, index, nestedSubform)}
                          >
                            Anadir contacto
                          </Button>
                        </Box>
                        {(Array.isArray(subformRow[nestedSubform.field])
                          ? subformRow[nestedSubform.field]
                          : []
                        ).map((nestedRow, nestedIndex) => (
                          <Box className="master-nested-subform-row" key={nestedRow._key || nestedIndex}>
                            {nestedSubform.fields.map((field) =>
                              field.type === 'boolean' ? (
                                <FormControlLabel
                                  key={field.field}
                                  className="master-dialog-checkbox"
                                  control={
                                    <Checkbox
                                      checked={Boolean(nestedRow[field.field])}
                                      onChange={(event) =>
                                        handleNestedSubformChange(
                                          subform.field,
                                          index,
                                          nestedSubform.field,
                                          nestedIndex,
                                          field.field,
                                          event.target.checked
                                        )
                                      }
                                    />
                                  }
                                  label={field.label}
                                />
                              ) : (
                                <TextField
                                  key={field.field}
                                  className={`master-subform-field master-subform-field-${field.field}`}
                                  label={field.label}
                                  value={nestedRow[field.field] ?? ''}
                                  onChange={(event) =>
                                    handleNestedSubformChange(
                                      subform.field,
                                      index,
                                      nestedSubform.field,
                                      nestedIndex,
                                      field.field,
                                      event.target.value
                                    )
                                  }
                                  fullWidth
                                />
                              )
                            )}
                            <IconButton
                              color="error"
                              onClick={() =>
                                handleRemoveNestedSubformRow(
                                  subform.field,
                                  index,
                                  nestedSubform.field,
                                  nestedIndex
                                )
                              }
                              aria-label="Quitar contacto"
                            >
                              <DeleteOutlineRoundedIcon />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    ))}
                    <IconButton
                      color="error"
                      onClick={() => handleRemoveSubformRow(subform.field, index)}
                      aria-label="Quitar fila"
                    >
                      <DeleteOutlineRoundedIcon />
                    </IconButton>
                  </Box>
                )
              )}
              {(!Array.isArray(form[subform.field]) ||
                form[subform.field].length === 0) ? (
                <Box className="transport-empty-state">{subform.emptyText}</Box>
              ) : null}
            </Box>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions className="master-dialog-actions">
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          className="suite-primary-button"
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
        >
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const REQUIRED_VIEW_FIELDS = [
  'line_id',
  'document_id',
  'document_type',
  'fecha_carga',
  'source_document_no',
  'source_line_no',
  'cantidad_pales',
  'cantidad_pales_pendientes',
];

function ViewProfileDialog({
  open,
  profile,
  fields,
  clientes,
  customerProfiles = [],
  loading,
  onClose,
  onSubmit,
}) {
  const [form, setForm] = useState(() =>
    viewProfileForm(fields, profile, customerProfiles)
  );
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setForm(viewProfileForm(fields, profile, customerProfiles));
      setError('');
    }
  }, [customerProfiles, fields, open, profile]);

  const selectedFields = useMemo(
    () => new Set(form.fields.map((field) => field.field)),
    [form.fields]
  );
  const dialogFields = useMemo(
    () => mergeViewFieldDefinitions(fields, form.fields),
    [fields, form.fields]
  );
  const sortFieldOptions = useMemo(
    () =>
      [...form.fields].sort(
        (left, right) => Number(left.displayOrder || 0) - Number(right.displayOrder || 0)
      ),
    [form.fields]
  );
  const isGeneralProfile = form.code.trim().toUpperCase() === GENERAL_VIEW_CUSTOMER_CODE;

  const handleBasicChange = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleToggleField = (fieldDefinition) => {
    const fieldName = fieldDefinition.field;
    if (REQUIRED_VIEW_FIELDS.includes(fieldName)) {
      return;
    }

    setForm((current) => {
      const exists = current.fields.some((field) => field.field === fieldName);
      if (exists) {
        return {
          ...current,
          defaultSortField:
            current.defaultSortField === fieldName ? 'fecha_carga' : current.defaultSortField,
          fields: current.fields.filter((field) => field.field !== fieldName),
        };
      }

      return {
        ...current,
        fields: [
          ...current.fields,
          {
            field: fieldName,
            label: fieldDefinition.label,
            visible: true,
            displayOrder: nextDisplayOrder(current.fields),
          },
        ],
      };
    });
  };

  const handleVisibleChange = (fieldName, visible) => {
    setForm((current) => ({
      ...current,
      fields: current.fields.map((field) =>
        field.field === fieldName ? { ...field, visible } : field
      ),
    }));
  };

  const handleOrderChange = (fieldName, value) => {
    setForm((current) => ({
      ...current,
      fields: current.fields.map((field) =>
        field.field === fieldName
          ? { ...field, displayOrder: Number(value || 0) }
          : field
      ),
    }));
  };

  const handleAddCustomer = () => {
    setForm((current) => ({
      ...current,
      codClientes: [...current.codClientes, ''],
    }));
  };

  const handleCustomerChange = (index, value) => {
    setForm((current) => ({
      ...current,
      codClientes: current.codClientes.map((codCliente, customerIndex) =>
        customerIndex === index ? value : codCliente
      ),
    }));
  };

  const handleRemoveCustomer = (index) => {
    setForm((current) => ({
      ...current,
      codClientes: current.codClientes.filter((_, customerIndex) => customerIndex !== index),
    }));
  };

  const handleSubmit = () => {
    if (!form.code.trim() || !form.name.trim()) {
      setError('Codigo y nombre son obligatorios.');
      return;
    }

    if (form.fields.length === 0) {
      setError('Selecciona al menos un campo.');
      return;
    }

    onSubmit({
      codClientes: [...new Set(form.codClientes.filter(Boolean))],
      profile: {
        code: form.code.trim(),
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        documentType: form.documentType || undefined,
        active: true,
        defaultSortField: form.defaultSortField || 'fecha_carga',
        defaultSortDirection: form.defaultSortDirection || 'asc',
        fields: form.fields
          .map((field, index) => ({
            field: field.field,
            label: field.label || field.field,
            visible: field.visible,
            displayOrder: Number(field.displayOrder ?? index * 10),
          }))
          .sort((left, right) => left.displayOrder - right.displayOrder),
      },
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{ className: 'master-dialog-paper' }}
    >
      <DialogTitle className="master-dialog-title">
        {profile?.id ? 'Editar vista personalizada' : 'Nueva vista personalizada'}
      </DialogTitle>
      <DialogContent className="master-dialog-content">
        <Stack className="view-profile-dialog-form">
          {error ? <Alert severity="warning">{error}</Alert> : null}
          <TextField
            label="Codigo"
            value={form.code}
            required
            onChange={(event) => handleBasicChange('code', event.target.value)}
          />
          <TextField
            label="Nombre"
            value={form.name}
            required
            onChange={(event) => handleBasicChange('name', event.target.value)}
          />
          <FormControl>
            <InputLabel>Tipo documento</InputLabel>
            <Select
              label="Tipo documento"
              value={form.documentType}
              onChange={(event) => handleBasicChange('documentType', event.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="PEDIDO">Pedido</MenuItem>
              <MenuItem value="EXPEDICION">Expedicion</MenuItem>
            </Select>
          </FormControl>
          <FormControl>
            <InputLabel>Orden por defecto</InputLabel>
            <Select
              label="Orden por defecto"
              value={form.defaultSortField}
              onChange={(event) => handleBasicChange('defaultSortField', event.target.value)}
            >
              {sortFieldOptions.map((field) => (
                <MenuItem key={field.field} value={field.field}>
                  {field.label || field.field}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <InputLabel>Direccion orden</InputLabel>
            <Select
              label="Direccion orden"
              value={form.defaultSortDirection}
              onChange={(event) => handleBasicChange('defaultSortDirection', event.target.value)}
            >
              <MenuItem value="asc">Ascendente</MenuItem>
              <MenuItem value="desc">Descendente</MenuItem>
            </Select>
          </FormControl>
          <TextField
            className="master-dialog-field-wide"
            label="Descripcion"
            value={form.description}
            multiline
            minRows={2}
            onChange={(event) => handleBasicChange('description', event.target.value)}
          />
          {!isGeneralProfile ? (
            <Box className="master-subform view-profile-customers-subform">
              <Box className="master-subform-header">
                <Box>
                  <Typography className="section-title">Clientes asignados</Typography>
                  <Typography className="transport-section-subtitle">
                    Clientes que usaran esta vista en Cargas.
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<AddRoundedIcon />}
                  onClick={handleAddCustomer}
                >
                  Anadir
                </Button>
              </Box>
              {!form.codClientes.length ? (
                <Box className="transport-empty-state">
                  Sin clientes asignados a esta vista.
                </Box>
              ) : (
                form.codClientes.map((codCliente, index) => {
                  const selectedCodes = new Set(
                    form.codClientes.filter((_, selectedIndex) => selectedIndex !== index)
                  );
                  return (
                    <Box className="view-profile-customer-row" key={`${codCliente}-${index}`}>
                      <FormControl>
                        <InputLabel>Cliente</InputLabel>
                        <Select
                          label="Cliente"
                          value={codCliente}
                          onChange={(event) =>
                            handleCustomerChange(index, event.target.value)
                          }
                        >
                          <MenuItem value="">Seleccionar cliente</MenuItem>
                          {clientes.map((cliente) => {
                            const code = customerCodeForProfile(cliente);
                            return (
                              <MenuItem
                                key={getId(cliente)}
                                value={code}
                                disabled={selectedCodes.has(code)}
                              >
                                {cliente.name || cliente.code}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                      <IconButton
                        title="Quitar cliente"
                        onClick={() => handleRemoveCustomer(index)}
                      >
                        <DeleteOutlineRoundedIcon />
                      </IconButton>
                    </Box>
                  );
                })
              )}
            </Box>
          ) : null}
          <Box className="master-subform">
            <Box className="master-subform-header">
              <Box>
                <Typography className="section-title">Campos de la tabla</Typography>
                <Typography className="transport-section-subtitle">
                  Marca que campos aparecen como columnas y ajusta su orden.
                </Typography>
              </Box>
              <Chip
                className="suite-status-chip"
                label={`${form.fields.length} configurados / ${dialogFields.length} disponibles`}
              />
            </Box>
            <TableContainer className="activity-table-wrap transport-table-wrap view-fields-table-wrap">
              <Table size="small" className="transport-table view-fields-table">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell>Campo</TableCell>
                    <TableCell>Etiqueta</TableCell>
                    <TableCell>Visible</TableCell>
                    <TableCell>Orden</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dialogFields.map((fieldDefinition) => {
                    const selected = selectedFields.has(fieldDefinition.field);
                    const configured = form.fields.find(
                      (field) => field.field === fieldDefinition.field
                    );
                    const required = REQUIRED_VIEW_FIELDS.includes(fieldDefinition.field);
                    return (
                      <TableRow key={fieldDefinition.field} hover>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selected}
                            disabled={required}
                            onChange={() => handleToggleField(fieldDefinition)}
                          />
                        </TableCell>
                        <TableCell className="transport-line-cell">
                          {fieldDefinition.field}
                        </TableCell>
                        <TableCell>{fieldDefinition.label}</TableCell>
                        <TableCell>
                          <Checkbox
                            checked={Boolean(configured?.visible)}
                            disabled={!selected}
                            onChange={(event) =>
                              handleVisibleChange(
                                fieldDefinition.field,
                                event.target.checked
                              )
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            value={configured?.displayOrder ?? ''}
                            disabled={!selected}
                            onChange={(event) =>
                              handleOrderChange(
                                fieldDefinition.field,
                                event.target.value
                              )
                            }
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions className="master-dialog-actions">
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          className="suite-primary-button"
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
        >
          {profile?.id ? 'Guardar vista' : 'Crear vista'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function PendingImportsTable({ rows, meta, table, columns, selectedKeys, onToggle }) {
  const dragScroll = useTableDragScroll();

  if (!rows.length) {
    return (
      <Box className="transport-empty-state">
        No hay documentos pendientes en el rango seleccionado.
      </Box>
    );
  }

  return (
    <TableContainer
      className="activity-table-wrap transport-table-wrap pending-imports-table-wrap transport-table-wrap-draggable"
      {...dragScroll}
    >
      <Table size="small" className="transport-table pending-imports-table">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox" />
            {columns.map((column) => (
              <SortableHeadCell
                key={column.field}
                column={column}
                table={table}
              />
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const key = importKey(row);
            return (
              <TableRow component={AnimatedTableRow} key={key} hover>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={selectedKeys.has(key)}
                    onChange={() => onToggle(row)}
                    inputProps={{
                      'aria-label': `Seleccionar ${row.source_document_no}`,
                    }}
                  />
                </TableCell>
                {columns.map((column) => (
                  <TableCell
                    key={column.field}
                    className={
                      column.field === 'source_document_no'
                        ? 'transport-line-cell'
                        : undefined
                    }
                  >
                    {column.field === 'document_type' ? (
                      <Chip
                        size="small"
                        label={formatCellValue(readPath(row, column.field))}
                        className={`transport-status-chip ${
                          row.document_type === 'EXPEDICION'
                            ? 'transport-status-chip-info'
                            : 'transport-status-chip-neutral'
                        }`}
                      />
                    ) : (
                      formatCellValue(readPath(row, column.field))
                    )}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <TablePager table={table} count={meta?.total ?? rows.length} />
    </TableContainer>
  );
}

function PageHeader({ page, eyebrow, title, description, actions }) {
  const resolvedEyebrow = eyebrow || page?.eyebrow;
  const resolvedTitle = title || page?.label;
  const resolvedDescription = description || page?.description;

  return (
    <AnimatedSection>
      <Box className="page-heading suite-page-heading-row">
        <Box>
          <Typography className="suite-page-eyebrow">{resolvedEyebrow}</Typography>
          <Typography variant="h1" className="suite-page-title">
            {resolvedTitle}
          </Typography>
          <Typography className="suite-page-description">
            {resolvedDescription}
          </Typography>
        </Box>
        {actions ? <AnimatedActionBar>{actions}</AnimatedActionBar> : null}
      </Box>
    </AnimatedSection>
  );
}

function DashboardPage({
  eyebrow,
  title,
  description,
  cards,
  actionLabel,
  actionTo,
  statusLabel,
  children,
}) {
  return (
    <AnimatedPage>
      <Box className="suite-page">
        <PageHeader
          eyebrow={eyebrow}
          title={title}
          description={description}
          actions={
            <Stack direction="row" gap={1.25} sx={{ flexWrap: 'wrap' }}>
              {statusLabel ? (
                <Chip className="suite-status-chip" label={statusLabel} />
              ) : null}
              <Button
                className="suite-primary-button"
                component={NavLink}
                to={actionTo}
                end={actionTo === '/'}
                variant="contained"
              >
                {actionLabel}
              </Button>
            </Stack>
          }
        />

        <Box className="suite-metrics-grid">
          {cards.map((card) => (
            <MetricSummaryCard
              key={card.title}
              icon={<InsightsRoundedIcon fontSize="small" />}
              label={card.title}
              value={card.value}
              detail={card.detail}
            />
          ))}
        </Box>

        {children ? <AnimatedSection>{children}</AnimatedSection> : null}
      </Box>
    </AnimatedPage>
  );
}

export function OperationalPage({ page }) {
  return (
    <AnimatedPage>
      <Box className="suite-page">
        <PageHeader page={page} />
        <Box className="suite-metrics-grid">
          <MetricSummaryCard
            icon={<VerifiedRoundedIcon fontSize="small" />}
            label="Estado"
            value="Preparado"
            detail="Pantalla lista para conectar el formulario especifico."
          />
          <MetricSummaryCard
            icon={<RouteRoundedIcon fontSize="small" />}
            label="API"
            value="v1"
            detail="Usa cookies de sesion y x-application-code."
          />
          <MetricSummaryCard
            icon={<TuneRoundedIcon fontSize="small" />}
            label="Permisos"
            value="LOGISTICS"
            detail="Lectura y escritura segun permisos del usuario."
          />
        </Box>
      </Box>
    </AnimatedPage>
  );
}

function QuickLinks({ title, links }) {
  return (
    <Paper className="dashboard-section-card" elevation={0}>
      <Typography className="section-title">{title}</Typography>
      <Box className="suite-links-grid">
        {links.map((link) => (
          <Button
            key={link.path}
            component={NavLink}
            to={link.path}
            className="suite-link-card"
            end={link.path === '/'}
          >
            <span>{link.label}</span>
          </Button>
        ))}
      </Box>
    </Paper>
  );
}

function MetricSummaryCard({ icon, label, value, detail }) {
  return (
    <AnimatedSection variant={metricMotion}>
      <Paper className="metric-card" elevation={0}>
        <Box className="metric-card-content">
          <Box className="metric-icon">{icon}</Box>
          <Box>
            <Typography className="metric-title">{label}</Typography>
            <Typography className="metric-value">
              <AnimatedMetricValue className="suite-inline-metric-value" value={value} />
            </Typography>
            {detail ? (
              <Typography className="transport-section-subtitle">{detail}</Typography>
            ) : null}
          </Box>
        </Box>
      </Paper>
    </AnimatedSection>
  );
}

function normalizeViewDocuments(response, codCliente) {
  const groups = responseList(response);
  if (!codCliente) {
    const columns = new Map();
    groups.forEach((group) => (group.columns || []).forEach((column) => {
      if (!columns.has(column.field)) columns.set(column.field, column);
    }));
    return {
      columns: [...columns.values()],
      rows: groups.flatMap((group) => (group.rows || []).map((row) => ({
        ...row,
        cod_cliente: row.cod_cliente || group.cliente?.cod_cliente,
        des_cliente: row.des_cliente || group.cliente?.des_cliente,
      }))),
      meta: response?.meta ?? {},
    };
  }
  const group =
    groups.find((item) => item?.cliente?.cod_cliente === codCliente) ||
    groups[0] ||
    null;

  return {
    columns: Array.isArray(group?.columns) ? group.columns : [],
    rows: Array.isArray(group?.rows) ? group.rows : [],
    meta: response?.meta ?? {},
  };
}

function emptyViewDocuments() {
  return { columns: [], rows: [], meta: {} };
}

function emptyMasters() {
  return {
    clientes: [],
    transportistas: [],
    proveedores: [],
    logisticCenters: [],
    loadOrderStatuses: DEFAULT_LOAD_ORDER_STATUSES,
  };
}

async function loadOrderCustomerOptions() {
  const customers = [];
  let page = 1;
  let totalPages = 1;
  do {
    const response = await getLogisticsViewCustomers({ page, limit: PAGE_SIZE, sortDirection: 'asc', documentType: 'PEDIDO' });
    customers.push(...responseList(response));
    totalPages = response?.meta?.totalPages || 1;
    page += 1;
  } while (page <= totalPages);
  return customers.filter((item) => item.cliente?.cod_cliente !== GENERAL_VIEW_CUSTOMER_CODE && item.cliente?.active !== false);
}

async function loadAllLogisticsRows(loader, params = {}) {
  const rows = [];
  let page = 1;
  let totalPages = 1;
  do {
    const response = await loader({ ...params, page, limit: PAGE_SIZE });
    rows.push(...responseList(response));
    totalPages = response?.meta?.totalPages || Math.max(1, Math.ceil((response?.meta?.total || 0) / PAGE_SIZE));
    page += 1;
  } while (page <= totalPages);
  return rows;
}

async function loadMasterOptions() {
  const [
    clientesResponse,
    transportistasResponse,
    proveedoresResponse,
    logisticCentersResponse,
    statusesResponse,
    productsResponse,
  ] =
    await Promise.all([
      getLogisticsClientes({ page: 1, limit: PAGE_SIZE, active: true }),
      getLogisticsTransportistas({ page: 1, limit: PAGE_SIZE, active: true }),
      getLogisticsProveedores({ page: 1, limit: PAGE_SIZE, active: true }),
      getLogisticsLogisticCenters({ page: 1, limit: PAGE_SIZE, active: true }),
      getLogisticsLoadOrderStatuses({ page: 1, limit: PAGE_SIZE, active: true }),
      getLogisticsProducts({ page: 1, limit: PAGE_SIZE, active: true }),
    ]);

  return {
    clientes: responseList(clientesResponse),
    transportistas: responseList(transportistasResponse),
    proveedores: responseList(proveedoresResponse),
    logisticCenters: responseList(logisticCentersResponse),
    products: responseList(productsResponse),
    loadOrderStatuses:
      responseList(statusesResponse).length > 0
        ? responseList(statusesResponse)
        : DEFAULT_LOAD_ORDER_STATUSES,
  };
}

function defaultLoadOrderForm(overrides = {}) {
  return {
    loadDate: toInputDate(new Date()),
    status: 'PLANIFICADA',
    transportistaId: '',
    matriculaId: '',
    matricula: '',
    driverCount: 1,
    clienteId: '',
    customerDestinationId: '',
    providerWarehouseId: '',
    manualPrice: '',
    notes: '',
    ...overrides,
  };
}

function defaultLoadTripForm(overrides = {}) {
  return {
    loadDate: toInputDate(new Date()),
    transportistaId: '',
    matriculaId: '',
    matricula: '',
    notes: '',
    ...overrides,
  };
}

function defaultUnloadTripForm(overrides = {}) {
  return {
    sameAsLoad: true,
    loadDate: toInputDate(new Date()),
    transportistaId: '',
    matriculaId: '',
    matricula: '',
    customerDestinationId: '',
    notes: '',
    ...overrides,
  };
}

function defaultIntermediateStageForm(overrides = {}) {
  return {
    stageType: 'TRANSIT',
    loadDate: toInputDate(new Date()),
    matricula: '',
    locationName: '',
    logisticCenterId: '',
    notes: '',
    ...overrides,
  };
}

function loadOrderStatusForm(status = {}) {
  return {
    code: status?.code || '',
    label: status?.label || '',
    description: status?.description || '',
    displayOrder: Number(status?.displayOrder ?? 0),
    active: status?.active ?? true,
  };
}

function cmrProfileForm(profile = {}, slots = []) {
  const defaults = Object.fromEntries(
    slots.map((slot) => [slot.code, slot.defaultField || 'manual.empty'])
  );
  return {
    code: profile?.code || '',
    name: profile?.name || '',
    description: profile?.description || '',
    clienteId: profile?.clienteId ? String(profile.clienteId) : '',
    customerDestinationId: profile?.customerDestinationId
      ? String(profile.customerDestinationId)
      : '',
    language: profile?.language || 'es',
    mappings: { ...defaults, ...(profile?.mappings || {}) },
    active: profile?.active ?? true,
  };
}

function emailSettingForm(setting = {}) {
  return {
    code: setting?.code || '',
    name: setting?.name || '',
    clienteId: stringOrEmpty(setting?.cliente_id),
    customerDestinationId: stringOrEmpty(setting?.customer_destination_id),
    emailAccountId: stringOrEmpty(setting?.email_account_id),
    templateMode: 'existing',
    templateId: stringOrEmpty(setting?.template_id),
    recipientSource: setting?.recipient_source || 'PROVEEDOR',
    fixedCc: setting?.fixed_cc || '',
    newTemplateCode: '',
    newTemplateName: '',
    newTemplateDescription: '',
    subjectTemplate: 'CARGA EXPEDIENTES {Nº PEDIDO}',
    bodyTemplate:
      'Buenos dias,\n\nAdjuntamos documentacion para la carga {ORDEN DE CARGA}.\n\nCliente: {CLIENTE}\nDestino: {DESTINO}\nExpedientes: {EXPEDIENTES}\n\nUn saludo.',
    bodyFormat: 'TEXT',
    reportType: setting?.report_type || 'NONE',
    reportTemplateId: stringOrEmpty(setting?.report_template_id),
    cmrProfileId: stringOrEmpty(setting?.cmr_profile_id),
    isDefault: Boolean(setting?.is_default),
    active: setting?.active ?? true,
    notes: setting?.notes || '',
  };
}

function emailReportTypeLabel(value) {
  const labels = {
    NONE: 'Sin adjunto',
    CMR_PDF: 'CMR PDF',
  };
  return labels[value] || value || '-';
}

function emailRecipientSourceLabel(value) {
  const labels = {
    PROVEEDOR: 'Proveedor',
    CLIENTE: 'Cliente',
  };
  return labels[value] || value || '-';
}

function loadOrderFormFromOrder(order = {}) {
  const unloadStop = (order.stops || []).find((stop) => stop.stop_type === 'UNLOAD');
  const firstLine = Array.isArray(order.lines) ? order.lines[0] : null;

  return defaultLoadOrderForm({
    number: order.number || '',
    loadDate: toInputDateValue(order.load_date) || toInputDate(new Date()),
    status: order.status || 'PLANIFICADA',
    transportistaId: stringOrEmpty(order.transportista_id),
    matriculaId: stringOrEmpty(order.transportista_matricula_id),
    matricula: order.matricula || '',
    driverCount: Number(order.driver_count || 1),
    clienteId: stringOrEmpty(order.cliente_id || unloadStop?.cliente_id),
    customerDestinationId: stringOrEmpty(order.customer_destination_id || firstLine?.customer_destination_id),
    providerWarehouseId: stringOrEmpty(firstLine?.provider_warehouse_id),
    manualPrice: stringOrEmpty(order.manual_price),
    notes: order.notes || '',
  });
}

function loadOrderSplitsFromOrder(order = {}) {
  const lines = Array.isArray(order.lines) ? order.lines : [];
  const grouped = new Map();
  const stopSequenceById = new Map(
    (order.stops || []).map((stop) => [String(stop.id), Number(stop.sequence)])
  );

  lines.forEach((assignment) => {
    const documentLineId = stringOrEmpty(assignment.document_line_id);
    const isManual = Boolean(assignment.manual_line || !documentLineId);
    const groupKey = isManual
      ? `manual:${assignment.id || createTempId()}`
      : [
          documentLineId,
          assignment.matricula || order.matricula || '',
          assignment.load_stop_id || '',
          assignment.unload_stop_id || '',
        ].join(':');

    const sourceLine = assignment.source_line || {};
    const row = {
      ...sourceLine,
      id: sourceLine.id || documentLineId || assignment.id,
      line_id: sourceLine.line_id || documentLineId || assignment.id,
      document_type: sourceLine.document_type || (isManual ? 'MANUAL' : undefined),
      document_line_id: documentLineId,
      source_document_no:
        sourceLine.source_document_no ||
        assignment.expedicion_no ||
        assignment.pedido_no ||
        assignment.manual_document_no ||
        '',
      source_line_no: sourceLine.source_line_no || assignment.source_line_no || assignment.manual_line_no || 1,
      origin_pedido_no: sourceLine.origin_pedido_no || assignment.pedido_no,
      cantidad_pales: sourceLine.cantidad_pales || assignment.cantidad_pales,
      bultos_palet: sourceLine.bultos_palet || assignment.bultos,
      kilos_brutos: sourceLine.kilos_brutos || assignment.kilos_brutos,
      kilos_netos: sourceLine.kilos_netos || assignment.kilos_netos,
      kilos_oficiales: sourceLine.kilos_oficiales || assignment.kilos_oficiales,
      manual_description: assignment.manual_description || sourceLine.manual_description || '',
      manual_product_code: assignment.manual_product_code || sourceLine.manual_product_code || '',
      manual_product_name: assignment.manual_product_name || sourceLine.manual_product_name || '',
      manual_variety: assignment.manual_variety || sourceLine.manual_variety || '',
    };

    if (!grouped.has(groupKey)) {
      grouped.set(groupKey, {
        manual: isManual,
        row,
        key: getLineKey(row),
        label: isManual ? manualLineLabel(row) : lineLabel(row),
        matricula: assignment.matricula || order.matricula || '',
        maxPallets: lineTotalPallets(row),
        route: {
          loadStopSequence: stopSequenceById.get(stringOrEmpty(assignment.load_stop_id)),
          unloadStopSequence: stopSequenceById.get(stringOrEmpty(assignment.unload_stop_id)),
          providerWarehouseId: stringOrEmpty(assignment.provider_warehouse_id),
          customerDestinationId: stringOrEmpty(assignment.customer_destination_id),
        },
        parts: [],
      });
    }

    grouped.get(groupKey).parts.push({
      id: assignment.id ? String(assignment.id) : createTempId(),
      cantidadPales: formatFormQuantity(assignment.cantidad_pales),
      matricula: assignment.matricula || order.matricula || '',
    });
  });

  return [...grouped.values()].map((split) => ({
    ...split,
    parts: split.parts.length
      ? split.parts
      : [{ id: createTempId(), cantidadPales: '', matricula: split.matricula || order.matricula || '' }],
  }));
}

function validateLoadOrderForm(form, splits) {
  if (!form.loadDate) {
    return 'La fecha de carga es obligatoria.';
  }

  if (!splits.length) return 'Selecciona lineas de pedido antes de crear la orden de carga.';

  for (const split of splits) {
    const assigned = split.parts.reduce(
      (sum, part) => sum + decimalValue(part.cantidadPales),
      0
    );
    if (split.parts.some((part) => decimalValue(part.cantidadPales) < 0.01) || assigned <= 0) {
      return `Indica palets para ${split.label}.`;
    }
    if (
      split.manual &&
      !split.row.manual_description &&
      !split.row.manual_product_name &&
      !split.row.manual_variety
    ) {
      return 'Indica descripcion, producto o variedad en las lineas manuales.';
    }
    if ((!split.manual || split.maxPallets > 0) && assigned > split.maxPallets) {
      return `Los palets asignados superan los pendientes en ${split.label}.`;
    }
  }

  return '';
}

function validateLoadTripForm(form, lines) {
  if (!form.loadDate) {
    return 'La fecha de carga es obligatoria.';
  }

  if (!lines.length || lines.some((line) => !line.row.load_order_id || !line.row.source_load_order_line_id)) {
    return 'Selecciona lineas de las ordenes de carga para crear el viaje.';
  }
  if (hasMultipleCustomers(lines.map((line) => line.row))) {
    return 'Selecciona lineas de un solo cliente para crear el viaje.';
  }

  for (const line of lines) {
    const cantidadPales = decimalValue(line.cantidadPales);
    const pending = linePendingPallets(line.row);
    if (cantidadPales <= 0) {
      return `Indica palets a recoger para ${lineLabel(line.row)}.`;
    }
    if (cantidadPales > pending) {
      return `La linea ${lineLabel(line.row)} supera los palets pendientes.`;
    }
  }

  return '';
}

function validateUnloadTripForm(form, lines) {
  if (!form.loadDate) {
    return 'La fecha de descarga es obligatoria.';
  }

  const loadOrderIds = new Set(lines.map((line) => line.row?.load_order_id).filter(Boolean));
  if (loadOrderIds.size > 1) {
    return 'Selecciona lineas del mismo viaje de carga para crear la descarga.';
  }

  if (!form.sameAsLoad && !form.customerDestinationId) {
    return 'Selecciona destino de descarga.';
  }

  for (const line of lines) {
    const cantidadPales = decimalValue(line.cantidadPales);
    const pending = lineUnloadPendingPallets(line.row);
    if (cantidadPales <= 0) {
      return `Indica palets a descargar para ${lineLabel(line.row)}.`;
    }
    if (cantidadPales > pending) {
      return `La linea ${lineLabel(line.row)} supera los palets pendientes de descarga.`;
    }
  }

  return '';
}

function validateIntermediateStageForm(form, lines) {
  if (!form.loadDate) {
    return 'La fecha de etapa es obligatoria.';
  }
  if (!String(form.matricula || '').trim()) {
    return 'La matricula es obligatoria.';
  }
  if (form.stageType === 'LOGISTICS_CENTER' && !form.logisticCenterId) {
    return 'Selecciona un centro logistico.';
  }
  if ((form.stageType === 'TRANSIT' || form.stageType === 'CUSTOMS' || form.stageType === 'VEHICLE_CHANGE') && !String(form.locationName || '').trim()) {
    return 'Indica la ubicacion de la etapa.';
  }
  if (!lines.some((line) => decimalValue(line.cantidadPales) > 0)) {
    return 'Indica palets en al menos una linea.';
  }
  for (const line of lines) {
    const pending = lineStagePendingPallets(line.row);
    const requested = decimalValue(line.cantidadPales);
    if (requested > pending) {
      return 'No se pueden mover mas palets de los disponibles en la etapa anterior.';
    }
  }
  return '';
}

function buildLoadTripPayload(form, lines) {
  return cleanPayload({
    loadDate: form.loadDate,
    tripType: 'LOAD',
    operationType: 'LOAD_TRIP',
    parentLoadOrderId: new Set(lines.map((line) => line.row.load_order_id)).size === 1 ? lines[0]?.row.load_order_id : undefined,
    status: 'PLANIFICADA',
    transportistaId: form.transportistaId,
    matriculaId: form.matriculaId,
    matricula: form.matricula,
    notes: form.notes || 'Viaje de carga',
    lines: lines
      .map((line, index) => ({
        documentLineId: line.row.document_line_id || getLineId(line.row),
        sourceLoadOrderLineId: line.row.source_load_order_line_id,
        providerWarehouseId: line.row.provider_warehouse_id,
        customerDestinationId: line.row.customer_destination_id,
        cantidadPales: formatFormQuantity(decimalValue(line.cantidadPales)),
        matricula: form.matricula,
        displayOrder: index,
      }))
      .filter((line) => line.documentLineId && decimalValue(line.cantidadPales) > 0),
  });
}

function buildIntermediateStagePayload(order, form, lines) {
  const selectedStage = INTERMEDIATE_STAGE_OPTIONS.find((option) => option.value === form.stageType) || INTERMEDIATE_STAGE_OPTIONS[0];
  const tripType = selectedStage.tripType || selectedStage.value;
  const stop = cleanPayload({
    sequence: 1,
    stopType: tripType,
    logisticCenterId: form.stageType === 'LOGISTICS_CENTER' ? form.logisticCenterId : undefined,
    locationName: form.stageType === 'LOGISTICS_CENTER' ? undefined : form.locationName,
    matricula: form.matricula,
    plannedAt: form.loadDate,
    instructions: form.notes,
  });

  return cleanPayload({
    loadDate: form.loadDate,
    tripType,
    operationType: selectedStage.operationType,
    status: 'PLANIFICADA',
    matricula: form.matricula,
    parentLoadOrderId: stringOrEmpty(order?.id),
    notes: form.notes || selectedStage.label,
    stops: [stop],
    lines: lines
      .map((line, index) => ({
        documentLineId: stringOrEmpty(line.row?.document_line_id),
        sourceLoadOrderLineId: stringOrEmpty(line.row?.id),
        cantidadPales: formatFormQuantity(decimalValue(line.cantidadPales)),
        matricula: form.matricula,
        customerDestinationId: stringOrEmpty(line.row?.customer_destination_id),
        displayOrder: index,
      }))
      .filter((line) => line.documentLineId && decimalValue(line.cantidadPales) > 0),
  });
}

function buildUnloadTripPayload(form, lines) {
  const firstRow = lines[0]?.row || {};
  const sameAsLoad = Boolean(form.sameAsLoad);
  const matricula = sameAsLoad ? firstRow.matricula : form.matricula;
  const customerDestinationId = sameAsLoad
    ? stringOrEmpty(firstRow.customer_destination_id)
    : form.customerDestinationId;

  return cleanPayload({
    loadDate: sameAsLoad ? toInputDateValue(firstRow.load_date) || form.loadDate : form.loadDate,
    tripType: 'UNLOAD',
    operationType: 'UNLOAD_TRIP',
    status: 'PLANIFICADA',
    transportistaId: sameAsLoad ? stringOrEmpty(firstRow.transportista_id) : form.transportistaId,
    matriculaId: sameAsLoad ? stringOrEmpty(firstRow.transportista_matricula_id) : form.matriculaId,
    matricula,
    parentLoadOrderId: stringOrEmpty(firstRow.load_order_id),
    customerDestinationId,
    notes: form.notes || 'Viaje de descarga',
    lines: lines
      .map((line, index) => ({
        documentLineId: stringOrEmpty(line.row?.document_line_id),
        sourceLoadOrderLineId: stringOrEmpty(line.row?.source_load_order_line_id),
        cantidadPales: formatFormQuantity(decimalValue(line.cantidadPales)),
        matricula,
        customerDestinationId: sameAsLoad
          ? stringOrEmpty(line.row?.customer_destination_id)
          : customerDestinationId,
        displayOrder: index,
      }))
      .filter((line) => line.documentLineId && decimalValue(line.cantidadPales) > 0),
  });
}

function buildLoadOrderPayload(form, splits) {
  const lines = splits.flatMap((split, splitIndex) =>
    split.parts
      .map((part, partIndex) => {
        const cantidadPales = decimalValue(part.cantidadPales);
        if (cantidadPales <= 0) {
          return null;
        }
        return cleanPayload({
          documentLineId: split.manual ? undefined : getLineId(split.row),
          manualLine: Boolean(split.manual),
          manualDocumentNo: split.manual ? split.row.source_document_no : undefined,
          manualLineNo: split.manual ? Number(split.row.source_line_no || splitIndex + 1) : undefined,
          manualProductCode: split.manual ? split.row.manual_product_code : undefined,
          manualProductName: split.manual ? split.row.manual_product_name : undefined,
          manualVariety: split.manual ? split.row.manual_variety : undefined,
          manualDescription: split.manual ? split.row.manual_description : undefined,
          matricula: part.matricula || split.matricula || form.matricula,
          cantidadPales: formatFormQuantity(cantidadPales),
          // Imported lines use the backend's proportional quantities for the assigned pallets.
          bultos: split.manual ? split.row.bultos_palet : undefined,
          kilosBrutos: split.manual ? split.row.kilos_brutos : undefined,
          kilosNetos: split.manual ? split.row.kilos_netos : undefined,
          kilosOficiales: split.manual ? split.row.kilos_oficiales : undefined,
          displayOrder: splitIndex * 100 + partIndex,
        });
      })
      .filter(Boolean)
  );

  return cleanPayload({
    loadDate: form.loadDate,
    operationType: 'INTERNAL_LOAD_ORDER',
    status: form.status,
    transportistaId: form.transportistaId,
    matriculaId: form.matriculaId,
    matricula: form.matricula,
    driverCount: Number(form.driverCount || 1),
    manualPrice: form.manualPrice,
    notes: form.notes,
    lines,
  });
}

function baseMasterFields() {
  return [
    { field: 'code', label: 'Codigo', required: true },
    { field: 'codNavision', label: 'Cod. NAV' },
    { field: 'name', label: 'Nombre', required: true },
    { field: 'fiscalName', label: 'Nombre fiscal' },
    { field: 'address', label: 'Direccion' },
    { field: 'city', label: 'Poblacion' },
    { field: 'province', label: 'Provincia' },
    { field: 'postalCode', label: 'Codigo postal' },
    { field: 'country', label: 'Pais' },
    { field: 'notes', label: 'Notas', multiline: true, minRows: 3, maxRows: 3 },
    { field: 'active', label: 'Activo', type: 'boolean' },
  ];
}

function contactsSubform(title = 'Contactos') {
  return {
    field: 'contacts',
    title,
    description: 'Personas de contacto y canales de comunicacion.',
    emptyText: 'Sin contactos.',
    fields: [
      { field: 'name', label: 'Nombre' },
      { field: 'position', label: 'Cargo' },
      { field: 'phone', label: 'Telefono' },
      { field: 'email', label: 'Email' },
      { field: 'isPrimary', label: 'Principal', type: 'boolean' },
      { field: 'active', label: 'Activo', type: 'boolean' },
    ],
  };
}

function getTransportistaConfig() {
  return {
    title: 'Transportistas',
    singularTitle: 'transportista',
    formFields: baseMasterFields(),
    subforms: [
      {
        field: 'matriculas',
        title: 'Matriculas',
        description: 'Vehiculos disponibles para este transportista.',
        emptyText: 'Sin matriculas.',
        fields: [
          { field: 'matricula', label: 'Matricula', required: true },
          { field: 'description', label: 'Descripcion' },
          { field: 'active', label: 'Activa', type: 'boolean' },
        ],
      },
      contactsSubform(),
    ],
  };
}

function getTransportistasTableColumns() {
  return [
    { field: 'code', label: 'Codigo' },
    { field: 'codNavision', label: 'Cod. NAV' },
    { field: 'name', label: 'Nombre' },
    {
      field: 'matriculas',
      label: 'Matriculas',
      render: (row) => summarizeList(getMatriculas(row), 'matricula'),
    },
    { field: 'province', label: 'Provincia' },
    { field: 'active', label: 'Activo' },
  ];
}

function getTariffsTableColumns() {
  return [
    { field: 'code', label: 'Codigo' },
    { field: 'origin_province', label: 'Origen' },
    {
      field: 'cliente',
      label: 'Cliente',
      render: (row) => entityLabel(row.cliente),
    },
    {
      field: 'customer_destination',
      label: 'Destino',
      render: (row) => entityLabel(row.customer_destination),
    },
    { field: 'price_type', label: 'Tipo' },
    { field: 'base_price', label: 'Base' },
  ];
}

function getMasterConfig(entity) {
  if (entity === 'clientes') {
    return {
      title: 'Clientes y destinos',
      singularTitle: 'cliente',
      syncGroup: 'clientes',
      loader: getLogisticsClientes,
      creator: createLogisticsCliente,
      updater: updateLogisticsCliente,
      formFields: baseMasterFields(),
      subforms: [
        contactsSubform(),
        {
          field: 'destinos',
          title: 'Destinos cliente',
          description: 'Direcciones de entrega o descarga asociadas al cliente.',
          emptyText: 'Sin destinos.',
          fields: [
            { field: 'code', label: 'Codigo', required: true },
            { field: 'codNavision', label: 'Cod. NAV' },
            { field: 'name', label: 'Nombre', required: true },
            { field: 'address', label: 'Direccion' },
            { field: 'city', label: 'Poblacion' },
            { field: 'province', label: 'Provincia' },
            { field: 'postalCode', label: 'Codigo postal' },
            { field: 'country', label: 'Pais' },
            { field: 'active', label: 'Activo', type: 'boolean' },
          ],
          childSubforms: [contactsSubform('Contactos del destino')],
        },
      ],
      columns: [
        { field: 'code', label: 'Codigo' },
        { field: 'codNavision', label: 'Cod. NAV' },
        { field: 'name', label: 'Nombre' },
        { field: 'province', label: 'Provincia' },
        {
          field: 'destinos',
          label: 'Destinos',
          render: (row) => summarizeList(getCustomerDestinations(row), 'name'),
        },
        { field: 'active', label: 'Activo' },
      ],
    };
  }

  if (entity === 'proveedores') {
    return {
      title: 'Proveedores y almacenes de carga',
      singularTitle: 'proveedor',
      syncGroup: 'proveedores',
      loader: getLogisticsProveedores,
      creator: createLogisticsProveedor,
      updater: updateLogisticsProveedor,
      formFields: baseMasterFields(),
      subforms: [
        contactsSubform(),
        {
          field: 'almacenesCarga',
          title: 'Almacenes de carga',
          description: 'Direcciones donde este proveedor prepara o entrega la carga.',
          emptyText: 'Sin almacenes de carga.',
          fields: [
            { field: 'code', label: 'Codigo', required: true },
            { field: 'codNavision', label: 'Cod. NAV' },
            { field: 'name', label: 'Nombre', required: true },
            { field: 'address', label: 'Direccion' },
            { field: 'city', label: 'Poblacion' },
            { field: 'province', label: 'Provincia' },
            { field: 'postalCode', label: 'Codigo postal' },
            { field: 'country', label: 'Pais' },
            { field: 'active', label: 'Activo', type: 'boolean' },
          ],
          childSubforms: [contactsSubform('Contactos del almacen')],
        },
      ],
      columns: [
        { field: 'code', label: 'Codigo' },
        { field: 'codNavision', label: 'Cod. NAV' },
        { field: 'name', label: 'Nombre' },
        { field: 'province', label: 'Provincia' },
        {
          field: 'almacenesCarga',
          label: 'Almacenes',
          render: (row) => summarizeList(getProviderWarehouses(row), 'name'),
        },
        { field: 'source_type', label: 'Origen' },
        { field: 'active', label: 'Activo' },
      ],
    };
  }

  if (entity === 'centros-logisticos') {
    return {
      title: 'Centros logisticos',
      singularTitle: 'centro logistico',
      loader: getLogisticsLogisticCenters,
      creator: createLogisticsLogisticCenter,
      updater: updateLogisticsLogisticCenter,
      formFields: baseMasterFields().filter((field) => field.field !== 'fiscalName'),
      subforms: [contactsSubform()],
      columns: [
        { field: 'code', label: 'Codigo' },
        { field: 'codNavision', label: 'Cod. NAV' },
        { field: 'name', label: 'Nombre' },
        { field: 'city', label: 'Poblacion' },
        { field: 'province', label: 'Provincia' },
        { field: 'active', label: 'Activo' },
      ],
    };
  }

  return {
    title: 'Productos logisticos',
    singularTitle: 'producto',
    loader: getLogisticsProducts,
    creator: createLogisticsProduct,
    updater: updateLogisticsProduct,
    formFields: [
      { field: 'code', label: 'Codigo', required: true },
      { field: 'codNavision', label: 'Cod. NAV' },
      { field: 'name', label: 'Nombre', required: true },
      { field: 'codVariedad', label: 'Cod. variedad' },
      { field: 'desVariedad', label: 'Variedad' },
      { field: 'codConfeccion', label: 'Cod. confeccion' },
      { field: 'desConfeccion', label: 'Confeccion' },
      { field: 'codEnvase', label: 'Cod. envase' },
      { field: 'desEnvase', label: 'Envase' },
      { field: 'notes', label: 'Notas', multiline: true },
      { field: 'active', label: 'Activo', type: 'boolean' },
    ],
    columns: [
      { field: 'code', label: 'Codigo' },
      { field: 'codNavision', label: 'Cod. NAV' },
      { field: 'name', label: 'Nombre' },
      { field: 'des_variedad', label: 'Variedad' },
      { field: 'des_confeccion', label: 'Confeccion' },
      { field: 'des_envase', label: 'Envase' },
      { field: 'active', label: 'Activo' },
    ],
  };
}

function defaultDateRange() {
  const range = quickDateRange('today');
  return {
    fechaDesde: range.from,
    fechaHasta: range.to,
    codClientes: [],
    destinos: [],
  };
}

function quickDateRange(preset) {
  const today = new Date();
  if (preset === 'week') {
    const start = startOfWeek(today);
    return {
      from: toInputDate(start),
      to: toInputDate(addDays(start, 6)),
    };
  }

  const date = toInputDate(today);
  return { from: date, to: date };
}

function startOfWeek(date) {
  const result = new Date(date);
  const day = result.getDay() || 7;
  result.setDate(result.getDate() - day + 1);
  return result;
}

function applyFechaDateRangePreset(current, preset) {
  const range = quickDateRange(preset);
  return {
    ...current,
    fechaDesde: range.from,
    fechaHasta: range.to,
  };
}

function isFechaDateRangePreset(range, preset) {
  const expected = quickDateRange(preset);
  return range.fechaDesde === expected.from && range.fechaHasta === expected.to;
}

function readStoredPendingImportColumns() {
  const fallback = DEFAULT_PENDING_IMPORT_COLUMNS.map((column) => column.field);
  try {
    const raw = window.localStorage.getItem(PENDING_IMPORT_COLUMNS_STORAGE_KEY);
    const parsed = JSON.parse(raw || '[]');
    return Array.isArray(parsed) && parsed.length
      ? parsed.filter((field) => typeof field === 'string' && field.trim())
      : fallback;
  } catch {
    return fallback;
  }
}

function readStoredLoadOrderAssignedLineFields() {
  const legacyFields = {
    document: 'line.source_document_no',
    pedido: 'line.origin_pedido_no',
    line: 'line.source_line_no',
    product: 'line.des_producto',
    variety: 'line.des_variedad',
    matricula: 'assignment.matricula',
    load: 'line.des_dir_carga',
    unload: 'line.des_dir_envio',
    pallets: 'assignment.cantidad_pales',
  };
  try {
    const raw = window.localStorage.getItem(LOAD_ORDER_ASSIGNED_LINE_FIELDS_STORAGE_KEY);
    const selected = JSON.parse(raw || '[]');
    const fields = Array.isArray(selected)
      ? selected
        .filter((field) => typeof field === 'string')
        .map((field) => legacyFields[field] || field)
      : [];
    return fields.length
      ? fields
      : DEFAULT_LOAD_ORDER_ASSIGNED_LINE_FIELDS.map((field) => legacyFields[field] || field);
  } catch {
    return DEFAULT_LOAD_ORDER_ASSIGNED_LINE_FIELDS.map((field) => legacyFields[field] || field);
  }
}

function writeStoredLoadOrderAssignedLineFields(fields) {
  try {
    window.localStorage.setItem(
      LOAD_ORDER_ASSIGNED_LINE_FIELDS_STORAGE_KEY,
      JSON.stringify(fields)
    );
  } catch {
    // La configuracion permanece disponible durante la sesion actual.
  }
}

function useMasterTableColumns(entity, columns) {
  return useMemo(() => {
    const stored = readStoredMasterTableColumns();
    const selectedFields = Array.isArray(stored[entity]) && stored[entity].length
      ? stored[entity]
      : columns.map((column) => column.field);
    const columnsByField = new Map(columns.map((column) => [column.field, column]));
    const visibleColumns = selectedFields
      .map((field) => columnsByField.get(field))
      .filter(Boolean);

    return visibleColumns.length ? visibleColumns : columns;
  }, [columns, entity]);
}

function getMasterTableColumnOptions() {
  return {
    transportistas: getTransportistasTableColumns(),
    tarifas: getTariffsTableColumns(),
    clientes: getMasterConfig('clientes').columns,
    proveedores: getMasterConfig('proveedores').columns,
    'centros-logisticos': getMasterConfig('centros-logisticos').columns,
    productos: getMasterConfig('productos').columns,
  };
}

function readStoredMasterTableColumns() {
  const defaults = defaultMasterTableColumns();

  try {
    const raw = window.localStorage.getItem(MASTER_TABLE_COLUMNS_STORAGE_KEY);
    const parsed = JSON.parse(raw || '{}');

    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return defaults;
    }

    return Object.fromEntries(
      Object.entries(defaults).map(([entity, fallback]) => {
        const fields = Array.isArray(parsed[entity])
          ? parsed[entity].filter((field) => typeof field === 'string' && field.trim())
          : [];

        return [entity, fields.length ? fields : fallback];
      })
    );
  } catch {
    return defaults;
  }
}

function writeStoredMasterTableColumns(columnsByEntity) {
  try {
    window.localStorage.setItem(
      MASTER_TABLE_COLUMNS_STORAGE_KEY,
      JSON.stringify(columnsByEntity)
    );
  } catch {
    // La configuracion permanece disponible durante la sesion actual.
  }
}

function defaultMasterTableColumns() {
  const options = getMasterTableColumnOptions();
  return Object.fromEntries(
    MASTER_TABLE_ENTITIES.map((item) => [
      item.entity,
      (options[item.entity] || []).map((column) => column.field),
    ])
  );
}

function loadOrderAssignedLineFieldLabel(field) {
  const legacy = LOAD_ORDER_ASSIGNED_LINE_FIELD_OPTIONS.find((option) => option.field === field);
  const header = LOAD_ORDER_HEADER_FIELD_OPTIONS.find((option) => option.field === field);
  return legacy?.label || header?.label || humanizeFieldName(field.replace(/^(line|assignment)\./, ''));
}

function loadOrderAssignedLineFieldValue(split, field, assignedPallets, form = {}, transportista = null) {
  const row = split.row || {};
  const lineMatriculas = [...new Set(
    (split.parts || [])
      .map((part) => part.matricula || split.matricula)
      .filter(Boolean)
  )].join(', ');
  const values = {
    document: row.source_document_no || row.document_no || '-',
    pedido: row.origin_pedido_no || '-',
    line: row.source_line_no || row.line_no || '-',
    product: row.des_producto || row.manual_product_name || row.manual_description || '-',
    variety: row.des_variedad || row.manual_variety || '-',
    matricula: lineMatriculas || '-',
    load: row.des_dir_carga || row.cod_dir_carga || '-',
    unload: row.des_dir_envio || row.cod_dir_envio || '-',
    pallets: formatQuantity(assignedPallets),
  };
  if (field.startsWith('line.')) {
    return formatCellValue(readPath(row, field.slice(5)) ?? '-');
  }
  if (field === 'assignment.matricula') return lineMatriculas || '-';
  if (field === 'assignment.cantidad_pales') return formatQuantity(assignedPallets);
  if (field.startsWith('header.')) {
    const headerValues = {
      number: form.number || '-',
      loadDate: form.loadDate || '-',
      status: form.status || '-',
      transportistaId: transportista?.name || transportista?.code || '-',
      matricula: form.matricula || '-',
      driverCount: form.driverCount || '-',
      manualPrice: form.manualPrice || '-',
      notes: form.notes || '-',
    };
    return headerValues[field.slice(7)] || '-';
  }
  return values[field] || '-';
}

function writeStoredPendingImportColumns(fields) {
  try {
    window.localStorage.setItem(
      PENDING_IMPORT_COLUMNS_STORAGE_KEY,
      JSON.stringify(fields)
    );
  } catch {
    // El usuario puede tener almacenamiento bloqueado; la seleccion seguirá viva en memoria.
  }
}

function pendingImportColumnOptions(rows = [], selectedFields = []) {
  const labels = new Map(
    DEFAULT_PENDING_IMPORT_COLUMNS.map((column) => [column.field, column.label])
  );
  const fieldSet = new Set(DEFAULT_PENDING_IMPORT_COLUMNS.map((column) => column.field));
  selectedFields.forEach((field) => fieldSet.add(field));
  rows.forEach((row) => {
    Object.keys(row || {}).forEach((field) => fieldSet.add(field));
  });

  return [...fieldSet]
    .filter(Boolean)
    .map((field) => ({ field, label: labels.get(field) || humanizeFieldName(field) }))
    .sort((left, right) => {
      const leftDefault = DEFAULT_PENDING_IMPORT_COLUMNS.findIndex(
        (column) => column.field === left.field
      );
      const rightDefault = DEFAULT_PENDING_IMPORT_COLUMNS.findIndex(
        (column) => column.field === right.field
      );
      if (leftDefault !== -1 || rightDefault !== -1) {
        return (leftDefault === -1 ? 999 : leftDefault) - (rightDefault === -1 ? 999 : rightDefault);
      }
      return left.label.localeCompare(right.label, 'es', { sensitivity: 'base' });
    });
}

function selectedColumnsToDefinitions(selectedFields = [], options = []) {
  const optionByField = new Map(options.map((option) => [option.field, option]));
  return selectedFields
    .map((field) => optionByField.get(field) || { field, label: humanizeFieldName(field) })
    .filter((column) => column.field);
}

function ensureLoadTripColumns(columns = []) {
  const required = [
    { field: 'source_document_no', label: 'Pedido' },
    { field: 'source_line_no', label: 'Linea' },
    { field: 'des_cliente', label: 'Cliente' },
    { field: 'des_variedad', label: 'Variedad' },
    { field: 'cantidad_pales', label: 'Palets de la orden' },
    { field: 'cantidad_pales_asignados', label: 'Palets asignados' },
    { field: 'cantidad_pales_pendientes', label: 'Palets pendientes' },
  ];
  const byField = new Map(columns.map((column) => [column.field, column]));
  required.forEach((column) => {
    if (!byField.has(column.field)) {
      byField.set(column.field, column);
    }
  });
  return [...byField.values()];
}

function flattenLoadOrderOrigins(orders = [], trips = []) {
  const assigned = new Map();
  trips.forEach((trip) => (trip.lines || []).forEach((line) => {
    const id = String(line.document_line_id || '');
    assigned.set(id, (assigned.get(id) || 0) + decimalValue(line.cantidad_pales));
  }));
  return orders.flatMap((order) => (order.lines || []).filter((line) => line.document_line_id).map((line) => {
    const source = line.source_line || {};
    const used = activeOutgoingPallets(line);
    const total = decimalValue(line.cantidad_pales);
    const remainingInOrder = Math.max(total - used, 0);
    const remainingInSource = source.cantidad_pales === undefined ? remainingInOrder
      : Math.max(decimalValue(source.cantidad_pales) - (assigned.get(String(line.document_line_id)) || 0), 0);
    return { ...source, ...line,
      id: `load:${order.id}:${line.id}`, line_id: `load:${order.id}:${line.id}`,
      document_line_id: String(line.document_line_id), load_order_id: String(order.id),
      source_load_order_line_id: String(line.id),
      load_order_number: order.number, order_load_date: order.load_date,
      document_heading: loadDocumentHeadingData(order),
      source_document_no: source.source_document_no || line.pedido_no,
      cod_cliente: source.cod_cliente || order.cliente?.code || '',
      des_cliente: source.des_cliente || order.cliente?.name || '',
      customer_destination_id: line.customer_destination_id || order.customer_destination_id,
      cantidad_pales: total, cantidad_pales_asignados: used,
      cantidad_pales_pendientes: Math.min(remainingInOrder, remainingInSource),
    };
  }));
}

function flattenUnloadTripRows(sourceTrips = []) {
  return sourceTrips.flatMap((order) =>
    (order.lines || []).map((line) => {
      const sourceLine = line.source_line || {};
      const documentLineId = stringOrEmpty(
        line.document_line_id || sourceLine.line_id || sourceLine.id
      );
      const loadOrderId = stringOrEmpty(order.id);
      const stageLabel = loadOrderStageLabel(order);
      const loadedPallets = decimalValue(line.cantidad_pales);
      const outgoingPallets = activeOutgoingPallets(line);
      const assignedUnloadPallets = activeOutgoingPallets({ outgoing_links: (line.outgoing_links || [])
        .filter((link) => String(link.link_type || '').endsWith('_TO_UNLOAD')) });
      const pendingUnloadPallets = Math.max(loadedPallets - outgoingPallets, 0);

      return {
        ...sourceLine,
        ...line,
        cod_cliente: line.cod_cliente || sourceLine.cod_cliente || order.cliente?.cod_navision || order.cliente?.code || '',
        des_cliente: line.des_cliente || sourceLine.des_cliente || order.cliente?.name || '',
        id: `unload:${loadOrderId}:${line.id || documentLineId}`,
        line_id: `unload:${loadOrderId}:${line.id}`,
        source_load_order_line_id: stringOrEmpty(line.id),
        source_line_origin: (line.incoming_links || []).map((link) => link.from_load_order_number).filter(Boolean).join(', ')
          || loadTripOriginLabel(line, order),
        document_line_id: documentLineId,
        load_order_id: loadOrderId,
        load_order_number: order.number,
        document_heading: loadDocumentHeadingData(order),
        stage_label: stageLabel,
        source_position_label: [stageLabel, order.number].filter(Boolean).join(' - '),
        origin_location: loadOrderCurrentLocation(order, line),
        parent_load_order_id: stringOrEmpty(order.parent_load_order_id),
        load_date: order.load_date,
        transportista_id: stringOrEmpty(order.transportista_id),
        transportista_matricula_id: stringOrEmpty(order.transportista_matricula_id),
        matricula: line.matricula || order.matricula || '',
        customer_destination_id: stringOrEmpty(
          line.customer_destination_id || order.customer_destination_id
        ),
        cantidad_pales_cargados: formatFormQuantity(loadedPallets),
        cantidad_pales_otros_movimientos: formatFormQuantity(Math.max(outgoingPallets - assignedUnloadPallets, 0)),
        cantidad_pales_descarga_asignados: formatFormQuantity(assignedUnloadPallets),
        cantidad_pales_descargados: formatFormQuantity(assignedUnloadPallets),
        cantidad_pales_descarga_pendientes: formatFormQuantity(pendingUnloadPallets),
      };
    })
  );
}

function activeOutgoingPallets(line = {}) {
  return (line.outgoing_links || [])
    .filter((link) => !isCancelledLoadOrder({ status: link.to_load_order_status }))
    .reduce((total, link) => total + decimalValue(link.cantidad_pales), 0);
}

function lineStagePendingPallets(line = {}) {
  return Math.max(decimalValue(line.cantidad_pales) - activeOutgoingPallets(line), 0);
}

function loadDocumentHeadingData(order) {
  const { number, operation_type, trip_type, cliente, transportista, matricula, load_date,
    driver_count, customer_destination, notes, stops } = order;
  return { number, operation_type, trip_type, cliente, transportista, matricula, load_date,
    driver_count, customer_destination, notes, stops };
}

function LoadDocumentHeading({ order, label, showNumber = false }) {
  const lines = order.lines || [];
  const unique = (values) => [...new Set(values.map((value) => String(value || '').trim()).filter(Boolean))];
  const operationType = order.operation_type || order.operationType;
  const action = label || (operationType === 'LOAD_TRIP' ? 'Viaje de carga'
    : operationType === 'UNLOAD_TRIP' || order.trip_type === 'UNLOAD' ? 'Viaje de descarga'
      : !operationType || operationType === 'INTERNAL_LOAD_ORDER' ? 'Orden de carga' : loadOrderStageLabel(order));
  const customers = unique(lines.map((line) => line.source_line?.des_cliente || line.des_cliente));
  const customer = order.cliente?.name || order.cliente?.des_cliente || customers.join(', ') || order.cliente?.code || 'Sin cliente';
  const plates = unique([order.matricula, ...lines.map((line) => line.matricula)]);
  const orders = unique(lines.map((line) => line.pedido_no || line.source_line?.origin_pedido_no
    || line.source_line?.source_document_no || line.origin_pedido_no || line.source_document_no || line.manual_document_no));
  const deliveryStops = (order.stops || []).filter((stop) => (stop.stop_type || stop.stopType) === 'UNLOAD')
    .slice().sort((left, right) => Number(left.sequence || 0) - Number(right.sequence || 0));
  const stopDestinations = unique(deliveryStops.map((stop) => stop.location_name || stop.customer_destination?.name
    || stop.city || stop.address));
  const lineDestinations = unique(lines.map((line) => line.customer_destination?.name || line.source_line?.des_dir_envio || line.des_dir_envio));
  const destinations = stopDestinations.length ? stopDestinations : lineDestinations.length ? lineDestinations
    : unique([order.customer_destination?.name]);
  const carrier = order.transportista?.name || order.transportista?.code || 'Sin transportista';
  const driverCount = Number(order.driver_count) || 1;
  const info = String(order.notes || '').trim() || unique(lines.map((line) => line.notes)).join(' | ');
  return (
    <Box className="transport-document-heading" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
      <Stack direction="row" gap={1} sx={{ alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}>
        <Typography className="transport-managed-title">
          {[customer, carrier, plates.join(', ') || 'Sin matrícula'].join(' - ')}
        </Typography>
        {showNumber && order.number ? <Chip size="small" variant="outlined" label={order.number} title={action} /> : null}
        {label ? <Chip size="small" variant="outlined" label={label} /> : null}
      </Stack>
      <Typography className="transport-managed-dock">
        {[formatShortDate(order.load_date), ...destinations].join(' | ')}{' | '}
        {driverCount === 2 ? (
          <Box component="span" sx={{ color: '#ff0000', backgroundColor: '#ffff00', fontWeight: 800, px: 0.5 }}>
            DOBLE CHÓFER
          </Box>
        ) : `${driverCount} CHÓFER`}
      </Typography>
      <Typography className="transport-managed-dock">
        {[orders.join(' | '), info].filter(Boolean).join(' | ') || 'Sin pedidos ni observaciones'}
      </Typography>
    </Box>
  );
}

function loadOrderStageLabel(order = {}) {
  const operationType = order.operationType || order.operation_type;
  if (operationType === 'LOAD_TRIP') return 'Carga';
  if (operationType === 'TRANSIT_STAGE') return 'En transito';
  if (operationType === 'CUSTOMS_STAGE') return 'Aduanas';
  if (operationType === 'LOGISTICS_CENTER_STAGE') return 'Centro logistico';
  if (operationType === 'VEHICLE_CHANGE_STAGE') return 'Cambio matricula';
  if (operationType === 'UNLOAD_TRIP') return 'Descarga';
  return traceabilityStageLabel({ stage_type: order.trip_type || order.tripType || 'LOAD' });
}

function loadOrderCurrentLocation(order = {}, line = {}) {
  const stops = Array.isArray(order.stops) ? order.stops : [];
  const lineLoadStopId = stringOrEmpty(line.load_stop_id);
  const lineUnloadStopId = stringOrEmpty(line.unload_stop_id);
  const tripType = order.trip_type || order.tripType || 'LOAD';
  const matchedStop =
    (lineUnloadStopId && stops.find((stop) => stringOrEmpty(stop.id) === lineUnloadStopId)) ||
    (lineLoadStopId && stops.find((stop) => stringOrEmpty(stop.id) === lineLoadStopId)) ||
    stops.find((stop) => stop.stop_type === tripType || stop.stopType === tripType) ||
    stops[0];

  return (
    stopLocationLabel(matchedStop) ||
    entityLabel(line.customer_destination || order.customer_destination) ||
    entityLabel(line.provider_warehouse || order.provider_warehouse) ||
    '-'
  );
}

function stopLocationLabel(stop = {}) {
  if (!stop) {
    return '';
  }
  return (
    stop.location_name ||
    (stop.logistic_center ? entityLabel(stop.logistic_center) : '') ||
    (stop.customer_destination ? entityLabel(stop.customer_destination) : '') ||
    (stop.provider_warehouse ? entityLabel(stop.provider_warehouse) : '') ||
    (stop.cliente ? entityLabel(stop.cliente) : '') ||
    (stop.proveedor ? entityLabel(stop.proveedor) : '') ||
    ''
  );
}

function loadTripOriginLabel(line, order = {}) {
  const links = (line.incoming_links || []).filter((link) => ['INTERNAL_ORDER_TO_LOAD', 'LOAD_TO_LOAD'].includes(link.link_type));
  return [...new Set(links.map((link) => link.from_load_order_number || `Orden ${link.from_load_order_id}`))].join(', ')
    || order.parent_load_order?.number || '';
}

function loadOrderDocumentExcel(order) {
  const internal = (order.operation_type || order.operationType) === 'INTERNAL_LOAD_ORDER';
  const unload = (order.trip_type || order.tripType) === 'UNLOAD';
  const type = internal ? 'Orden de carga' : unload ? 'Viaje de descarga' : 'Viaje de carga';
  const prefix = internal ? 'orden-carga' : unload ? 'viaje-descarga' : 'viaje-carga';
  const number = String(order.number || order.id).replace(/[<>:"/\\|?*]/g, '-').replace(/\s+/g, '-').slice(0, 120);
  const columns = [
    { field: 'order_number', label: 'Pedido' },
    { field: 'customer_reference', label: 'Ref. cliente' },
    { field: 'load_date', label: 'Fecha carga' },
    { field: 'unload_date', label: 'Fecha descarga' },
    { field: 'customer_name', label: 'Cliente' },
    { field: 'assigned_pallets', label: 'PAL' },
    { field: 'packages', label: 'Cajas' },
    { field: 'product', label: 'Producto' },
    { field: 'pallet_type', label: 'Tipo pal.' },
    { field: 'supplier', label: 'Proveedor' },
    { field: 'info', label: 'Info' },
  ];
  const rows = (order.lines?.length ? order.lines : [{}]).map((line) => {
    const source = line.source_line || {};
    const customer = source.des_cliente || order.cliente?.name || '';
    const destination = line.customer_destination?.name || source.des_dir_envio || order.customer_destination?.name || '';
    const customerName = !destination ? customer : !customer || destination.toLocaleLowerCase('es').includes(customer.toLocaleLowerCase('es'))
      ? destination : `${customer} ${destination}`;
    const loadingDate = unload ? source.fecha_carga : order.load_date || source.fecha_carga;
    const unloadingDate = unload ? order.load_date || source.fecha_entrada : source.fecha_entrada;
    return {
      order_number: line.pedido_no || source.origin_pedido_no || source.source_document_no || line.manual_document_no,
      customer_reference: source.num_doc_ext,
      load_date: loadingDate ? formatShortDate(loadingDate) : null,
      unload_date: unloadingDate ? formatShortDate(unloadingDate) : null,
      customer_name: customerName,
      customer_group: JSON.stringify([source.cod_cliente || order.cliente?.cod_navision || order.cliente?.code || customer,
        line.customer_destination_id || source.cod_dir_envio || destination]),
      assigned_pallets: line.cantidad_pales,
      packages: line.bultos,
      product: [source.des_variedad || line.manual_product_name || line.manual_variety || line.manual_description, source.des_confeccion].filter(Boolean).join(' / '),
      pallet_type: source.des_palet || source.cod_palet,
      supplier: source.des_proveedor || source.cod_proveedor,
      info: '',
    };
  });
  return { rows, columns, fileName: `${prefix}-${number}-${order.id}.xlsx`, sheetName: type,
    exportType: internal ? 'INTERNAL_LOAD_ORDER_DOCUMENT' : unload ? 'UNLOAD_TRIP_DOCUMENT' : 'LOAD_TRIP_DOCUMENT' };
}

async function exportRowsToExcel({ rows = [], columns = [], fileName, sheetName = 'Datos' }) {
  const visibleColumns = columns.length > 0
    ? columns.filter((column) => column.visible !== false)
    : Object.keys(rows[0] || {}).map((field) => ({ field, label: humanizeFieldName(field) }));
  const blob = await exportLogisticsExcel({
    title: sheetName,
    columns: visibleColumns.map(({ field, label }) => ({ field, label: label || humanizeFieldName(field) })),
    rows: rows.map((row) => Object.fromEntries([
      ...visibleColumns.map((column) => [column.field, excelCellValue(row, column)]),
      // Group even when the customer column is hidden in the personalized view.
      ['customer_code', row.customer_code || row.cod_cliente || ''],
      ['customer_group', row.customer_group || ''],
    ])),
  });
  downloadBlob(blob, fileName || 'exportacion.xlsx');
}

function excelCellValue(row, column) {
  const rawValue = column.render
    ? column.render(row)
    : readPath(row, column.field);

  if (rawValue === null || rawValue === undefined || rawValue === '') return null;
  if (['order_number', 'customer_reference', 'customer_name', 'product', 'pallet_type', 'supplier', 'info'].includes(column.field)) return String(rawValue);
  if (isDecimalLike(rawValue)) return decimalValue(rawValue);

  if (React.isValidElement(rawValue)) {
    return '';
  }

  return typeof rawValue === 'number' || typeof rawValue === 'boolean' ? rawValue : formatCellValue(rawValue);
}

function downloadBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function humanizeFieldName(field) {
  return String(field)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function selectedCustomerCodesParam(codClientes = []) {
  const codes = Array.isArray(codClientes) ? codClientes : [codClientes];
  const normalizedCodes = codes.map((code) => String(code || '').trim()).filter(Boolean);
  return normalizedCodes.length > 0 ? normalizedCodes.join(',') : undefined;
}

function customerOptionCode(customer) {
  return String(
    customer?.cod_cliente ??
      customer?.codCliente ??
      customer?.codNavision ??
      customer?.cod_navision ??
      customer?.code ??
      ''
  ).trim();
}

function customerOptionLabel(customer) {
  const code = customerOptionCode(customer);
  const name =
    customer?.des_cliente ??
    customer?.desCliente ??
    customer?.name ??
    customer?.displayName ??
    '';

  if (code && name) {
    return `${code} - ${name}`;
  }
  return code || name || 'Cliente';
}

function selectedCustomerOptions(customerOptions = [], codClientes = []) {
  const selectedCodes = new Set((Array.isArray(codClientes) ? codClientes : [codClientes]).filter(Boolean));
  return customerOptions.filter((customer) => selectedCodes.has(customerOptionCode(customer)));
}

function selectedDestinationFiltersParam(destinos = []) {
  const values = Array.isArray(destinos) ? destinos : [destinos];
  const normalizedValues = values.map((value) => String(value || '').trim()).filter(Boolean);
  return normalizedValues.length > 0 ? normalizedValues.join(',') : undefined;
}

function flattenCustomerDestinations(customerOptions = [], codClientes = []) {
  const selectedCodes = new Set(
    (Array.isArray(codClientes) ? codClientes : [codClientes]).filter(Boolean)
  );
  const shouldFilterByCustomer = selectedCodes.size > 0;

  return customerOptions.flatMap((customer) => {
    const customerCode = customerOptionCode(customer);
    if (shouldFilterByCustomer && !selectedCodes.has(customerCode)) {
      return [];
    }

    return getCustomerDestinations(customer)
      .filter((destination) => destination?.active !== false)
      .map((destination) => ({
        ...destination,
        customerId: getId(customer),
        customerName: customer?.name || customer?.des_cliente || customerCode,
        _customerCode: customerCode,
        _customerName: customer?.name || customer?.des_cliente || customerCode,
      }));
  });
}

function destinationOptionFilterValue(destination) {
  return String(
    destination?.codNavision ??
      destination?.cod_navision ??
      destination?.code ??
      destination?.name ??
      ''
  ).trim();
}

function destinationOptionKey(destination) {
  return [
    destination?._customerCode || '',
    destinationOptionFilterValue(destination),
    destination?.id || '',
  ]
    .filter(Boolean)
    .join(':');
}

function destinationOptionLabel(destination) {
  const destinationLabelText = destinationLabel(destination);
  const customerName = destination?._customerName;
  return customerName
    ? `${customerName} - ${destinationLabelText}`
    : destinationLabelText;
}

function selectedDestinationOptions(destinationOptions = [], destinos = []) {
  const selectedValues = new Set(
    (Array.isArray(destinos) ? destinos : [destinos]).map(String).filter(Boolean)
  );
  return destinationOptions.filter((destination) =>
    selectedValues.has(destinationOptionFilterValue(destination))
  );
}

function addDays(date, days) {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + days);
  return nextDate;
}

function toInputDate(date) {
  return date.toISOString().slice(0, 10);
}

function toInputDateValue(value) {
  if (!value) {
    return '';
  }

  if (value instanceof Date) {
    return toInputDate(value);
  }

  const text = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) {
    return text.slice(0, 10);
  }

  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? '' : toInputDate(date);
}

function stringOrEmpty(value) {
  return value === undefined || value === null ? '' : String(value);
}

function responseList(response) {
  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  if (Array.isArray(response?.items)) {
    return response.items;
  }

  if (Array.isArray(response?.value)) {
    return response.value;
  }

  return [];
}

function defaultMeta() {
  return {
    page: 1,
    limit: DEFAULT_TABLE_PAGE_SIZE,
    total: 0,
    totalPages: 1,
  };
}

async function printLoadOrderCmr(loadOrderId, payload = {}) {
  const report = await generateLogisticsLoadOrderCmrPdf(loadOrderId, payload);
  const fileUuid =
    report?.files?.public_uuid ||
    report?.file?.public_uuid ||
    report?.file_public_uuid;

  if (!fileUuid) {
    throw new Error('La API no ha devuelto el archivo CMR generado.');
  }

  const { blob, fileName } = await downloadFileBlob(fileUuid);
  openPdfBlob(blob, fileName || `cmr-${loadOrderId}.pdf`);
}

async function downloadAttachment(file = {}) {
  const fileUuid = file.public_uuid || file.file_public_uuid;
  if (!fileUuid) {
    throw new Error('El documento no tiene identificador de descarga.');
  }
  const { blob, fileName } = await downloadFileBlob(fileUuid);
  openPdfBlob(blob, fileName || file.original_name || 'documento');
}

function openPdfBlob(blob, fileName) {
  const url = window.URL.createObjectURL(blob);
  const opened = window.open(url, '_blank', 'noopener,noreferrer');

  if (!opened) {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
}

function emailDeliveryColumns() {
  return [
    {
      field: 'source_id',
      label: 'Orden',
      render: (row) => row.source_id || '-',
    },
    { field: 'subject', label: 'Asunto' },
    {
      field: 'recipients_to',
      label: 'Para',
      render: (row) => formatEmailList(row.recipients_to),
    },
    {
      field: 'recipients_cc',
      label: 'CC',
      render: (row) => formatEmailList(row.recipients_cc),
    },
    {
      field: 'status',
      label: 'Estado',
      render: (row) => emailDeliveryStatusLabel(row.status),
    },
    {
      field: 'created_at',
      label: 'Creado',
      render: (row) => formatDateTimeValue(row.created_at),
    },
    {
      field: 'sent_at',
      label: 'Enviado',
      render: (row) => formatDateTimeValue(row.sent_at),
    },
    {
      field: 'attachments',
      label: 'Adjuntos',
      render: (row) => `${row.attachments?.length || 0}`,
    },
    {
      field: 'error_message',
      label: 'Error',
      render: (row) => row.error_message || '-',
    },
  ];
}

const EMAIL_DELIVERY_STATUS_OPTIONS = [
  { value: 'PENDING', label: 'Pendiente' },
  { value: 'QUEUED', label: 'En cola' },
  { value: 'SENDING', label: 'Enviando' },
  { value: 'RETRY', label: 'Reintento' },
  { value: 'SENT', label: 'Enviado' },
  { value: 'FAILED', label: 'Error' },
  { value: 'CANCELLED', label: 'Cancelado' },
];

function defaultEmailDeliveryFilters() {
  return {
    search: '',
    orderId: '',
    status: '',
    createdFrom: '',
    createdTo: '',
  };
}

function formatEmailList(value) {
  return Array.isArray(value) && value.length ? value.join(', ') : '-';
}

function emailDeliveryStatusLabel(status) {
  const labels = {
    PENDING: 'Pendiente',
    QUEUED: 'En cola',
    SENDING: 'Enviando',
    RETRY: 'Reintento',
    SENT: 'Enviado',
    FAILED: 'Error',
    CANCELLED: 'Cancelado',
  };
  return labels[status] || status || '-';
}

function splitEmails(value = '') {
  return String(value)
    .split(/[;,]/)
    .map((email) => email.trim())
    .filter(Boolean);
}

async function loadAllPendingImports(params = {}) {
  const limit = PAGE_SIZE;
  const firstResponse = await getLogisticsPendingImports({
    ...params,
    page: 1,
    limit,
  });
  const firstRows = responseList(firstResponse);
  const total = Number(firstResponse?.meta?.total ?? firstRows.length);
  const totalPages = Math.max(1, Math.ceil(total / limit));

  if (totalPages <= 1) {
    return firstRows;
  }

  const remainingResponses = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, index) =>
      getLogisticsPendingImports({
        ...params,
        page: index + 2,
        limit,
      })
    )
  );

  return [
    ...firstRows,
    ...remainingResponses.flatMap((response) => responseList(response)),
  ];
}

function viewProfileForm(fields, profile = null, customerProfiles = []) {
  const fieldMap = new Map(fields.map((field) => [field.field, field]));
  const requiredFields = REQUIRED_VIEW_FIELDS.map((fieldName, index) => {
    const definition = fieldMap.get(fieldName);
    const existing = profile?.fields?.find((field) => field.field === fieldName);
    return {
      field: fieldName,
      label: existing?.label || definition?.label || fieldName,
      visible: existing?.visible ?? false,
      displayOrder: existing?.displayOrder ?? index,
    };
  });

  const visibleFields = profile?.fields?.length
    ? profile.fields
        .filter((field) => !REQUIRED_VIEW_FIELDS.includes(field.field))
        .map((field, index) => ({
          field: field.field,
          label: field.label || field.field,
          visible: field.visible ?? true,
          displayOrder: field.displayOrder ?? (index + 1) * 10,
        }))
    : fields
        .filter((field) => !REQUIRED_VIEW_FIELDS.includes(field.field))
        .slice(0, 8)
        .map((field, index) => ({
          field: field.field,
          label: field.label,
          visible: true,
          displayOrder: (index + 1) * 10,
        }));

  const profileId = profile?.id ? String(profile.id) : '';
  const codClientes = profileId
    ? customerProfiles
        .filter((assignment) => String(assignment.profile?.id || '') === profileId)
        .map((assignment) => assignment.cliente?.cod_cliente)
        .filter(Boolean)
    : [];

  return {
    code: profile?.code || '',
    name: profile?.name || '',
    description: profile?.description || '',
    codClientes,
    documentType: profile?.documentType || '',
    defaultSortField: profile?.defaultSortField || 'fecha_carga',
    defaultSortDirection: profile?.defaultSortDirection || 'asc',
    fields: [...requiredFields, ...visibleFields],
  };
}

function mergeViewFieldDefinitions(fields = [], configuredFields = []) {
  const definitions = new Map(
    fields.map((field) => [
      field.field,
      {
        ...field,
        defaultOrder: Number(field.defaultOrder ?? field.default_order ?? 9999),
      },
    ])
  );

  configuredFields.forEach((field) => {
    if (!definitions.has(field.field)) {
      definitions.set(field.field, {
        field: field.field,
        label: field.label || field.field,
        defaultOrder: Number(field.displayOrder ?? 9999),
        active: true,
      });
    }
  });

  return [...definitions.values()].sort(
    (left, right) =>
      Number(left.defaultOrder ?? 9999) - Number(right.defaultOrder ?? 9999) ||
      String(left.field).localeCompare(String(right.field))
  );
}

function nextDisplayOrder(fields) {
  const maxOrder = fields.reduce(
    (max, field) => Math.max(max, Number(field.displayOrder || 0)),
    0
  );
  return maxOrder + 10;
}

function initialMasterForm(config, row) {
  const form = Object.fromEntries(
    config.formFields.map((field) => {
      if (field.type === 'boolean') {
        return [field.field, row ? Boolean(readPath(row, field.field)) : true];
      }

      return [field.field, readPath(row, field.field) ?? ''];
    })
  );

  for (const subform of config.subforms || []) {
    form[subform.field] = getSubformRows(row, subform).map((item) =>
      initialSubformRow(subform, item)
    );
  }

  return form;
}

function initialSubformRow(subform, row = {}) {
  const result = {
    _key: row.id || createTempId(),
    id: row.id ? String(row.id) : undefined,
    ...Object.fromEntries(
      subform.fields.map((field) => {
        if (field.type === 'boolean') {
          const value = readPath(row, field.field);
          return [field.field, value === undefined ? true : Boolean(value)];
        }
        return [field.field, readPath(row, field.field) ?? ''];
      })
    ),
  };

  for (const nestedSubform of subform.childSubforms || []) {
    result[nestedSubform.field] = (readPath(row, nestedSubform.field) || []).map((item) =>
      initialSubformRow(nestedSubform, item)
    );
  }

  return result;
}

function getSubformRows(row, subform) {
  if (!row) {
    return [];
  }

  if (subform.field === 'matriculas') {
    return getMatriculas(row);
  }

  if (subform.field === 'almacenesCarga') {
    return getProviderWarehouses(row);
  }

  if (subform.field === 'destinos') {
    return getCustomerDestinations(row);
  }

  if (subform.field === 'contacts') {
    const contacts = readPath(row, 'contacts');
    if (Array.isArray(contacts) && contacts.length > 0) {
      return contacts;
    }
    if (row.email || row.phone) {
      return [
        {
          name: row.name || '',
          email: row.email || '',
          phone: row.phone || '',
          isPrimary: true,
          active: true,
        },
      ];
    }
    return [];
  }

  return readPath(row, subform.field) || [];
}

function buildMasterPayload(config, form) {
  const payload = {};

  config.formFields.forEach((field) => {
    const value = form[field.field];

    if (field.type === 'boolean') {
      payload[field.field] = Boolean(value);
      return;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed !== '') {
        payload[field.field] = trimmed;
      }
      return;
    }

    if (value !== undefined && value !== null) {
      payload[field.field] = value;
    }
  });

  for (const subform of config.subforms || []) {
    payload[subform.field] = (Array.isArray(form[subform.field])
      ? form[subform.field]
      : []
    )
      .map((row) => buildSubformPayload(subform, row))
      .filter((row) => subformRowHasRequiredData(subform, row));
  }

  return payload;
}

function buildSubformPayload(subform, row) {
  const payload = {};

  if (row.id) {
    payload.id = String(row.id);
  }

  for (const field of subform.fields) {
    const value = row[field.field];
    if (field.type === 'boolean') {
      payload[field.field] = Boolean(value);
      continue;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed !== '') {
        payload[field.field] = trimmed;
      }
      continue;
    }
    if (value !== undefined && value !== null) {
      payload[field.field] = value;
    }
  }

  for (const nestedSubform of subform.childSubforms || []) {
    payload[nestedSubform.field] = (Array.isArray(row[nestedSubform.field])
      ? row[nestedSubform.field]
      : [])
      .map((nestedRow) => buildSubformPayload(nestedSubform, nestedRow))
      .filter((nestedRow) => subformRowHasAnyData(nestedSubform, nestedRow));
  }

  return payload;
}

function subformRowHasRequiredData(subform, row) {
  return subform.fields
    .filter((field) => field.required)
    .every((field) => String(row[field.field] ?? '').trim() !== '');
}

function subformRowHasAnyData(subform, row) {
  return subform.fields.some((field) => {
    if (field.type === 'boolean') {
      return false;
    }
    return String(row[field.field] ?? '').trim() !== '';
  });
}

function readPath(row, field) {
  return String(field)
    .split('.')
    .reduce((current, part) => {
      if (!current) {
        return undefined;
      }
      return current[part] ?? current[toSnakeCase(part)];
    }, row);
}

function toSnakeCase(value) {
  return String(value).replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

function pendingLineForm(line = {}) {
  const source = line || {};
  return {
    originPedidoNo: stringOrEmpty(source.origin_pedido_no),
    originPedidoLineNo: stringOrEmpty(source.origin_pedido_line_no),
    codAlmacen: stringOrEmpty(source.cod_almacen),
    codProveedor: stringOrEmpty(source.cod_proveedor),
    desProveedor: stringOrEmpty(source.des_proveedor),
    codDirCarga: stringOrEmpty(source.cod_dir_carga),
    desDirCarga: stringOrEmpty(source.des_dir_carga),
    codVariedad: stringOrEmpty(source.cod_variedad),
    desVariedad: stringOrEmpty(source.des_variedad),
    codConfeccion: stringOrEmpty(source.cod_confeccion),
    desConfeccion: stringOrEmpty(source.des_confeccion),
    codEnvase: stringOrEmpty(source.cod_envase),
    desEnvase: stringOrEmpty(source.des_envase),
    codPalet: stringOrEmpty(source.cod_palet),
    desPalet: stringOrEmpty(source.des_palet),
    cantidadPales: stringOrEmpty(decimalValue(source.cantidad_pales)),
    bultosPalet: stringOrEmpty(source.bultos_palet),
    kilosBrutos: stringOrEmpty(decimalValue(source.kilos_brutos)),
    kilosNetos: stringOrEmpty(decimalValue(source.kilos_netos)),
    precio: stringOrEmpty(decimalValue(source.precio)),
    observacion: stringOrEmpty(source.observacion),
    logisticsNotes: stringOrEmpty(source.logistics_notes),
  };
}

function tableSortValue(row, field, column) {
  const value = readPath(row, field);
  if (value !== undefined && value !== null && value !== '') {
    return value;
  }
  return column?.render ? column.render(row) : value;
}

function compareTableValues(left, right) {
  const leftValue = normalizeSortValue(left);
  const rightValue = normalizeSortValue(right);

  if (leftValue === rightValue) {
    return 0;
  }

  if (leftValue === null || leftValue === undefined || leftValue === '') {
    return 1;
  }

  if (rightValue === null || rightValue === undefined || rightValue === '') {
    return -1;
  }

  if (typeof leftValue === 'number' && typeof rightValue === 'number') {
    return leftValue - rightValue;
  }

  return String(leftValue).localeCompare(String(rightValue), 'es', {
    numeric: true,
    sensitivity: 'base',
  });
}

function normalizeSortValue(value) {
  if (value instanceof Date) {
    return value.getTime();
  }

  if (typeof value === 'boolean') {
    return value ? 1 : 0;
  }

  if (typeof value === 'number') {
    return value;
  }

  if (value && typeof value === 'object') {
    const decimal = decimalValue(value);
    if (decimal !== 0) {
      return decimal;
    }
    if ('label' in value) {
      return value.label;
    }
    if ('name' in value) {
      return value.name;
    }
    if ('code' in value) {
      return value.code;
    }
    return JSON.stringify(value);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return '';
    }
    const timestamp = Date.parse(trimmed);
    if (!Number.isNaN(timestamp) && /\d{4}-\d{2}-\d{2}/.test(trimmed)) {
      return timestamp;
    }
    const numeric = Number(trimmed.replace(',', '.'));
    if (!Number.isNaN(numeric) && /^-?\d+([,.]\d+)?$/.test(trimmed)) {
      return numeric;
    }
    return trimmed;
  }

  return value;
}

function cleanPayload(payload) {
  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => {
      if (value === undefined || value === null) {
        return false;
      }
      if (typeof value === 'string') {
        return value.trim() !== '';
      }
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return true;
    })
  );
}

function formatShortDate(value) {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return new Intl.DateTimeFormat('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

function formatDateTimeValue(value) {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return new Intl.DateTimeFormat('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function formatTraceabilityDate(value) {
  if (!value) {
    return '-';
  }
  const text = String(value);
  return text.includes('T') ? formatDateTimeValue(value) : formatShortDate(value);
}

function traceabilityStageLabel(event = {}) {
  if (event.event_type === 'STATUS_CHANGED') return 'Cambio de estado';
  const labels = {
    ORIGIN: 'Pedido origen',
    LOAD: 'Carga',
    TRANSIT: 'En transito',
    CUSTOMS: 'Aduanas',
    LOGISTICS_CENTER: 'Centro logistico',
    UNLOAD: 'Descarga',
  };
  return labels[event.stage_type] || event.label || event.stage_type || '-';
}

function compareTraceabilityEvents(left, right) {
  return (
    traceabilityEventTimestamp(left, 'registered_at') -
      traceabilityEventTimestamp(right, 'registered_at') ||
    traceabilityEventTimestamp(left, 'event_at') -
      traceabilityEventTimestamp(right, 'event_at') ||
    Number(left.sort_order ?? 0) - Number(right.sort_order ?? 0) ||
    Number(left.load_order_id ?? 0) - Number(right.load_order_id ?? 0) ||
    Number(left.load_order_line_id ?? 0) - Number(right.load_order_line_id ?? 0)
  );
}

function traceabilityEventTimestamp(event = {}, field) {
  const value = event?.[field];
  if (!value) {
    return 0;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
}

function traceabilityEventStatusLabel(event = {}) {
  if (!event.load_order_number) {
    return '';
  }
  if (event.event_type === 'STATUS_CHANGED') {
    return `${loadOrderStatusLabel(event.previous_status)} → ${loadOrderStatusLabel(event.load_order_status)}`;
  }
  if (event.historical_status && !event.load_order_status) return 'Estado historico no registrado';
  if (event.cancelled || isCancelledLoadOrder({ status: event.load_order_status })) {
    return 'Cancelado';
  }
  return loadOrderStatusLabel(event.load_order_status);
}

function traceabilityEventStatusChipClass(event = {}) {
  return event.cancelled || isCancelledLoadOrder({ status: event.load_order_status })
    ? 'transport-status-chip transport-status-chip-warning'
    : 'transport-status-chip transport-status-chip-info';
}

function traceabilityAttachmentLabel(attachments = []) {
  if (!Array.isArray(attachments) || attachments.length === 0) {
    return '-';
  }
  return attachments
    .map((attachment) => attachment?.file?.original_name || attachment?.description || attachment?.category)
    .filter(Boolean)
    .join(', ');
}

function formatCellValue(value) {
  if (React.isValidElement(value)) return value;
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  if (typeof value === 'boolean') {
    return value ? 'Si' : 'No';
  }

  if (Array.isArray(value)) {
    return summarizeList(value);
  }

  if (typeof value === 'object') {
    if (isDecimalLike(value)) {
      return formatQuantity(decimalValue(value));
    }
    return entityLabel(value);
  }

  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
    return formatShortDate(value);
  }

  return value;
}

function formatQuantity(value) {
  const number = decimalValue(value);
  if (!Number.isFinite(number)) {
    return '0';
  }
  return new Intl.NumberFormat('es-ES', {
    minimumFractionDigits: number % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(number);
}

function formatFormQuantity(value) {
  const number = decimalValue(value);
  if (!Number.isFinite(number)) {
    return '';
  }
  return String(Number(number.toFixed(2)));
}

function isCancelledLoadOrder(order) {
  return CANCELLED_LOAD_ORDER_STATUS_CODES.includes(
    String(order?.status || '').trim().toUpperCase(),
  );
}

function activeLoadOrders(rows = []) {
  return rows.filter((order) => !isCancelledLoadOrder(order));
}

function groupLoadOrdersByParent(rows = []) {
  return rows.reduce((groups, order) => {
    const parentId = stringOrEmpty(order.parent_load_order_id || order.parentLoadOrderId);
    if (!parentId) {
      return groups;
    }
    const current = groups.get(parentId) || [];
    current.push(order);
    groups.set(parentId, current);
    return groups;
  }, new Map());
}

function decimalValue(value) {
  if (value === null || value === undefined || value === '') {
    return 0;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value === 'string') {
    const normalized = value.replace(',', '.');
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  if (isDecimalLike(value)) {
    const sign = value.s === -1 ? -1 : 1;
    const digits = Array.isArray(value.d) ? value.d.join('') : '0';
    const exponent = Number.isFinite(value.e) ? value.e : digits.length - 1;
    const decimalIndex = exponent + 1;
    const normalized =
      decimalIndex >= digits.length
        ? `${digits}${'0'.repeat(decimalIndex - digits.length)}`
        : `${digits.slice(0, decimalIndex)}.${digits.slice(decimalIndex)}`;
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed * sign : 0;
  }

  return 0;
}

function isDecimalLike(value) {
  return Boolean(
    value &&
      typeof value === 'object' &&
      Array.isArray(value.d) &&
      Object.prototype.hasOwnProperty.call(value, 's') &&
      Object.prototype.hasOwnProperty.call(value, 'e')
  );
}

function importKey(item) {
  return `${item.document_type}:${item.source_document_no}:${item.source_line_no ?? ''}`;
}

function getId(item) {
  return String(item?.id ?? '');
}

function getLineId(row) {
  return String(row?.line_id || row?.document_line_id || row?.source_line_id || row?.id || '');
}

function getLineKey(row) {
  return getLineId(row) || `${row?.source_document_no}:${row?.source_line_no}`;
}

function hasMultipleCustomers(rows) {
  return new Set(rows.map((row) => row.cod_cliente || row.source_line?.cod_cliente).filter(Boolean)).size > 1;
}

function loadOrderMatchesCustomer(order, codCliente, customerRows = []) {
  if (!codCliente) {
    return false;
  }

  const rowLineIds = new Set(customerRows.map(getLineId).filter(Boolean));
  const rowDocumentLines = new Set(
    customerRows
      .map((row) => `${row?.source_document_no || ''}:${row?.source_line_no || ''}`)
      .filter((value) => value !== ':')
  );
  const lines = Array.isArray(order?.lines) ? order.lines : [];

  return lines.some((line) => {
    const sourceLine = line.source_line || {};
    const lineCustomer =
      line.cod_cliente ||
      line.cliente?.code ||
      line.cliente?.cod_cliente ||
      sourceLine.cod_cliente ||
      sourceLine.cliente?.code ||
      sourceLine.cliente?.cod_cliente;

    if (lineCustomer === codCliente) {
      return true;
    }

    const lineId = String(
      line.document_line_id ||
        line.source_line_id ||
        sourceLine.line_id ||
        sourceLine.id ||
        ''
    );
    if (lineId && rowLineIds.has(lineId)) {
      return true;
    }

    const documentNo =
      sourceLine.source_document_no ||
      line.expedicion_no ||
      line.pedido_no ||
      line.source_document_no ||
      '';
    const lineNo = sourceLine.source_line_no || line.source_line_no || '';
    return rowDocumentLines.has(`${documentNo}:${lineNo}`);
  });
}

function loadOrderEmailScopeOptions(orders = []) {
  const byScope = new Map();
  orders.forEach((order) => {
    const clienteId = stringOrEmpty(order.cliente_id || order.cliente?.id);
    if (!clienteId) {
      return;
    }
    const clienteLabel = entityLabel(order.cliente) || `Cliente ${clienteId}`;
    (order.lines || []).forEach((line) => {
      const providerWarehouseId = stringOrEmpty(
        line.provider_warehouse_id || line.provider_warehouse?.id
      );
      if (!providerWarehouseId) {
        return;
      }
      const key = `${providerWarehouseId}:${clienteId}`;
      const current =
        byScope.get(key) || {
          key,
          providerWarehouseId,
          clienteId,
          providerWarehouseLabel: entityLabel(line.provider_warehouse) || `Almacen ${providerWarehouseId}`,
          clienteLabel,
          orderIds: new Set(),
        };
      current.orderIds.add(order.id);
      byScope.set(key, current);
    });
  });

  return [...byScope.values()]
    .map((scope) => ({
      ...scope,
      orderCount: scope.orderIds.size,
      label: `${scope.providerWarehouseLabel} / ${scope.clienteLabel} (${scope.orderIds.size})`,
    }))
    .sort((left, right) => left.label.localeCompare(right.label, 'es', { sensitivity: 'base' }));
}

function lineLabel(row) {
  const documentNo = row?.source_document_no || row?.expedicion_no || row?.pedido_no || '-';
  const lineNo = row?.source_line_no || row?.linea || row?.document_line_id || '-';
  return `${documentNo} / ${lineNo}`;
}

function manualLineLabel(row = {}) {
  return (
    row.manual_product_name ||
    row.manual_variety ||
    row.manual_description ||
    row.source_document_no ||
    'Linea manual'
  );
}

function linePendingPallets(row) {
  return decimalValue(row?.cantidad_pales_pendientes);
}

function lineUnloadPendingPallets(row) {
  return decimalValue(row?.cantidad_pales_descarga_pendientes);
}

function lineTotalPallets(row) {
  return decimalValue(row?.cantidad_pales ?? row?.cantidadPales);
}

function entityLabel(value) {
  if (!value) {
    return '-';
  }

  if (typeof value !== 'object') {
    return String(value);
  }

  return (
    value.name ||
    value.nombre ||
    value.description ||
    value.code ||
    value.number ||
    value.matricula ||
    JSON.stringify(value)
  );
}

function summarizeList(list, preferredField) {
  if (!Array.isArray(list) || !list.length) {
    return '-';
  }

  const first = list[0];
  const firstLabel =
    (preferredField && first?.[preferredField]) || entityLabel(first) || 'registro';
  if (list.length === 1) {
    return firstLabel;
  }
  return `${firstLabel} +${list.length - 1}`;
}

function viewProfileCustomersSummary(profile, customerProfiles = []) {
  const profileId = String(profile?.id || '');
  const values = customerProfiles
    .filter((assignment) => String(assignment.profile?.id || '') === profileId)
    .map((assignment) => assignment.cliente?.des_cliente || assignment.cliente?.cod_cliente)
    .filter(Boolean);

  if (!values.length) {
    return '-';
  }

  return values.length > 3 ? `${values.slice(0, 3).join(', ')} +${values.length - 3}` : values.join(', ');
}

function getCustomerDestinations(cliente) {
  if (!cliente) {
    return [];
  }

  return (
    cliente.destinos ||
    cliente.customer_destinations ||
    cliente.logistics_customer_destinations ||
    []
  );
}

function getProviderWarehouses(proveedor) {
  if (!proveedor) {
    return [];
  }

  return (
    proveedor.almacenesCarga ||
    proveedor.provider_warehouses ||
    proveedor.logistics_provider_warehouses ||
    []
  );
}

function getMatriculas(transportista) {
  if (!transportista) {
    return [];
  }

  return (
    transportista.matriculas ||
    transportista.transportista_matriculas ||
    transportista.logistics_transportista_matriculas ||
    []
  );
}

function customerCodeForProfile(cliente) {
  return cliente?.codNavision || cliente?.cod_navision || cliente?.code || '';
}

function destinationLabel(destino) {
  const parts = [destino.name || destino.code, destino.city, destino.province].filter(
    Boolean
  );
  return parts.join(' - ');
}

function createTempId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function apiMessage(error, fallback) {
  return {
    severity: 'error',
    text: error?.message || fallback,
  };
}

